import { a as getValidationStateClasses, c as isDuplicateMapKey, d as INTEGER_HTML_PATTERN, f as INTEGER_INPUT_PATTERN, i as getErrorMessage, l as validateField, n as createValidationResult, o as handleValidationError, p as INTEGER_PATTERN, r as formatValidationError, s as hasFieldError, t as ErrorMessage, u as validateMapEntries } from "./ErrorMessage-BqOEJm84.mjs";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cva } from "class-variance-authority";
import { AlertCircle, Calendar as Calendar$1, Check, CheckCircle, CheckCircle2, CheckIcon, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Circle, ClipboardPaste, CloudOff, Copy, DollarSign, ExternalLink as ExternalLink$1, ExternalLinkIcon, Eye, EyeOff, File, FileText, GripVertical, Hash, Info, Loader2, Menu, MoreHorizontal, Network, Pencil, PencilLine, Plus, Search, Settings, Timer, TriangleAlert, Upload, X } from "lucide-react";
import * as React$1 from "react";
import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { classifyAddressCandidates, classifyAddressInput, cn, crossNetworkFallbackMessageNames, getDefaultValueForType, getFallbackNetworks, getHostedNetworkAvailabilityNoticeCopy, getInvalidUrlMessage, getServiceDisplayName, isChainScopeMismatch, isCrossNetworkFallback, isNetworkAvailabilityPolicyActive, isValidUrl, logger, nameResolutionChainScopeMismatchMessage, nameResolutionCrossNetworkFallbackMessage, nameResolutionMessageForCode, parseDelimitedAddressInput, truncateMiddle, validateBytesSimple } from "@openzeppelin/ui-utils";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { DayPicker } from "react-day-picker";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { format } from "date-fns";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Controller, FormProvider, useController, useFieldArray, useForm, useFormContext, useFormState, useWatch } from "react-hook-form";
import * as LabelPrimitive from "@radix-ui/react-label";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { isMapEntryArray } from "@openzeppelin/ui-types";
import { Toaster as Toaster$1, toast } from "sonner";
import { useTheme } from "next-themes";

//#region src/version.ts
const VERSION = "3.8.0";

//#endregion
//#region src/components/ui/accordion.tsx
const accordionItemVariants = cva("", {
	variants: { variant: {
		default: "border-b",
		card: "mb-3 rounded-lg border bg-card overflow-hidden"
	} },
	defaultVariants: { variant: "default" }
});
const accordionTriggerVariants = cva("flex flex-1 items-center justify-between font-medium transition-all [&[data-state=open]>svg]:rotate-180", {
	variants: { variant: {
		default: "py-4 hover:underline",
		card: "px-4 py-3 hover:bg-muted/50"
	} },
	defaultVariants: { variant: "default" }
});
const accordionContentVariants = cva("overflow-hidden text-sm transition-all duration-300 ease-in-out will-change-[height,opacity] data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down", {
	variants: { variant: {
		default: "",
		card: ""
	} },
	defaultVariants: { variant: "default" }
});
const accordionContentInnerVariants = cva("", {
	variants: { variant: {
		default: "pb-4 pt-0",
		card: "px-4 pb-4 pt-1"
	} },
	defaultVariants: { variant: "default" }
});
const AccordionVariantContext = React$1.createContext("default");
const Accordion = React$1.forwardRef(({ variant = "default", ...props }, ref) => /* @__PURE__ */ jsx(AccordionVariantContext.Provider, {
	value: variant,
	children: /* @__PURE__ */ jsx(AccordionPrimitive.Root, {
		ref,
		...props
	})
}));
Accordion.displayName = "Accordion";
const AccordionItem = React$1.forwardRef(({ className, variant: variantProp, ...props }, ref) => {
	const contextVariant = React$1.useContext(AccordionVariantContext);
	const variant = variantProp ?? contextVariant;
	return /* @__PURE__ */ jsx(AccordionPrimitive.Item, {
		ref,
		className: cn(accordionItemVariants({ variant }), className),
		...props
	});
});
AccordionItem.displayName = "AccordionItem";
const AccordionTrigger = React$1.forwardRef(({ className, children, variant: variantProp, ...props }, ref) => {
	const contextVariant = React$1.useContext(AccordionVariantContext);
	const variant = variantProp ?? contextVariant;
	return /* @__PURE__ */ jsx(AccordionPrimitive.Header, {
		className: "flex",
		children: /* @__PURE__ */ jsxs(AccordionPrimitive.Trigger, {
			ref,
			className: cn(accordionTriggerVariants({ variant }), className),
			...props,
			children: [children, /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 transition-transform duration-200" })]
		})
	});
});
AccordionTrigger.displayName = "AccordionTrigger";
const AccordionContent = React$1.forwardRef(({ className, children, variant: variantProp, ...props }, ref) => {
	const contextVariant = React$1.useContext(AccordionVariantContext);
	const variant = variantProp ?? contextVariant;
	return /* @__PURE__ */ jsx(AccordionPrimitive.Content, {
		ref,
		className: cn(accordionContentVariants({ variant }), className),
		...props,
		children: /* @__PURE__ */ jsx("div", {
			className: cn(accordionContentInnerVariants({ variant })),
			children
		})
	});
});
AccordionContent.displayName = "AccordionContent";

//#endregion
//#region src/components/ui/tooltip.tsx
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React$1.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx(TooltipPrimitive.Content, {
	ref,
	sideOffset,
	className: cn("bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs", className),
	...props
}) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

//#endregion
//#region src/components/ui/address-display/address-avatar.tsx
/**
* Allowlist for name-owner-controlled avatar URLs at the render site.
* Only `https:` and `data:image/*` pass; `http:` (mixed content), `javascript:`,
* and other schemes are dropped. Does not rely on adapter sanitization.
*/
function isAllowedAvatarSrc(src) {
	try {
		const url = new URL(src);
		if (url.protocol === "https:") return true;
		if (url.protocol === "data:") return /^data:image\/[a-z0-9.+-]+/i.test(src);
		return false;
	} catch {
		return false;
	}
}
/**
* @internal Avatar image for a resolved name, hiding itself on load error
* (never a broken-image icon) — the name renders as a complete, avatar-less
* row. Purely presentational; its only state is the per-instance load-error
* flag (INV-121).
*
* The error state is keyed to the current `src` rather than a bare boolean,
* so it resets automatically when `src` changes — a virtualized/reused list
* row that scrolls to a new address re-attempts its avatar instead of staying
* permanently hidden by a prior URL's failure (INV-59).
*
* @param props - {@link AddressAvatarProps}.
* @returns The avatar `<img>`, or `null` when the load failed for this `src`.
*/
function AddressAvatar({ src, alt = "", variant = "fill", sizeClassName }) {
	const [failedSrc, setFailedSrc] = React$1.useState(null);
	if (!isAllowedAvatarSrc(src)) return null;
	if (failedSrc === src) return null;
	return /* @__PURE__ */ jsx("img", {
		src,
		alt,
		className: cn("object-cover", sizeClassName ?? (variant === "fill" ? "absolute inset-0 h-full w-full rounded-none" : "h-4 w-4 shrink-0 rounded-full")),
		referrerPolicy: "no-referrer",
		onError: () => setFailedSrc(src)
	});
}

//#endregion
//#region src/components/ui/address-display/context.ts
/**
* @internal Shared context instance consumed by both AddressDisplay and
* AddressLabelProvider. Kept in its own file so component files export
* only components (required by React Fast Refresh).
*/
const AddressLabelContext = createContext(null);
/**
* @internal Shared context instance consumed by AddressDisplay,
* AddressNameProvider, and useAddressName. Sibling of AddressLabelContext
* (SF-4 INV-122): carries a synchronous, value-only reverse-name resolver.
* Defaults to `null` — no provider means no resolution and a byte-identical
* zero-injection render (INV-54).
*/
const AddressNameContext = createContext(null);

//#endregion
//#region src/components/ui/address-display/address-display.tsx
/**
* True when the primary input can hover (e.g. desktop with mouse).
* Touch-first phones typically report false. SSR assumes hover-capable.
*/
function usePrefersHover() {
	return React$1.useSyncExternalStore(React$1.useCallback((onStoreChange) => {
		if (typeof window === "undefined" || typeof window.matchMedia === "undefined") return () => {};
		const mq = window.matchMedia("(hover: hover)");
		mq.addEventListener("change", onStoreChange);
		return () => mq.removeEventListener("change", onStoreChange);
	}, []), () => typeof window !== "undefined" && typeof window.matchMedia !== "undefined" ? window.matchMedia("(hover: hover)").matches : true, () => true);
}
/**
* Strip C0/C1 controls, bidi embeds/isolates, and zero-width characters from a
* verified name at the display seam. ENSIP-15 normalization remains the
* adapter's job; this only removes invisible directionality/control glyphs so
* a raw render cannot spoof adjacent UI (homoglyph confusables are out of scope).
*/
function sanitizeVerifiedNameForDisplay(name) {
	return name.replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF]/g, "");
}
/**
* Displays a blockchain address with optional truncation, copy button,
* explorer link, tooltip, human-readable label, and (when an already-resolved
* record is injected) a forward-verified name + avatar.
*
* Labels are resolved in priority order (INV-56):
* 1. Explicit `label` prop
* 2. `AddressLabelContext` resolver (via `AddressLabelProvider`)
* 3. Forward-verified resolved name (`resolvedName` prop, else
*    `AddressNameContext` via `AddressNameProvider`) — a record with
*    `forwardVerified: false` is suppressed to hex (INV-52, LOCKED)
* 4. No label (renders address only, identical to previous behavior)
*
* The component is synchronous and capability-free (INV-121): it never
* resolves anything itself — it reads injected values. Hex renders on first
* commit; the name+avatar swap in when the react layer feeds an updated
* record (progressive enhancement, INV-61). With no `resolvedName` and no
* `AddressNameProvider`, output is byte-identical to the pre-enhancement
* component (INV-54, LOCKED).
*
* Pass `disableLabel` to render fully raw — it suppresses both the context
* alias and any injected resolved name (e.g. when the surrounding UI already
* shows a name, such as a contract selector).
*
* @example
* ```tsx
* // Basic usage (unchanged)
* <AddressDisplay address="0x742d35Cc..." showCopyButton />
*
* // Explicit label
* <AddressDisplay address="0x742d35Cc..." label="Treasury" />
*
* // Auto-resolved via context (no changes needed at call site)
* <AddressLabelProvider resolveLabel={myResolver}>
*   <AddressDisplay address="0x742d35Cc..." />
* </AddressLabelProvider>
*
* // Suppress label resolution for a specific instance
* <AddressDisplay address="0x742d35Cc..." disableLabel />
*
* // Reveal full address on hover (still truncated when idle)
* <AddressDisplay address="0x742d35Cc..." untruncateOnHover />
*
* // Tooltip with full address on hover + copy icon on hover
* <AddressDisplay address="0x742d35Cc..." showTooltip showCopyButton showCopyButtonOnHover />
*
* // Inline variant (no chip background) — useful inside wallet bars
* <AddressDisplay address="0x742d35Cc..." variant="inline" showTooltip showCopyButton />
*
* // Reverse-resolved name + avatar via an injected value (react layer owns the async)
* <AddressDisplay address="0x742d35Cc..." resolvedName={record} showCopyButton />
*
* // Or subtree-wide via context (rows stay plain <AddressDisplay/>)
* <AddressNameProvider resolveAddressName={readFromResolutionCache}>
*   <AddressDisplay address="0x742d35Cc..." />
* </AddressNameProvider>
*
* // Truncate only when an alias/label is present (full address when unlabeled)
* <AddressDisplay address="G..." truncateWhenLabeled />
* ```
*/
function AddressDisplay({ address, truncate: truncateProp, truncateWhenLabeled = false, untruncateOnHover = false, startChars = 6, endChars = 4, showCopyButton = false, showCopyButtonOnHover = false, explorerUrl, label: labelProp, onLabelEdit: onLabelEditProp, networkId, disableLabel = false, showTooltip = false, variant = "chip", avatar, avatarVariant = "fill", resolvedName, showCrossNetworkFallbackDisclaimer = true, className, onPointerEnter, onPointerLeave, onMouseEnter, onMouseLeave, onClick, ...props }) {
	const [copied, setCopied] = React$1.useState(false);
	const [isHovered, setIsHovered] = React$1.useState(false);
	const copyTimeoutRef = React$1.useRef(null);
	const prefersHover = usePrefersHover();
	const resolver = React$1.useContext(AddressLabelContext);
	const nameResolver = React$1.useContext(AddressNameContext);
	const resolvedLabel = disableLabel ? void 0 : labelProp ?? resolver?.resolveLabel(address, networkId);
	const record = disableLabel ? void 0 : resolvedName ?? nameResolver?.resolveAddressName(address, networkId);
	const ensName = record?.forwardVerified === true ? sanitizeVerifiedNameForDisplay(record.name) : void 0;
	const effectiveLabel = resolvedLabel ?? ensName;
	const showFallbackNote = showCrossNetworkFallbackDisclaimer && ensName !== void 0 && effectiveLabel === ensName && record !== void 0 && isCrossNetworkFallback(record.provenance);
	const fallbackNote = (() => {
		if (!showFallbackNote) return void 0;
		const networks = getFallbackNetworks(record.provenance);
		if (networks === void 0) return void 0;
		return nameResolutionCrossNetworkFallbackMessage(networks, crossNetworkFallbackMessageNames(networks, nameResolver?.resolveNetworkLabel));
	})();
	const effectiveTruncate = truncateProp !== void 0 ? truncateProp : truncateWhenLabeled ? Boolean(effectiveLabel) : true;
	const contextEditHandler = React$1.useCallback(() => {
		resolver?.onEditLabel?.(address, networkId);
	}, [
		resolver,
		address,
		networkId
	]);
	const editHandler = disableLabel ? void 0 : onLabelEditProp ?? (resolver?.onEditLabel ? contextEditHandler : void 0);
	const canUntruncate = untruncateOnHover && effectiveTruncate && !showTooltip;
	const showFullAddress = !effectiveTruncate || canUntruncate && isHovered;
	const displayAddress = showFullAddress ? address : truncateMiddle(address, startChars, endChars);
	const addressTextClassName = cn(!showFullAddress && "truncate", (showFullAddress || !effectiveTruncate) && "break-all");
	const expandInteractionClassName = canUntruncate && !prefersHover ? "cursor-pointer" : void 0;
	const handlePointerEnter = (e) => {
		if (canUntruncate && prefersHover) setIsHovered(true);
		onPointerEnter?.(e);
		onMouseEnter?.(e);
	};
	const handlePointerLeave = (e) => {
		if (canUntruncate && prefersHover) setIsHovered(false);
		onPointerLeave?.(e);
		onMouseLeave?.(e);
	};
	const handleUntruncateClick = (e) => {
		if (canUntruncate && !prefersHover) setIsHovered((open) => !open);
		onClick?.(e);
	};
	const handleCopy = (e) => {
		e.stopPropagation();
		navigator.clipboard.writeText(address);
		setCopied(true);
		if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
		copyTimeoutRef.current = window.setTimeout(() => {
			setCopied(false);
			copyTimeoutRef.current = null;
		}, 2e3);
	};
	React$1.useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
		};
	}, []);
	const isChip = variant === "chip";
	const actionButtons = /* @__PURE__ */ jsxs(Fragment, { children: [
		showCopyButton && /* @__PURE__ */ jsx("button", {
			type: "button",
			onClick: handleCopy,
			className: cn("shrink-0 text-slate-500", !copied && "hover:text-slate-700", showCopyButtonOnHover ? "ml-0 w-0 overflow-hidden opacity-0 transition-all duration-150 group-hover:ml-1.5 group-hover:w-3.5 group-hover:opacity-100 focus:ml-1.5 focus:w-3.5 focus:opacity-100" : "ml-1.5 transition-colors"),
			"aria-label": copied ? "Copied" : "Copy address",
			children: copied ? /* @__PURE__ */ jsx(Check, { className: "h-3.5 w-3.5 text-emerald-600" }) : /* @__PURE__ */ jsx(Copy, { className: "h-3.5 w-3.5" })
		}),
		explorerUrl && /* @__PURE__ */ jsx("a", {
			href: explorerUrl,
			target: "_blank",
			rel: "noopener noreferrer",
			onClick: (e) => {
				e.stopPropagation();
			},
			className: "ml-1.5 shrink-0 text-slate-500 transition-colors hover:text-slate-700",
			"aria-label": "View in explorer",
			children: /* @__PURE__ */ jsx(ExternalLink$1, { className: "h-3.5 w-3.5" })
		}),
		editHandler && /* @__PURE__ */ jsx("button", {
			type: "button",
			onClick: (e) => {
				e.stopPropagation();
				editHandler();
			},
			className: "ml-0 w-0 shrink-0 overflow-hidden text-slate-500 opacity-0 transition-all duration-150 hover:text-slate-700 group-hover:ml-1.5 group-hover:w-3.5 group-hover:opacity-100 focus:ml-1.5 focus:w-3.5 focus:opacity-100",
			"aria-label": "Edit label",
			children: /* @__PURE__ */ jsx(Pencil, { className: "h-3.5 w-3.5" })
		})
	] });
	const ensAvatarUrl = ensName !== void 0 && resolvedLabel === void 0 ? record?.avatarUrl : void 0;
	const effectiveAvatar = avatar ?? (ensAvatarUrl ? /* @__PURE__ */ jsx(AddressAvatar, {
		src: ensAvatarUrl,
		variant: avatarVariant
	}) : void 0);
	const isFillAvatar = avatarVariant === "fill" && Boolean(effectiveAvatar);
	const fillAvatarChipClassName = isFillAvatar && isChip ? "overflow-hidden" : void 0;
	const avatarSlot = effectiveAvatar ? /* @__PURE__ */ jsx("span", {
		className: cn("flex shrink-0", isFillAvatar ? cn("relative w-9 self-stretch overflow-hidden mr-2", isChip && "-my-1 -ml-2") : "mr-1.5 items-center"),
		children: effectiveAvatar
	}) : null;
	const shouldShowTooltip = showTooltip && effectiveTruncate;
	const wrapWithTooltip = (content) => {
		if (!shouldShowTooltip) return content;
		return /* @__PURE__ */ jsx(TooltipProvider, {
			delayDuration: 300,
			children: /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, {
				asChild: true,
				children: content
			}), /* @__PURE__ */ jsx(TooltipContent, {
				className: "font-mono text-xs",
				children: address
			})] })
		});
	};
	if (effectiveLabel) {
		const labelColumn = /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs("span", {
			className: "inline-flex min-w-0 max-w-full items-center gap-1",
			children: [/* @__PURE__ */ jsx("span", {
				className: "truncate font-sans font-medium text-slate-900 leading-snug",
				children: effectiveLabel
			}), fallbackNote && showCrossNetworkFallbackDisclaimer ? /* @__PURE__ */ jsx(TooltipProvider, {
				delayDuration: 300,
				children: /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, {
					asChild: true,
					children: /* @__PURE__ */ jsx("button", {
						type: "button",
						className: "inline-flex shrink-0 rounded-sm text-amber-500 outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50",
						"aria-label": fallbackNote,
						children: /* @__PURE__ */ jsx(TriangleAlert, {
							className: "h-3 w-3",
							"aria-hidden": true
						})
					})
				}), /* @__PURE__ */ jsx(TooltipContent, {
					className: "max-w-xs font-sans text-xs font-normal normal-case",
					children: fallbackNote
				})] })
			}) : null]
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex min-w-0 items-center font-mono text-[10px] text-slate-400 leading-snug",
			children: [/* @__PURE__ */ jsx("span", {
				className: addressTextClassName,
				children: displayAddress
			}), actionButtons]
		})] });
		return wrapWithTooltip(/* @__PURE__ */ jsx("div", {
			className: cn("group inline-flex max-w-full min-w-0", effectiveAvatar ? "items-center" : "flex-col", isChip && "rounded-md bg-slate-100 px-2 py-1", fillAvatarChipClassName, "text-xs text-slate-700", expandInteractionClassName, className),
			onPointerEnter: handlePointerEnter,
			onPointerLeave: handlePointerLeave,
			onClick: handleUntruncateClick,
			...props,
			children: effectiveAvatar ? /* @__PURE__ */ jsxs(Fragment, { children: [avatarSlot, /* @__PURE__ */ jsx("div", {
				className: "inline-flex min-w-0 flex-col",
				children: labelColumn
			})] }) : labelColumn
		}));
	}
	return wrapWithTooltip(/* @__PURE__ */ jsxs("div", {
		className: cn("group inline-flex max-w-full min-w-0 items-center", isChip && "rounded-md bg-slate-100 px-2 py-1", fillAvatarChipClassName, "text-xs font-mono text-slate-700", expandInteractionClassName, className),
		onPointerEnter: handlePointerEnter,
		onPointerLeave: handlePointerLeave,
		onClick: handleUntruncateClick,
		...props,
		children: [
			avatarSlot,
			/* @__PURE__ */ jsx("span", {
				className: addressTextClassName,
				children: displayAddress
			}),
			actionButtons
		]
	}));
}

//#endregion
//#region src/components/ui/address-display/address-label-context.tsx
/**
* Address Label Context
*
* Provides a React context for resolving human-readable labels for blockchain
* addresses. When an `AddressLabelProvider` is mounted, every `AddressDisplay`
* in the subtree automatically resolves and renders labels without any
* call-site changes.
*
* @example
* ```tsx
* import { AddressLabelProvider } from '@openzeppelin/ui-components';
*
* function App() {
*   const resolver = useAliasLabelResolver(db);
*   return (
*     <AddressLabelProvider {...resolver}>
*       <MyApp />
*     </AddressLabelProvider>
*   );
* }
* ```
*/
/**
* Provides address label resolution to all `AddressDisplay` instances in the
* subtree. Wrap your application (or a subsection) with this provider and
* supply a `resolveLabel` function.
*
* @param props - Resolver functions and children
*
* @example
* ```tsx
* <AddressLabelProvider
*   resolveLabel={(addr) => addressBook.get(addr)}
*   onEditLabel={(addr) => openEditor(addr)}
* >
*   <App />
* </AddressLabelProvider>
* ```
*/
function AddressLabelProvider({ children, resolveLabel, onEditLabel }) {
	const value = React$1.useMemo(() => ({
		resolveLabel,
		onEditLabel
	}), [resolveLabel, onEditLabel]);
	return /* @__PURE__ */ jsx(AddressLabelContext.Provider, {
		value,
		children
	});
}

//#endregion
//#region src/components/ui/address-display/use-address-label.ts
/**
* Convenience hook for resolving an address label from the nearest
* `AddressLabelProvider`.
*
* Kept in its own file so that `address-label-context.tsx` exports only
* components (required by React Fast Refresh).
*/
/**
* Convenience hook that resolves a label for a specific address using the
* nearest `AddressLabelProvider`. Returns `undefined` values when no provider
* is mounted.
*
* @param address - The blockchain address to resolve
* @param networkId - Optional network identifier for network-specific aliases
* @returns Resolved label and edit handler for the address
*
* @example
* ```tsx
* function MyAddress({ address }: { address: string }) {
*   const { label, onEdit } = useAddressLabel(address, 'ethereum-mainnet');
*   return <span>{label ?? address}</span>;
* }
* ```
*/
function useAddressLabel(address, networkId) {
	const resolver = React$1.useContext(AddressLabelContext);
	const label = resolver?.resolveLabel(address, networkId);
	const onEdit = React$1.useCallback(() => {
		resolver?.onEditLabel?.(address, networkId);
	}, [
		resolver,
		address,
		networkId
	]);
	return {
		label,
		onEdit: resolver?.onEditLabel ? onEdit : void 0
	};
}

//#endregion
//#region src/components/ui/address-display/address-name-context.tsx
/**
* Address Name Context
*
* Provides a React context for surfacing already-resolved reverse name
* records (`ResolvedName`) for blockchain addresses. When an
* `AddressNameProvider` is mounted, every `AddressDisplay` in the subtree
* reads its record synchronously — no call-site changes — and renders the
* name/avatar only when the record is forward-verified (INV-52).
*
* Structural mirror of `AddressLabelProvider` (INV-122): synchronous,
* value-only, no-op by default. The resolver implementation lives in the
* react/renderer layer, which bridges async resolution state into this
* synchronous read; this file stays capability-free (INV-121).
*
* @example
* ```tsx
* import { AddressNameProvider } from '@openzeppelin/ui-components';
*
* <AddressNameProvider resolveAddressName={readFromResolutionCache}>
*   <TxHistory />  {// rows are plain <AddressDisplay/> — verified names fill in }
* </AddressNameProvider>
* ```
*/
/**
* Provides reverse-name resolution to all `AddressDisplay` instances in the
* subtree. `resolveAddressName` MUST be synchronous — it is called during
* render (INV-122). A resolver that returns `undefined` for an address is
* behaviorally identical to mounting no provider (INV-54).
*
* @param props - Resolver function and children
*/
function AddressNameProvider({ children, resolveAddressName, resolveNetworkLabel }) {
	const value = React$1.useMemo(() => ({
		resolveAddressName,
		resolveNetworkLabel
	}), [resolveAddressName, resolveNetworkLabel]);
	return /* @__PURE__ */ jsx(AddressNameContext.Provider, {
		value,
		children
	});
}

//#endregion
//#region src/components/ui/address-display/use-address-name.ts
/**
* Convenience hook for reading a reverse name record from the nearest
* `AddressNameProvider`.
*
* Kept in its own file so that `address-name-context.tsx` exports only
* components (required by React Fast Refresh) — mirrors `use-address-label.ts`.
*/
/**
* Reads the `AddressNameContext` for one address, synchronously (INV-122).
* Returns `{ record: undefined }` when no provider is mounted — never throws,
* never suspends. Note the record is returned verbatim: callers rendering a
* name from it must apply the same `forwardVerified === true` gate that
* `AddressDisplay` applies (INV-52).
*
* @param address - The blockchain address to reverse-resolve
* @param networkId - Optional network identifier scoping the lookup
* @returns The current best-known record for the address
*
* @example
* ```tsx
* function EnsBadge({ address }: { address: string }) {
*   const { record } = useAddressName(address);
*   return record?.forwardVerified ? <Badge>{record.name}</Badge> : null;
* }
* ```
*/
function useAddressName(address, networkId) {
	return { record: React$1.useContext(AddressNameContext)?.resolveAddressName(address, networkId) };
}

//#endregion
//#region src/components/ui/alert.tsx
const alertVariants = cva("relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground", {
	variants: { variant: {
		default: "bg-background text-foreground",
		destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
		success: "border-green-500/50 text-green-700 dark:border-green-500 [&>svg]:text-green-700"
	} },
	defaultVariants: { variant: "default" }
});
const Alert = React$1.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx("div", {
	ref,
	role: "alert",
	className: cn(alertVariants({ variant }), className),
	...props
}));
Alert.displayName = "Alert";
const AlertTitle = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("h5", {
	ref,
	className: cn("mb-1 font-medium leading-none tracking-tight", className),
	...props
}));
AlertTitle.displayName = "AlertTitle";
const AlertDescription = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("text-sm [&_p]:leading-relaxed", className),
	...props
}));
AlertDescription.displayName = "AlertDescription";

//#endregion
//#region src/components/ui/banner.tsx
const variantStyles = {
	info: {
		container: "border-blue-200 bg-blue-50",
		icon: "text-blue-600",
		title: "text-blue-900",
		body: "text-blue-800"
	},
	success: {
		container: "border-green-200 bg-green-50",
		icon: "text-green-600",
		title: "text-green-900",
		body: "text-green-800"
	},
	warning: {
		container: "border-amber-200 bg-amber-50",
		icon: "text-amber-600",
		title: "text-amber-900",
		body: "text-amber-800"
	},
	error: {
		container: "border-red-200 bg-red-50",
		icon: "text-red-600",
		title: "text-red-900",
		body: "text-red-800"
	},
	neutral: {
		container: "border-border bg-muted/30",
		icon: "text-muted-foreground",
		title: "text-foreground",
		body: "text-muted-foreground"
	}
};
const sizeStyles = {
	default: {
		container: "p-4",
		gap: "gap-3",
		title: "mb-2 text-sm font-semibold",
		body: "text-sm whitespace-pre-line",
		dismissIcon: "h-5 w-5"
	},
	compact: {
		container: "px-4 py-3",
		gap: "gap-2",
		title: "mb-1 text-xs font-medium",
		body: "text-xs leading-relaxed whitespace-pre-line",
		dismissIcon: "h-4 w-4"
	}
};
/**
* Dismissible banner component for notifications and alerts
* Can be used with various variants (info, success, warning, error, neutral)
*/
const Banner = React$1.forwardRef(({ className, variant = "info", size = "default", title, children, dismissible = true, onDismiss, icon }, ref) => {
	const styles = variantStyles[variant];
	const sizing = sizeStyles[size];
	return /* @__PURE__ */ jsx("div", {
		ref,
		role: "alert",
		className: cn("rounded-md border", sizing.container, styles.container, className),
		children: /* @__PURE__ */ jsxs("div", {
			className: cn("flex", sizing.gap),
			children: [
				icon && /* @__PURE__ */ jsx("div", {
					className: cn("mt-0.5 shrink-0", styles.icon),
					children: icon
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "min-w-0 flex-1",
					children: [title && /* @__PURE__ */ jsx("h4", {
						className: cn(sizing.title, styles.title),
						children: title
					}), /* @__PURE__ */ jsx("div", {
						className: cn(sizing.body, styles.body),
						children
					})]
				}),
				dismissible && /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: onDismiss,
					className: cn("self-start shrink-0 transition-colors hover:opacity-70", styles.icon),
					"aria-label": "Dismiss banner",
					children: /* @__PURE__ */ jsx(X, { className: sizing.dismissIcon })
				})
			]
		})
	});
});
Banner.displayName = "Banner";

//#endregion
//#region src/utils/button-variants.ts
const buttonVariants = cva("ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50", {
	variants: {
		variant: {
			default: "bg-primary text-primary-foreground hover:bg-primary/90",
			destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
			outline: "border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground border",
			secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
			ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
			link: "text-primary underline-offset-4 hover:underline"
		},
		size: {
			default: "h-10 px-4 py-2",
			sm: "h-9 rounded-md px-3",
			lg: "h-11 rounded-md px-8",
			icon: "size-10"
		}
	},
	defaultVariants: {
		variant: "default",
		size: "default"
	}
});

//#endregion
//#region src/components/ui/button.tsx
const Button = React$1.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "button", {
		"data-slot": "button",
		"data-variant": variant || "default",
		"data-size": size || "default",
		className: cn(buttonVariants({
			variant,
			size,
			className
		}), "cursor-pointer"),
		ref,
		...props
	});
});
Button.displayName = "Button";

//#endregion
//#region src/components/ui/calendar.tsx
/**
* Calendar component built on react-day-picker with shadcn/ui styling.
* Supports single date, multiple dates, and date range selection modes.
*/
function Calendar({ className, classNames, showOutsideDays = true, ...props }) {
	return /* @__PURE__ */ jsx(DayPicker, {
		showOutsideDays,
		className: cn("relative p-3", className),
		classNames: {
			months: "flex flex-col sm:flex-row gap-2 px-5",
			month: "flex flex-col gap-4",
			month_caption: "flex justify-center pt-1 relative items-center w-full",
			caption_label: "text-sm font-medium",
			nav: "flex items-center gap-1",
			button_previous: cn(buttonVariants({ variant: "outline" }), "absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"),
			button_next: cn(buttonVariants({ variant: "outline" }), "absolute right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"),
			month_grid: "w-full border-collapse space-y-1",
			weekdays: "flex",
			weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
			week: "flex w-full mt-2",
			day: cn("relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-selected/15 [&:has([aria-selected].day-outside)]:bg-selected/10 [&:has([aria-selected].day-range-end)]:rounded-r-md", props.mode === "range" ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md" : "[&:has([aria-selected])]:rounded-md"),
			day_button: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-none hover:rounded-none"),
			range_start: "day-range-start rounded-l-md",
			range_end: "day-range-end rounded-r-md",
			selected: "bg-selected text-selected-foreground hover:bg-selected hover:text-selected-foreground focus:bg-selected focus:text-selected-foreground",
			today: "bg-accent text-accent-foreground",
			outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-selected/10 aria-selected:text-muted-foreground aria-selected:opacity-30",
			disabled: "text-muted-foreground opacity-50",
			range_middle: "aria-selected:bg-selected/15 aria-selected:text-foreground",
			hidden: "invisible",
			...classNames
		},
		components: { Chevron: ({ orientation }) => {
			return /* @__PURE__ */ jsx(orientation === "left" ? ChevronLeft : ChevronRight, { className: "h-4 w-4" });
		} },
		...props
	});
}
Calendar.displayName = "Calendar";

//#endregion
//#region src/components/ui/card.tsx
/** Card container component with rounded borders. */
function Card({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card",
		className: cn("bg-card text-card-foreground flex flex-col rounded-xl border", className),
		...props
	});
}
/** Card header section for title and description. Provides top padding for the card. */
function CardHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-header",
		className: cn("flex flex-col gap-1.5 px-6 pt-6", className),
		...props
	});
}
/** Card title with semibold styling. */
function CardTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-title",
		className: cn("leading-none font-semibold", className),
		...props
	});
}
/** Card description with muted text styling. */
function CardDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-description",
		className: cn("text-muted-foreground text-sm", className),
		...props
	});
}
/** Card content area with horizontal padding, top spacing, and bottom padding. */
function CardContent({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-content",
		className: cn("px-6 pt-4 pb-6", className),
		...props
	});
}
/** Card footer with flex alignment for actions, top spacing, and bottom padding. */
function CardFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-footer",
		className: cn("flex items-center px-6 pt-4 pb-6", className),
		...props
	});
}

//#endregion
//#region src/components/ui/checkbox.tsx
/** Checkbox component wrapping Radix UI Checkbox primitive. */
function Checkbox({ className, ...props }) {
	return /* @__PURE__ */ jsx(CheckboxPrimitive.Root, {
		"data-slot": "checkbox",
		className: cn("border-input data-[state=checked]:bg-selected data-[state=checked]:text-selected-foreground data-[state=checked]:border-selected focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50", className),
		...props,
		children: /* @__PURE__ */ jsx(CheckboxPrimitive.Indicator, {
			"data-slot": "checkbox-indicator",
			className: "flex items-center justify-center text-current transition-none",
			children: /* @__PURE__ */ jsx(CheckIcon, {
				"data-slot": "checkbox-indicator-icon",
				className: "size-3.5"
			})
		})
	});
}

//#endregion
//#region src/components/ui/popover.tsx
const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;
const PopoverContent = React$1.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(PopoverPrimitive.Content, {
	ref,
	align,
	sideOffset,
	className: cn("z-50 w-72 rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-md outline-none", "data-[state=open]:animate-in data-[state=closed]:animate-out", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95", "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2", "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className),
	...props
}) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

//#endregion
//#region src/components/ui/date-range-picker.tsx
/**
* DateRangePicker component for selecting a date range.
* Uses react-day-picker with Radix Popover for a shadcn-styled date range selection.
*
* @example
* ```tsx
* const [dateRange, setDateRange] = useState<DateRange | undefined>();
*
* <DateRangePicker
*   value={dateRange}
*   onChange={setDateRange}
*   placeholder="Select date range"
* />
* ```
*/
function DateRangePicker({ value, onChange, placeholder = "Pick a date range", className, disabled = false, numberOfMonths = 2, align = "start" }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("grid gap-2", className),
		children: /* @__PURE__ */ jsxs(Popover, { children: [/* @__PURE__ */ jsx(PopoverTrigger, {
			asChild: true,
			children: /* @__PURE__ */ jsxs(Button, {
				id: "date-range",
				variant: "outline",
				disabled,
				className: cn("w-[280px] justify-start text-left font-normal", !value && "text-muted-foreground"),
				children: [/* @__PURE__ */ jsx(Calendar$1, { className: "mr-2 h-4 w-4" }), value?.from ? value.to ? /* @__PURE__ */ jsxs(Fragment, { children: [
					format(value.from, "LLL dd, y"),
					" - ",
					format(value.to, "LLL dd, y")
				] }) : format(value.from, "LLL dd, y") : /* @__PURE__ */ jsx("span", { children: placeholder })]
			})
		}), /* @__PURE__ */ jsx(PopoverContent, {
			className: "w-auto p-0",
			align,
			children: /* @__PURE__ */ jsx(Calendar, {
				autoFocus: true,
				mode: "range",
				defaultMonth: value?.from,
				selected: value,
				onSelect: onChange,
				numberOfMonths
			})
		})] })
	});
}
DateRangePicker.displayName = "DateRangePicker";

//#endregion
//#region src/components/ui/dialog.tsx
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
	ref,
	className: cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80", className),
	...props
}));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React$1.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(DialogPortal, { children: [/* @__PURE__ */ jsx(DialogOverlay, {}), /* @__PURE__ */ jsxs(DialogPrimitive.Content, {
	ref,
	className: cn("bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex flex-col w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border shadow-lg duration-200 sm:rounded-lg max-h-[90vh]", className),
	...props,
	children: [/* @__PURE__ */ jsxs(DialogPrimitive.Close, {
		className: "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none z-10",
		children: [/* @__PURE__ */ jsxs("svg", {
			xmlns: "http://www.w3.org/2000/svg",
			width: "18",
			height: "18",
			viewBox: "0 0 24 24",
			fill: "none",
			stroke: "currentColor",
			strokeWidth: "2",
			strokeLinecap: "round",
			strokeLinejoin: "round",
			className: "lucide lucide-x",
			children: [/* @__PURE__ */ jsx("path", { d: "M18 6 6 18" }), /* @__PURE__ */ jsx("path", { d: "m6 6 12 12" })]
		}), /* @__PURE__ */ jsx("span", {
			className: "sr-only",
			children: "Close"
		})]
	}), /* @__PURE__ */ jsx("div", {
		className: "overflow-y-auto p-6 flex flex-col gap-4",
		children
	})]
})] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("flex flex-col space-y-1.5 text-center sm:text-left", className),
	...props
});
DialogHeader.displayName = "DialogHeader";
const DialogFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className),
	...props
});
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Title, {
	ref,
	className: cn("text-lg leading-none font-semibold tracking-tight", className),
	...props
}));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Description, {
	ref,
	className: cn("text-muted-foreground text-sm", className),
	...props
}));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

//#endregion
//#region src/components/ui/dropdown-menu.tsx
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const DropdownMenuSubTrigger = React$1.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(DropdownMenuPrimitive.SubTrigger, {
	ref,
	className: cn("flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent", inset && "pl-8", className),
	...props,
	children: [children, /* @__PURE__ */ jsx(ChevronRight, { className: "ml-auto h-4 w-4" })]
}));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.SubContent, {
	ref,
	className: cn("z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className),
	...props
}));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React$1.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.Content, {
	ref,
	sideOffset,
	className: cn("z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className),
	...props
}) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
const DropdownMenuItem = React$1.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Item, {
	ref,
	className: cn("relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", inset && "pl-8", className),
	...props
}));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React$1.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(DropdownMenuPrimitive.CheckboxItem, {
	ref,
	className: cn("relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className),
	checked,
	...props,
	children: [/* @__PURE__ */ jsx("span", {
		className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
		children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) })
	}), children]
}));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = React$1.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(DropdownMenuPrimitive.RadioItem, {
	ref,
	className: cn("relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className),
	...props,
	children: [/* @__PURE__ */ jsx("span", {
		className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
		children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Circle, { className: "h-2 w-2 fill-current" }) })
	}), children]
}));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = React$1.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Label, {
	ref,
	className: cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className),
	...props
}));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Separator, {
	ref,
	className: cn("-mx-1 my-1 h-px bg-muted", className),
	...props
}));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx("span", {
		className: cn("ml-auto text-xs tracking-widest opacity-60", className),
		...props
	});
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

//#endregion
//#region src/components/ui/ecosystem-dropdown.tsx
/** Simple dropdown selector for choosing a blockchain ecosystem. */
function EcosystemDropdown({ options, value, onValueChange, getEcosystemIcon, disabled = false, className, placeholder = "Select blockchain...", "aria-labelledby": ariaLabelledby }) {
	const [open, setOpen] = React$1.useState(false);
	const selectedOption = options.find((o) => o.value === value);
	return /* @__PURE__ */ jsxs(DropdownMenu, {
		open,
		onOpenChange: setOpen,
		children: [/* @__PURE__ */ jsx(DropdownMenuTrigger, {
			asChild: true,
			children: /* @__PURE__ */ jsxs(Button, {
				variant: "outline",
				role: "combobox",
				"aria-expanded": open,
				"aria-labelledby": ariaLabelledby,
				disabled,
				className: cn("w-full justify-between", className),
				children: [/* @__PURE__ */ jsx("span", {
					className: "flex items-center gap-2 truncate",
					children: selectedOption ? /* @__PURE__ */ jsxs(Fragment, { children: [getEcosystemIcon?.(selectedOption.value), /* @__PURE__ */ jsx("span", {
						className: "truncate",
						children: selectedOption.label
					})] }) : /* @__PURE__ */ jsx("span", {
						className: "text-muted-foreground",
						children: placeholder
					})
				}), /* @__PURE__ */ jsx(ChevronDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })]
			})
		}), /* @__PURE__ */ jsx(DropdownMenuContent, {
			className: "w-[--radix-dropdown-menu-trigger-width] min-w-[200px]",
			align: "start",
			children: options.map((option) => /* @__PURE__ */ jsxs(DropdownMenuItem, {
				disabled: !option.enabled,
				onSelect: () => {
					onValueChange(option.value);
					setOpen(false);
				},
				className: "gap-2",
				children: [
					getEcosystemIcon?.(option.value),
					/* @__PURE__ */ jsx("span", {
						className: "flex-1 truncate",
						children: option.label
					}),
					!option.enabled && option.disabledLabel && /* @__PURE__ */ jsx("span", {
						className: "shrink-0 text-xs text-muted-foreground",
						children: option.disabledLabel
					}),
					value === option.value && /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 shrink-0 opacity-100" })
				]
			}, option.value))
		})]
	});
}

//#endregion
//#region src/components/icons/MidnightIcon.tsx
/**
* MidnightIcon - SVG icon for the Midnight blockchain
* Inline SVG to ensure it renders correctly when this package is consumed as a library
*/
function MidnightIcon({ size = 16, className = "", variant: _variant }) {
	return /* @__PURE__ */ jsxs("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		viewBox: "0 0 789.37 789.37",
		width: size,
		height: size,
		className,
		"aria-hidden": "true",
		children: [
			/* @__PURE__ */ jsx("path", {
				d: "m394.69,0C176.71,0,0,176.71,0,394.69s176.71,394.69,394.69,394.69,394.69-176.71,394.69-394.69S612.67,0,394.69,0Zm0,716.6c-177.5,0-321.91-144.41-321.91-321.91S217.18,72.78,394.69,72.78s321.91,144.41,321.91,321.91-144.41,321.91-321.91,321.91Z",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("rect", {
				x: "357.64",
				y: "357.64",
				width: "74.09",
				height: "74.09",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("rect", {
				x: "357.64",
				y: "240.66",
				width: "74.09",
				height: "74.09",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("rect", {
				x: "357.64",
				y: "123.69",
				width: "74.09",
				height: "74.09",
				fill: "currentColor"
			})
		]
	});
}

//#endregion
//#region src/components/ui/ecosystem-icon.tsx
/** Displays the appropriate icon for a blockchain ecosystem. */
function EcosystemIcon({ ecosystem, fallbackLabel, className, size = 16, variant = "branded" }) {
	if (ecosystem.id === "midnight") return /* @__PURE__ */ jsx(MidnightIcon, {
		size,
		variant,
		className: cn("shrink-0", className)
	});
	if (ecosystem.iconComponent) return /* @__PURE__ */ jsx(ecosystem.iconComponent, {
		size,
		variant,
		className: cn("shrink-0", className)
	});
	const initial = (fallbackLabel ?? ecosystem.id).charAt(0).toUpperCase();
	return /* @__PURE__ */ jsx("div", {
		className: cn("bg-muted text-muted-foreground shrink-0 rounded-full flex items-center justify-center font-medium", className),
		style: {
			width: size,
			height: size,
			fontSize: size * .5
		},
		role: "img",
		"aria-label": fallbackLabel ?? ecosystem.id,
		children: initial
	});
}

//#endregion
//#region src/components/ui/empty-state.tsx
/**
* Reusable empty state component for showing helpful messages when content is not available
*/
function EmptyState({ icon, title, description, className = "", size = "default" }) {
	const defaultIcon = /* @__PURE__ */ jsx(FileText, { className: "h-6 w-6 text-muted-foreground" });
	const styles = {
		small: {
			container: "py-6",
			iconContainer: "p-2 mb-2",
			title: "text-base font-medium mb-1",
			description: "text-xs max-w-sm"
		},
		default: {
			container: "py-12",
			iconContainer: "p-3 mb-4",
			title: "text-lg font-medium mb-2",
			description: "text-sm max-w-md"
		},
		large: {
			container: "py-16",
			iconContainer: "p-4 mb-6",
			title: "text-xl font-medium mb-3",
			description: "text-base max-w-lg"
		}
	}[size];
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col items-center justify-center text-center ${styles.container} ${className}`,
		children: [
			/* @__PURE__ */ jsx("div", {
				className: `rounded-full bg-muted ${styles.iconContainer}`,
				children: icon || defaultIcon
			}),
			/* @__PURE__ */ jsx("h3", {
				className: `${styles.title}`,
				children: title
			}),
			/* @__PURE__ */ jsx("p", {
				className: `text-muted-foreground ${styles.description}`,
				children: description
			})
		]
	});
}

//#endregion
//#region src/components/ui/external-link.tsx
const ExternalLink = React.forwardRef(({ children, className, ...props }, ref) => {
	return /* @__PURE__ */ jsxs("a", {
		ref,
		className: cn("inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline", className),
		target: "_blank",
		rel: "noopener noreferrer",
		...props,
		children: [
			children,
			/* @__PURE__ */ jsx(ExternalLinkIcon, { className: "h-3 w-3 ml-1" }),
			" "
		]
	});
});
ExternalLink.displayName = "ExternalLink";

//#endregion
//#region src/components/ui/footer.tsx
/**
* Application footer component with customizable legal links and branding.
*
* This component provides a consistent footer layout with customizable:
* - Company name and copyright year
* - Privacy policy and terms of service links and text
* - Optional visibility controls for legal links
* - Custom styling support
*/
const Footer = ({ companyName = "OpenZeppelin", copyrightYear = (/* @__PURE__ */ new Date()).getFullYear(), privacyPolicyUrl = "https://www.openzeppelin.com/privacy", privacyPolicyText = "Privacy Policy", termsOfServiceUrl = "https://www.openzeppelin.com/tos", termsOfServiceText = "Terms of Service", showPrivacyPolicy = true, showTermsOfService = true, className } = {}) => {
	const hasLegalLinks = showPrivacyPolicy || showTermsOfService;
	return /* @__PURE__ */ jsx("footer", {
		className: cn("border-t bg-background border-[#F5F5F5]", className),
		children: /* @__PURE__ */ jsx("div", {
			className: "px-6 py-4 sm:h-12",
			children: /* @__PURE__ */ jsxs("div", {
				className: "text-sm text-muted-foreground flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-between",
				children: [/* @__PURE__ */ jsxs("span", { children: [
					"© ",
					copyrightYear,
					" ",
					companyName
				] }), hasLegalLinks && /* @__PURE__ */ jsxs("div", {
					className: "flex flex-col items-center gap-2 sm:flex-row sm:gap-4",
					children: [showPrivacyPolicy && /* @__PURE__ */ jsx("a", {
						href: privacyPolicyUrl,
						target: "_blank",
						rel: "noopener noreferrer",
						className: "hover:text-foreground transition-colors",
						children: privacyPolicyText
					}), showTermsOfService && /* @__PURE__ */ jsx("a", {
						href: termsOfServiceUrl,
						target: "_blank",
						rel: "noopener noreferrer",
						className: "hover:text-foreground transition-colors",
						children: termsOfServiceText
					})]
				})]
			})
		})
	});
};

//#endregion
//#region src/components/ui/label.tsx
/**
* Label component for form fields, following shadcn/ui styling
*/
const Label = React$1.forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx(LabelPrimitive.Root, {
		ref,
		"data-slot": "label",
		className: cn("text-sm leading-none font-medium", className),
		...props
	});
});
Label.displayName = LabelPrimitive.Root.displayName;

//#endregion
//#region src/components/ui/form.tsx
/**
* Context provider for the form
*/
const Form = ({ ...props }) => {
	return /* @__PURE__ */ jsx(FormProvider, { ...props });
};
/**
* Context for form field state
*/
const FormFieldContext = React$1.createContext({});
/**
* Get form field context
*/
const useFormField = () => {
	const fieldContext = React$1.useContext(FormFieldContext);
	const itemContext = React$1.useContext(FormItemContext);
	const { getFieldState, formState } = useFormContext();
	const fieldState = getFieldState(fieldContext.name, formState);
	if (!fieldContext) throw new Error("useFormField should be used within <FormField>");
	const { id } = itemContext;
	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		error: fieldState.error
	};
};
/**
* Form field component
*/
const FormField = ({ ...props }) => {
	return /* @__PURE__ */ jsx(FormFieldContext.Provider, {
		value: { name: props.name },
		children: /* @__PURE__ */ jsx(Controller, { ...props })
	});
};
const FormItemContext = React$1.createContext({});
/**
* Form item component
*/
const FormItem = React$1.forwardRef(({ className, ...props }, ref) => {
	const id = React$1.useId();
	return /* @__PURE__ */ jsx(FormItemContext.Provider, {
		value: { id },
		children: /* @__PURE__ */ jsx("div", {
			ref,
			"data-slot": "form-item",
			className: cn("flex flex-col gap-2", className),
			...props
		})
	});
});
FormItem.displayName = "FormItem";
/**
* Form label component
*/
const FormLabel = React$1.forwardRef(({ className, ...props }, ref) => {
	const { error, formItemId } = useFormField();
	return /* @__PURE__ */ jsx(Label, {
		ref,
		className: error ? cn("text-destructive", className) : className,
		htmlFor: formItemId,
		...props
	});
});
FormLabel.displayName = "FormLabel";
/**
* Form control component
*/
const FormControl = React$1.forwardRef(({ ...props }, ref) => {
	const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
	return /* @__PURE__ */ jsx("div", {
		ref,
		id: formItemId,
		"aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`,
		"aria-invalid": !!error,
		...props
	});
});
FormControl.displayName = "FormControl";
/**
* Form description component
*/
const FormDescription = React$1.forwardRef(({ className, ...props }, ref) => {
	const { formDescriptionId } = useFormField();
	return /* @__PURE__ */ jsx("p", {
		ref,
		id: formDescriptionId,
		"data-slot": "form-description",
		className: cn("text-muted-foreground text-sm", className),
		...props
	});
});
FormDescription.displayName = "FormDescription";
/**
* Form error message component
*/
const FormMessage = React$1.forwardRef(({ className, children, ...props }, ref) => {
	const { error, formMessageId } = useFormField();
	const body = error ? String(error?.message) : children;
	if (!body) return null;
	return /* @__PURE__ */ jsx("p", {
		ref,
		id: formMessageId,
		"data-slot": "form-message",
		className: cn("text-destructive text-sm font-medium", className),
		...props,
		children: body
	});
});
FormMessage.displayName = "FormMessage";

//#endregion
//#region src/components/ui/header.tsx
const Header = ({ title, onOpenSidebar, rightContent }) => {
	return /* @__PURE__ */ jsx("header", {
		className: "border-b border-[#F5F5F5] bg-background",
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex h-16 items-center px-3 sm:px-4 md:px-5 min-w-0",
			children: [
				/* @__PURE__ */ jsx("button", {
					type: "button",
					"aria-label": "Open menu",
					onClick: onOpenSidebar,
					className: "mr-2 sm:mr-3 inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary xl:hidden",
					children: /* @__PURE__ */ jsx(Menu, { className: "size-5" })
				}),
				title && /* @__PURE__ */ jsx("div", {
					className: "flex items-center min-w-0",
					children: /* @__PURE__ */ jsx("h1", {
						className: "truncate max-w-[50vw] text-base font-semibold text-foreground",
						children: title
					})
				}),
				/* @__PURE__ */ jsx("div", {
					className: "ml-auto flex items-center",
					children: rightContent
				})
			]
		})
	});
};

//#endregion
//#region src/components/ui/input.tsx
const Input = React$1.forwardRef(({ className, type, ...props }, ref) => {
	return /* @__PURE__ */ jsx("input", {
		type,
		"data-slot": "input",
		className: cn("border-input bg-background ring-offset-background focus-visible:ring-ring placeholder:text-muted-foreground h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50", className),
		ref,
		...props
	});
});
Input.displayName = "Input";

//#endregion
//#region src/components/ui/loading-button.tsx
/**
* Loading spinner component
*/
const Spinner = () => /* @__PURE__ */ jsxs("svg", {
	className: "mr-2 h-4 w-4 animate-spin",
	xmlns: "http://www.w3.org/2000/svg",
	fill: "none",
	viewBox: "0 0 24 24",
	children: [/* @__PURE__ */ jsx("circle", {
		className: "opacity-25",
		cx: "12",
		cy: "12",
		r: "10",
		stroke: "currentColor",
		strokeWidth: "4"
	}), /* @__PURE__ */ jsx("path", {
		className: "opacity-75",
		fill: "currentColor",
		d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
	})]
});
const LoadingButton = React$1.forwardRef(({ className, loading = false, children, disabled, variant, size, asChild = false, ...props }, ref) => {
	return /* @__PURE__ */ jsxs(asChild ? Slot : "button", {
		"data-slot": "button",
		"data-variant": variant || "default",
		"data-size": size || "default",
		className: cn(buttonVariants({
			variant,
			size,
			className
		})),
		ref,
		disabled: loading || disabled,
		...props,
		children: [loading && /* @__PURE__ */ jsx(Spinner, {}), /* @__PURE__ */ jsx(Slottable, { children })]
	});
});
LoadingButton.displayName = "LoadingButton";

//#endregion
//#region src/components/ui/network-icon.tsx
/** Displays the appropriate icon for a blockchain network. */
function NetworkIcon({ network, className, size = 16, variant = "branded" }) {
	if (network.ecosystem === "midnight") return /* @__PURE__ */ jsx(MidnightIcon, {
		size,
		variant,
		className: cn("shrink-0", className)
	});
	if (network.iconComponent) return /* @__PURE__ */ jsx(network.iconComponent, {
		size,
		variant,
		className: cn("shrink-0", className)
	});
	return /* @__PURE__ */ jsx("div", {
		className: cn("bg-muted shrink-0 rounded-full", className),
		style: {
			width: size,
			height: size
		},
		"aria-hidden": "true"
	});
}

//#endregion
//#region src/components/ui/network-selector.tsx
/** Searchable dropdown selector for blockchain networks with optional grouping and multi-select. */
function NetworkSelector({ networks, getNetworkLabel, getNetworkIcon, getNetworkType, getNetworkId, groupByEcosystem = false, getEcosystem, filterNetwork, isNetworkDisabled, getNetworkDisabledLabel, className, placeholder = "Select Network", ...modeProps }) {
	const [open, setOpen] = React$1.useState(false);
	const [searchQuery, setSearchQuery] = React$1.useState("");
	const isMultiple = modeProps.multiple === true;
	const selectedNetworkIds = isMultiple ? modeProps.selectedNetworkIds : void 0;
	const onSelectionChange = isMultiple ? modeProps.onSelectionChange : void 0;
	const selectedNetwork = !isMultiple ? modeProps.selectedNetwork : void 0;
	const onSelectNetwork = !isMultiple ? modeProps.onSelectNetwork : void 0;
	const filteredNetworks = React$1.useMemo(() => {
		if (!searchQuery) return networks;
		if (filterNetwork) return networks.filter((n) => filterNetwork(n, searchQuery));
		return networks.filter((n) => getNetworkLabel(n).toLowerCase().includes(searchQuery.toLowerCase()));
	}, [
		networks,
		searchQuery,
		filterNetwork,
		getNetworkLabel
	]);
	const groupedNetworks = React$1.useMemo(() => {
		if (!groupByEcosystem || !getEcosystem) return { All: filteredNetworks };
		return filteredNetworks.reduce((acc, network) => {
			const ecosystem = getEcosystem(network);
			if (!acc[ecosystem]) acc[ecosystem] = [];
			acc[ecosystem].push(network);
			return acc;
		}, {});
	}, [
		filteredNetworks,
		groupByEcosystem,
		getEcosystem
	]);
	const isSelected = React$1.useCallback((network) => {
		if (isMultiple && selectedNetworkIds) return selectedNetworkIds.includes(getNetworkId(network));
		return selectedNetwork ? getNetworkId(selectedNetwork) === getNetworkId(network) : false;
	}, [
		isMultiple,
		selectedNetworkIds,
		selectedNetwork,
		getNetworkId
	]);
	const handleSelect = React$1.useCallback((network) => {
		if (isNetworkDisabled?.(network)) return;
		if (isMultiple && selectedNetworkIds && onSelectionChange) {
			const id = getNetworkId(network);
			onSelectionChange(selectedNetworkIds.includes(id) ? selectedNetworkIds.filter((x) => x !== id) : [...selectedNetworkIds, id]);
		} else if (onSelectNetwork) {
			onSelectNetwork(network);
			setOpen(false);
		}
	}, [
		isMultiple,
		selectedNetworkIds,
		onSelectionChange,
		onSelectNetwork,
		getNetworkId,
		isNetworkDisabled
	]);
	const handleClearAll = React$1.useCallback(() => {
		if (isMultiple && onSelectionChange) onSelectionChange([]);
	}, [isMultiple, onSelectionChange]);
	const selectedCount = selectedNetworkIds?.length ?? 0;
	const renderTrigger = isMultiple ? modeProps.renderTrigger : void 0;
	return /* @__PURE__ */ jsxs(DropdownMenu, {
		open,
		onOpenChange: setOpen,
		children: [/* @__PURE__ */ jsx(DropdownMenuTrigger, {
			asChild: true,
			children: (() => {
				if (isMultiple && renderTrigger) return renderTrigger({
					selectedCount,
					open
				});
				if (isMultiple) return /* @__PURE__ */ jsxs(Button, {
					variant: "outline",
					role: "combobox",
					"aria-expanded": open,
					className: cn("w-full justify-between", className),
					children: [/* @__PURE__ */ jsx("span", {
						className: "truncate text-muted-foreground",
						children: selectedCount > 0 ? `${selectedCount} network${selectedCount > 1 ? "s" : ""} selected` : placeholder
					}), /* @__PURE__ */ jsx(ChevronDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })]
				});
				return /* @__PURE__ */ jsxs(Button, {
					variant: "outline",
					role: "combobox",
					"aria-expanded": open,
					className: cn("w-full justify-between", className),
					children: [/* @__PURE__ */ jsx("span", {
						className: "flex items-center gap-2 truncate",
						children: selectedNetwork ? /* @__PURE__ */ jsxs(Fragment, { children: [
							getNetworkIcon?.(selectedNetwork),
							/* @__PURE__ */ jsx("span", {
								className: "truncate",
								children: getNetworkLabel(selectedNetwork)
							}),
							getNetworkType && /* @__PURE__ */ jsx("span", {
								className: "shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground",
								children: getNetworkType(selectedNetwork)
							})
						] }) : /* @__PURE__ */ jsx("span", {
							className: "text-muted-foreground",
							children: placeholder
						})
					}), /* @__PURE__ */ jsx(ChevronDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })]
				});
			})()
		}), /* @__PURE__ */ jsxs(DropdownMenuContent, {
			className: "w-[--radix-dropdown-menu-trigger-width] min-w-[240px] p-0",
			align: "start",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center border-b px-3",
				children: [/* @__PURE__ */ jsx(Search, { className: "mr-2 h-4 w-4 shrink-0 opacity-50" }), /* @__PURE__ */ jsx(Input, {
					placeholder: "Search network...",
					value: searchQuery,
					onChange: (e) => setSearchQuery(e.target.value),
					onKeyDown: (e) => {
						if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") e.stopPropagation();
					},
					className: "h-9 w-full border-0 bg-transparent p-0 placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0",
					"aria-label": "Search networks"
				})]
			}), /* @__PURE__ */ jsxs("div", {
				className: "max-h-[300px] overflow-y-auto p-1",
				children: [isMultiple && selectedCount > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs("div", {
					className: "flex items-center justify-between px-2 py-1.5",
					children: [/* @__PURE__ */ jsxs("span", {
						className: "text-xs font-medium text-muted-foreground",
						children: [selectedCount, " selected"]
					}), /* @__PURE__ */ jsx("button", {
						onClick: handleClearAll,
						className: "text-xs text-muted-foreground hover:text-foreground",
						children: "Clear all"
					})]
				}), /* @__PURE__ */ jsx(DropdownMenuSeparator, {})] }), Object.entries(groupedNetworks).length === 0 ? /* @__PURE__ */ jsx("div", {
					className: "py-6 text-center text-sm text-muted-foreground",
					children: "No network found."
				}) : Object.entries(groupedNetworks).map(([group, groupNetworks], index) => /* @__PURE__ */ jsxs(React$1.Fragment, { children: [
					groupByEcosystem && /* @__PURE__ */ jsx(DropdownMenuLabel, {
						className: "text-xs font-medium text-muted-foreground",
						children: group
					}),
					/* @__PURE__ */ jsx(DropdownMenuGroup, { children: groupNetworks.map((network) => {
						const disabled = isNetworkDisabled?.(network) ?? false;
						const disabledLabel = disabled ? getNetworkDisabledLabel?.(network) : void 0;
						return /* @__PURE__ */ jsxs(DropdownMenuItem, {
							disabled,
							onSelect: (e) => {
								if (disabled) {
									e.preventDefault();
									return;
								}
								if (isMultiple) e.preventDefault();
								handleSelect(network);
							},
							className: cn("gap-2", disabled && "cursor-not-allowed opacity-60"),
							children: [
								isMultiple ? /* @__PURE__ */ jsx("div", {
									className: cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors", isSelected(network) ? "border-selected bg-selected text-selected-foreground" : "border-input bg-background", disabled && "opacity-50"),
									children: isSelected(network) && /* @__PURE__ */ jsx(Check, { className: "h-3 w-3" })
								}) : null,
								getNetworkIcon?.(network),
								/* @__PURE__ */ jsxs("div", {
									className: "flex flex-1 items-center gap-2 min-w-0",
									children: [
										/* @__PURE__ */ jsx("span", {
											className: "truncate",
											children: getNetworkLabel(network)
										}),
										getNetworkType && /* @__PURE__ */ jsx("span", {
											className: "shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground",
											children: getNetworkType(network)
										}),
										disabledLabel ? /* @__PURE__ */ jsx("span", {
											className: "shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground",
											children: disabledLabel
										}) : null
									]
								}),
								!isMultiple && isSelected(network) && /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-selected opacity-100" })
							]
						}, getNetworkId(network));
					}) }),
					index < Object.keys(groupedNetworks).length - 1 && /* @__PURE__ */ jsx(DropdownMenuSeparator, {})
				] }, group))]
			})]
		})]
	});
}

//#endregion
//#region src/components/ui/network-status-badge.tsx
const ICON_SIZE = 16;
/**
* NetworkStatusBadge - Displays network information in a compact badge format
* Shows the network icon, ecosystem, and name with dashed borders for testnet/devnet
*/
function NetworkStatusBadge({ network, className }) {
	if (!network) return null;
	return /* @__PURE__ */ jsxs("div", {
		className: cn("inline-flex items-center gap-2 rounded-full bg-muted px-3 py-2", network.type === "testnet" || network.type === "devnet" ? "border-2 border-dashed border-muted-foreground/40" : "border-0", className),
		children: [/* @__PURE__ */ jsx(NetworkIcon, {
			network,
			size: ICON_SIZE
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex items-baseline gap-1.5",
			children: [/* @__PURE__ */ jsx("span", {
				className: "text-xs font-medium text-muted-foreground uppercase",
				children: network.ecosystem
			}), /* @__PURE__ */ jsx("span", {
				className: "text-xs font-medium",
				children: network.name
			})]
		})]
	});
}

//#endregion
//#region src/components/ui/overflow-menu.tsx
/** Compact "..." dropdown menu for secondary actions. */
function OverflowMenu({ items, align = "end", className, "aria-label": ariaLabel = "More actions" }) {
	if (items.length === 0) return null;
	return /* @__PURE__ */ jsxs(DropdownMenu, { children: [/* @__PURE__ */ jsx(DropdownMenuTrigger, {
		asChild: true,
		children: /* @__PURE__ */ jsx(Button, {
			variant: "ghost",
			size: "icon",
			className: cn("h-8 w-8", className),
			"aria-label": ariaLabel,
			children: /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" })
		})
	}), /* @__PURE__ */ jsx(DropdownMenuContent, {
		align,
		className: "min-w-[140px]",
		children: items.map((item, index) => /* @__PURE__ */ jsxs(React$1.Fragment, { children: [item.destructive && index > 0 && /* @__PURE__ */ jsx(DropdownMenuSeparator, {}), /* @__PURE__ */ jsxs(DropdownMenuItem, {
			onClick: item.onSelect,
			disabled: item.disabled,
			className: cn(item.destructive && "text-destructive focus:text-destructive"),
			children: [item.icon, item.label]
		})] }, item.id))
	})] });
}

//#endregion
//#region src/components/ui/progress.tsx
const Progress = React$1.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ jsx(ProgressPrimitive.Root, {
	ref,
	className: cn("relative h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800", className),
	...props,
	children: /* @__PURE__ */ jsx(ProgressPrimitive.Indicator, {
		className: "bg-primary h-full w-full flex-1 transition-all",
		style: { transform: `translateX(-${100 - (value || 0)}%)` }
	})
}));
Progress.displayName = ProgressPrimitive.Root.displayName;

//#endregion
//#region src/components/ui/radio-group.tsx
const RadioGroup = React$1.forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx(RadioGroupPrimitive.Root, {
		className: cn("flex flex-col space-y-2", className),
		"data-slot": "radiogroup",
		...props,
		ref
	});
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
const RadioGroupItem = React$1.forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx(RadioGroupPrimitive.Item, {
		ref,
		className: cn("border-input bg-background text-selected h-4 w-4 rounded-full border shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className),
		...props,
		children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, {
			className: "flex items-center justify-center",
			children: /* @__PURE__ */ jsx(Circle, { className: "h-2.5 w-2.5 fill-current text-current" })
		})
	});
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

//#endregion
//#region src/components/ui/select.tsx
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;
const SelectTrigger = React$1.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
	ref,
	"data-slot": "select-trigger",
	className: cn("border-input bg-background placeholder:text-muted-foreground flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1", className),
	...props,
	children: [children, /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
		asChild: true,
		children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 opacity-50" })
	})]
}));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectScrollUpButton = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, {
	ref,
	"data-slot": "select-scroll-button",
	className,
	...props,
	children: /* @__PURE__ */ jsx(ChevronUp, { className: "h-4 w-4" })
}));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, {
	ref,
	"data-slot": "select-scroll-button",
	className,
	...props,
	children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4" })
}));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
const SelectContent = React$1.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(SelectPrimitive.Content, {
	ref,
	"data-slot": "select-content",
	className: cn("bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border shadow-md", position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1", className),
	position,
	...props,
	children: [
		/* @__PURE__ */ jsx(SelectScrollUpButton, {}),
		/* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
			className: cn("p-1", position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"),
			children
		}),
		/* @__PURE__ */ jsx(SelectScrollDownButton, {})
	]
}) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Label, {
	ref,
	"data-slot": "select-label",
	className: cn("py-1.5 pr-2 pl-8 text-sm font-semibold", className),
	...props
}));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = React$1.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(SelectPrimitive.Item, {
	ref,
	"data-slot": "select-item",
	className: cn("hover:bg-accent relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm transition-colors duration-150 outline-none select-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className),
	...props,
	children: [/* @__PURE__ */ jsx("span", {
		"data-slot": "select-item-indicator",
		className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
		children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) })
	}), /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })]
}));
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Separator, {
	ref,
	"data-slot": "select-separator",
	className: cn("bg-muted -mx-1 my-1 h-px", className),
	...props
}));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

//#endregion
//#region src/components/ui/sidebar/SidebarButton.tsx
/**
* A styled button component for sidebar actions with consistent styling.
* Can render as a button or anchor element depending on whether href is provided.
*/
function SidebarButton({ icon, children, onClick, size = "default", badge, disabled = false, isSelected = false, href, target, rel, className }) {
	const commonClass = cn("group relative flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 rounded-lg font-semibold text-sm transition-colors", badge ? "justify-between" : "justify-start", disabled ? "text-muted-foreground/60 cursor-not-allowed" : isSelected ? "text-selected bg-selected/10" : "text-muted-foreground hover:text-foreground cursor-pointer hover:before:content-[\"\"] hover:before:absolute hover:before:inset-x-0 hover:before:top-1 hover:before:bottom-1 hover:before:bg-muted/80 hover:before:rounded-lg hover:before:-z-10", size === "small" ? "min-h-10" : "min-h-11", className);
	const content = /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs("div", {
		className: "flex items-center gap-2",
		children: [icon, children]
	}), badge && /* @__PURE__ */ jsx("span", {
		className: "text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full font-medium",
		children: badge
	})] });
	if (href) return /* @__PURE__ */ jsx("a", {
		href,
		target,
		rel,
		className: commonClass,
		onClick,
		children: content
	});
	return /* @__PURE__ */ jsx("button", {
		className: commonClass,
		onClick: disabled ? void 0 : onClick,
		disabled,
		children: content
	});
}

//#endregion
//#region src/components/ui/sidebar/SidebarGroup.tsx
/**
* A collapsible group component for organizing sidebar navigation items.
* Supports both controlled and uncontrolled modes.
*/
function SidebarGroup({ title, icon, children, defaultOpen = false, open: controlledOpen, onOpenChange, className, triggerClassName, contentClassName }) {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const isControlled = controlledOpen !== void 0;
	const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
	const handleOpenChange = (nextOpen) => {
		if (!isControlled) setUncontrolledOpen(nextOpen);
		onOpenChange?.(nextOpen);
	};
	return /* @__PURE__ */ jsxs(CollapsiblePrimitive.Root, {
		open: isOpen,
		onOpenChange: handleOpenChange,
		className: cn("w-full", className),
		children: [/* @__PURE__ */ jsxs(CollapsiblePrimitive.Trigger, {
			className: cn("group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold", "text-muted-foreground transition-colors hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", triggerClassName),
			children: [
				/* @__PURE__ */ jsx(ChevronRight, { className: cn("size-4 shrink-0 transition-transform duration-200", isOpen && "rotate-90") }),
				icon && /* @__PURE__ */ jsx("span", {
					className: "shrink-0",
					children: icon
				}),
				/* @__PURE__ */ jsx("span", {
					className: "flex-1 text-left",
					children: title
				})
			]
		}), /* @__PURE__ */ jsx(CollapsiblePrimitive.Content, {
			className: cn("overflow-hidden transition-all duration-200 ease-in-out", "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down", contentClassName),
			children: /* @__PURE__ */ jsx("div", {
				className: "py-1 pl-4",
				children
			})
		})]
	});
}

//#endregion
//#region src/components/ui/sidebar/SidebarLayout.tsx
/**
* A flexible sidebar layout component with desktop sidebar and mobile slide-over.
* Provides slots for header, scrollable content, and footer.
*/
function SidebarLayout({ header, subHeader, children, footer, className, width = 289, background = "bg-[rgba(245,245,245,0.31)]", mobileOpen, onMobileOpenChange, mobileAriaLabel = "Menu" }) {
	const widthStyle = typeof width === "number" ? `${width}px` : width;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsxs("div", {
			className: cn("fixed left-0 top-0 z-40 h-full hidden xl:flex xl:flex-col", background, className),
			style: { width: widthStyle },
			children: [
				header && /* @__PURE__ */ jsx("div", {
					className: "shrink-0 px-8 pt-12",
					children: header
				}),
				subHeader && /* @__PURE__ */ jsx("div", {
					className: "shrink-0 px-8 mt-6",
					children: subHeader
				}),
				/* @__PURE__ */ jsx("div", {
					className: "flex-1 overflow-y-auto px-8 pb-24",
					children
				}),
				footer && /* @__PURE__ */ jsx("div", {
					className: "pointer-events-auto sticky bottom-0 px-8 py-4",
					children: footer
				})
			]
		}),
		/* @__PURE__ */ jsx("div", {
			className: "hidden xl:block",
			style: { width: widthStyle }
		}),
		typeof mobileOpen === "boolean" && onMobileOpenChange && /* @__PURE__ */ jsxs("div", {
			className: cn("xl:hidden fixed inset-0 z-50", mobileOpen ? "pointer-events-auto" : "pointer-events-none"),
			"aria-hidden": !mobileOpen,
			children: [/* @__PURE__ */ jsx("div", {
				className: cn("absolute inset-0 bg-black/40 transition-opacity", mobileOpen ? "opacity-100" : "opacity-0"),
				onClick: () => onMobileOpenChange(false)
			}), /* @__PURE__ */ jsx("div", {
				className: cn("absolute left-0 top-0 h-full w-[85%] max-w-[320px] bg-[rgba(245,245,245,0.98)] shadow-xl transition-transform", mobileOpen ? "translate-x-0" : "-translate-x-full"),
				role: "dialog",
				"aria-modal": "true",
				"aria-label": mobileAriaLabel,
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex h-full flex-col",
					children: [
						header && /* @__PURE__ */ jsx("div", {
							className: "shrink-0 px-6 pt-10 pb-4",
							children: header
						}),
						subHeader && /* @__PURE__ */ jsx("div", {
							className: "shrink-0 px-6 pb-4",
							children: subHeader
						}),
						/* @__PURE__ */ jsx("div", {
							className: "flex-1 overflow-y-auto px-6 pb-20",
							children
						}),
						footer && /* @__PURE__ */ jsx("div", {
							className: "px-6 py-4",
							children: footer
						})
					]
				})
			})]
		})
	] });
}

//#endregion
//#region src/components/ui/sidebar/SidebarSection.tsx
/**
* A generic sidebar section wrapper with optional title.
* Used to group related sidebar items together.
*/
function SidebarSection({ title, children, className, titleClassName, grow = false }) {
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex flex-col w-full", grow && "flex-1", className),
		children: [title && /* @__PURE__ */ jsx("div", {
			className: cn("text-[#5e5e5e] text-xs font-semibold leading-4 mb-1", titleClassName),
			children: title
		}), /* @__PURE__ */ jsx("div", {
			className: cn("flex flex-col", grow && "flex-1 overflow-hidden"),
			children
		})]
	});
}

//#endregion
//#region src/components/ui/tabs.tsx
/** Tabs container component wrapping Radix UI Tabs Root. */
function Tabs({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.Root, {
		"data-slot": "tabs",
		className: cn("flex w-full max-w-full min-w-0 flex-col gap-2 overflow-x-hidden", className),
		...props
	});
}
/** Tabs list component containing tab triggers. */
function TabsList({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.List, {
		"data-slot": "tabs-list",
		className: cn("bg-muted text-muted-foreground flex h-9 w-full max-w-full min-w-0 items-center justify-start rounded-lg p-1 overflow-x-auto overflow-y-hidden flex-nowrap overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:overflow-visible", className),
		...props
	});
}
/** Individual tab trigger button. */
function TabsTrigger({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.Trigger, {
		"data-slot": "tabs-trigger",
		className: cn("data-[state=active]:bg-background data-[state=active]:text-selected focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		...props
	});
}
/** Tab content panel displayed when its trigger is active. */
function TabsContent({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.Content, {
		"data-slot": "tabs-content",
		className: cn("flex-1 outline-none", className),
		...props
	});
}

//#endregion
//#region src/components/ui/textarea.tsx
const Textarea = React$1.forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx("textarea", {
		"data-slot": "textarea",
		className: cn("border-input bg-background ring-offset-background focus-visible:ring-ring placeholder:text-muted-foreground aria-invalid:ring-destructive/20 aria-invalid:border-destructive dark:aria-invalid:ring-destructive/40 min-h-16 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50", className),
		ref,
		...props
	});
});
Textarea.displayName = "Textarea";

//#endregion
//#region src/components/ui/view-contract-state-button.tsx
/**
* ViewContractStateButton - A button to toggle the contract state widget
* Shows the contract address in a truncated format
*/
function ViewContractStateButton({ contractAddress, onToggle }) {
	if (!contractAddress) return null;
	return /* @__PURE__ */ jsxs(Button, {
		variant: "outline",
		size: "sm",
		className: "px-3 py-2 h-auto gap-2 items-start justify-start sm:items-center",
		onClick: onToggle,
		title: "Show Contract State",
		children: [/* @__PURE__ */ jsx(FileText, {
			size: 16,
			className: "shrink-0 self-center"
		}), /* @__PURE__ */ jsxs("span", {
			className: "flex flex-col sm:flex-row sm:items-center text-left leading-tight",
			children: [/* @__PURE__ */ jsx("span", {
				className: "text-sm font-medium",
				children: "View Contract State"
			}), /* @__PURE__ */ jsxs("span", {
				className: "text-xs text-muted-foreground sm:ml-1",
				children: [
					"(",
					truncateMiddle(contractAddress),
					")"
				]
			})]
		})]
	});
}

//#endregion
//#region src/components/ui/wizard/WizardStepper.tsx
function resolveState(step, index, currentStepIndex, furthestStepIndex) {
	if (step.status === "completed" || step.status === "skipped") return "completed";
	if (index === currentStepIndex) return "current";
	if (step.isInvalid && (index < currentStepIndex || index <= furthestStepIndex)) return "invalid";
	if (index < currentStepIndex) return "completed";
	if (index <= furthestStepIndex) return "visited";
	return "upcoming";
}
function canClick(state, freeNavigation = false) {
	if (freeNavigation) return true;
	return state !== "upcoming";
}
function StepCircle({ state, index }) {
	return /* @__PURE__ */ jsx("span", {
		className: cn("flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all", state === "completed" && "bg-selected text-selected-foreground", state === "current" && "bg-selected text-selected-foreground ring-2 ring-selected/30", state === "visited" && "bg-selected/10 text-selected ring-1 ring-selected/40", state === "invalid" && "bg-destructive/10 text-destructive ring-1 ring-destructive/40", state === "upcoming" && "bg-muted text-muted-foreground"),
		children: state === "completed" ? /* @__PURE__ */ jsx(Check, { className: "size-3.5" }) : state === "visited" ? /* @__PURE__ */ jsx(Pencil, { className: "size-3" }) : state === "invalid" ? /* @__PURE__ */ jsx(AlertCircle, { className: "size-3.5" }) : index + 1
	});
}
function StepLabel({ title, state, isSkipped }) {
	return /* @__PURE__ */ jsxs("div", {
		className: "min-w-0 flex-1",
		children: [/* @__PURE__ */ jsx("span", {
			className: cn("text-sm font-medium transition-colors", state === "current" && "text-selected", state === "completed" && "text-foreground", state === "visited" && "text-selected", state === "invalid" && "text-destructive", state === "upcoming" && "text-muted-foreground"),
			children: title
		}), isSkipped && /* @__PURE__ */ jsx("span", {
			className: "mt-0.5 block text-[11px] text-muted-foreground",
			children: "Skipped"
		})]
	});
}
function VerticalStepper({ steps, currentStepIndex, furthestStepIndex = currentStepIndex, onStepClick, freeNavigation, className }) {
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": "Wizard steps",
		className: cn("rounded-2xl border border-border bg-card p-6", className),
		children: /* @__PURE__ */ jsx("div", {
			className: "flex flex-col gap-1",
			children: steps.map((step, index) => {
				const state = resolveState(step, index, currentStepIndex, furthestStepIndex);
				const clickable = canClick(state, freeNavigation) && !!onStepClick;
				return /* @__PURE__ */ jsxs("button", {
					type: "button",
					onClick: () => clickable && onStepClick?.(index),
					disabled: !clickable,
					className: cn("flex items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-left transition-all duration-150", clickable ? "cursor-pointer" : "cursor-not-allowed opacity-50", state === "current" && "border-selected/40 bg-selected/5", state === "completed" && "bg-card hover:bg-muted/40", state === "visited" && "bg-card hover:bg-selected/5", state === "invalid" && "border-destructive/40 bg-destructive/5 hover:bg-destructive/10", state === "upcoming" && "bg-card"),
					"aria-current": state === "current" ? "step" : void 0,
					children: [/* @__PURE__ */ jsx(StepCircle, {
						state,
						index
					}), /* @__PURE__ */ jsx(StepLabel, {
						title: step.title,
						state,
						isSkipped: step.status === "skipped"
					})]
				}, step.id);
			})
		})
	});
}
function HorizontalStepper({ steps, currentStepIndex, furthestStepIndex = currentStepIndex, onStepClick, freeNavigation, className }) {
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": "Wizard steps",
		className: cn("rounded-2xl border border-border bg-card p-6", className),
		children: /* @__PURE__ */ jsx("div", {
			className: "flex w-full items-center",
			children: steps.map((step, index) => {
				const state = resolveState(step, index, currentStepIndex, furthestStepIndex);
				const clickable = canClick(state, freeNavigation) && !!onStepClick;
				const isLast = index === steps.length - 1;
				return /* @__PURE__ */ jsxs(React.Fragment, { children: [/* @__PURE__ */ jsxs("button", {
					type: "button",
					onClick: () => clickable && onStepClick?.(index),
					disabled: !clickable,
					className: cn("flex items-center gap-2 rounded-xl border border-transparent px-3 py-2 text-left transition-all duration-150", clickable ? "cursor-pointer" : "cursor-not-allowed opacity-50", state === "current" && "border-selected/40 bg-selected/5", state === "completed" && "bg-card hover:bg-muted/40", state === "visited" && "bg-card hover:bg-selected/5", state === "invalid" && "border-destructive/40 bg-destructive/5 hover:bg-destructive/10", state === "upcoming" && "bg-card"),
					"aria-current": state === "current" ? "step" : void 0,
					"aria-label": `Step ${index + 1}: ${step.title}`,
					children: [/* @__PURE__ */ jsx(StepCircle, {
						state,
						index
					}), /* @__PURE__ */ jsx("div", {
						className: "hidden sm:block",
						children: /* @__PURE__ */ jsx(StepLabel, {
							title: step.title,
							state,
							isSkipped: step.status === "skipped"
						})
					})]
				}), !isLast && /* @__PURE__ */ jsx("div", { className: cn("mx-1 h-px flex-1 transition-colors sm:mx-2", index < currentStepIndex ? "bg-selected" : "bg-border") })] }, step.id);
			})
		})
	});
}
/**
* A stepper component for navigating through a series of steps.
*
* @param props - The props for the WizardStepper component.
* @returns A React node representing the stepper component.
*/
function WizardStepper(props) {
	const { variant = "horizontal", ...rest } = props;
	return variant === "vertical" ? /* @__PURE__ */ jsx(VerticalStepper, {
		...rest,
		variant
	}) : /* @__PURE__ */ jsx(HorizontalStepper, {
		...rest,
		variant
	});
}

//#endregion
//#region src/components/ui/wizard/WizardNavigation.tsx
/**
* A navigation component for the wizard.
*
* @param props - The props for the WizardNavigation component.
* @returns A React node representing the navigation component.
*/
function WizardNavigation({ isFirstStep, isLastStep, canProceed = true, onPrevious, onNext, onCancel, extraActions, nextLabel = "Next", lastStepLabel = "Finish", onLastStepSecondary, lastStepSecondaryLabel, lastStepSecondaryDisabled, showLastStepPrimary = true, className }) {
	const showPrimary = !isLastStep || showLastStepPrimary;
	const showSecondary = isLastStep && lastStepSecondaryLabel != null && lastStepSecondaryLabel !== "" && onLastStepSecondary != null;
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex items-center justify-between", className),
		children: [/* @__PURE__ */ jsxs("div", {
			className: "flex gap-2",
			children: [onCancel && /* @__PURE__ */ jsxs(Button, {
				type: "button",
				variant: "outline",
				onClick: onCancel,
				className: "gap-2",
				children: [/* @__PURE__ */ jsx(X, { className: "size-4" }), "Cancel"]
			}), !isFirstStep && /* @__PURE__ */ jsxs(Button, {
				type: "button",
				variant: "outline",
				onClick: onPrevious,
				className: "gap-2",
				children: [/* @__PURE__ */ jsx(ChevronLeft, { className: "size-4" }), "Previous"]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex gap-2",
			children: [
				extraActions,
				showSecondary && /* @__PURE__ */ jsx(Button, {
					type: "button",
					variant: "outline",
					onClick: onLastStepSecondary,
					disabled: lastStepSecondaryDisabled ?? false,
					className: "gap-2",
					children: lastStepSecondaryLabel
				}),
				showPrimary && /* @__PURE__ */ jsxs(Button, {
					type: "button",
					onClick: onNext,
					disabled: !canProceed,
					className: "gap-2",
					children: [isLastStep ? lastStepLabel : nextLabel, !isLastStep && /* @__PURE__ */ jsx(ChevronRight, { className: "size-4" })]
				})
			]
		})]
	});
}

//#endregion
//#region src/components/ui/wizard/hooks.ts
/**
* Clamp a step index into the valid range for the current wizard.
*/
function getSafeStepIndex(stepCount, currentStepIndex) {
	if (stepCount === 0) return 0;
	return Math.max(0, Math.min(currentStepIndex, stepCount - 1));
}
/**
* Track the highest step reached unless a controlled value is provided.
*/
function useFurthestStepIndex(currentStepIndex, controlledFurthestStepIndex) {
	const [internalFurthestStepIndex, setInternalFurthestStepIndex] = useState(currentStepIndex);
	useEffect(() => {
		setInternalFurthestStepIndex((prev) => Math.max(prev, currentStepIndex));
	}, [currentStepIndex]);
	return controlledFurthestStepIndex ?? internalFurthestStepIndex;
}
/**
* Keep the scrollable wizard's active and visited step state in sync with scrolling and clicks.
*/
function useScrollableWizardStepTracking({ steps, currentStepIndex, onStepChange, scrollRef, sectionId, scrollPadding = SCROLL_PADDING_PX }) {
	const safeIndex = getSafeStepIndex(steps.length, currentStepIndex);
	const initialIndexRef = useRef(safeIndex);
	const rafRef = useRef(null);
	const manualSelectionIndexRef = useRef(null);
	const stepsRef = useRef(steps);
	const sectionIdRef = useRef(sectionId);
	const onStepChangeRef = useRef(onStepChange);
	const scrollPaddingRef = useRef(scrollPadding);
	useEffect(() => {
		stepsRef.current = steps;
		sectionIdRef.current = sectionId;
		onStepChangeRef.current = onStepChange;
		scrollPaddingRef.current = scrollPadding;
	});
	const [activeIndex, setActiveIndex] = useState(initialIndexRef.current);
	const activeIndexRef = useRef(initialIndexRef.current);
	const [furthestStepIndex, setFurthestStepIndex] = useState(initialIndexRef.current);
	const isMountedRef = useRef(false);
	const clearManualSelection = useCallback(() => {
		manualSelectionIndexRef.current = null;
	}, []);
	useEffect(() => {
		const container = scrollRef.current;
		if (!container) return;
		const ownerDocument = container.ownerDocument;
		isMountedRef.current = false;
		let didCompleteInitialRaf = false;
		const releaseManualSelectionOnUserScroll = () => {
			clearManualSelection();
		};
		const handleKeyDown = (event) => {
			if (isScrollableNavigationKey(event)) clearManualSelection();
		};
		const handleScroll = () => {
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
			rafRef.current = requestAnimationFrame(() => {
				const currentSteps = stepsRef.current;
				const currentSectionId = sectionIdRef.current;
				const currentOnStepChange = onStepChangeRef.current;
				if (currentSteps.length === 0) return;
				const manualSelectionIndex = manualSelectionIndexRef.current;
				const naturalState = resolveScrollableActiveIndex(container, currentSteps, currentSectionId);
				const naturalActiveIndex = naturalState.activeIndex;
				const newActiveIndex = manualSelectionIndex ?? naturalActiveIndex;
				const shouldCommitFurthestStepIndex = manualSelectionIndex !== null ? true : naturalState.commitFurthestStepIndex;
				if (activeIndexRef.current !== newActiveIndex) {
					activeIndexRef.current = newActiveIndex;
					setActiveIndex(newActiveIndex);
					if (isMountedRef.current) {
						lastEmittedIndexRef.current = newActiveIndex;
						currentOnStepChange(newActiveIndex);
					}
				} else setActiveIndex(newActiveIndex);
				if (shouldCommitFurthestStepIndex) setFurthestStepIndex((prev) => Math.max(prev, newActiveIndex));
				rafRef.current = null;
				if (!didCompleteInitialRaf) {
					didCompleteInitialRaf = true;
					isMountedRef.current = true;
				}
			});
		};
		container.addEventListener("wheel", releaseManualSelectionOnUserScroll, { passive: true });
		container.addEventListener("touchmove", releaseManualSelectionOnUserScroll, { passive: true });
		container.addEventListener("pointerdown", releaseManualSelectionOnUserScroll);
		ownerDocument.addEventListener("keydown", handleKeyDown);
		container.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();
		return () => {
			isMountedRef.current = false;
			container.removeEventListener("wheel", releaseManualSelectionOnUserScroll);
			container.removeEventListener("touchmove", releaseManualSelectionOnUserScroll);
			container.removeEventListener("pointerdown", releaseManualSelectionOnUserScroll);
			ownerDocument.removeEventListener("keydown", handleKeyDown);
			container.removeEventListener("scroll", handleScroll);
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
		};
	}, [clearManualSelection, scrollRef]);
	const lastEmittedIndexRef = useRef(safeIndex);
	useEffect(() => {
		const newSafeIndex = getSafeStepIndex(stepsRef.current.length, currentStepIndex);
		if (newSafeIndex === lastEmittedIndexRef.current) return;
		lastEmittedIndexRef.current = newSafeIndex;
		activeIndexRef.current = newSafeIndex;
		setActiveIndex(newSafeIndex);
		setFurthestStepIndex((prev) => Math.max(prev, newSafeIndex));
		const step = stepsRef.current[newSafeIndex];
		if (!step) return;
		const sectionElement = scrollRef.current?.querySelector(`#${CSS.escape(sectionIdRef.current(step.id))}`);
		if (scrollRef.current && sectionElement) scrollSectionIntoView(scrollRef.current, sectionElement, scrollPaddingRef.current);
	}, [currentStepIndex, scrollRef]);
	return {
		activeIndex,
		furthestStepIndex,
		scrollToSection: useCallback((index) => {
			const step = stepsRef.current[index];
			if (!step) return;
			manualSelectionIndexRef.current = index;
			activeIndexRef.current = index;
			lastEmittedIndexRef.current = index;
			setActiveIndex(index);
			setFurthestStepIndex((prev) => Math.max(prev, index));
			onStepChangeRef.current(index);
			const container = scrollRef.current;
			const sectionElement = container?.querySelector(`#${CSS.escape(sectionIdRef.current(step.id))}`);
			if (container && sectionElement) scrollSectionIntoView(container, sectionElement, scrollPaddingRef.current);
		}, [scrollRef])
	};
}
function resolveScrollableActiveIndex(container, steps, sectionId) {
	if (steps.length === 0) return {
		activeIndex: 0,
		commitFurthestStepIndex: false
	};
	const containerRect = container.getBoundingClientRect();
	const anchorY = containerRect.top + Math.min(containerRect.height * .35, 220);
	const isScrollable = container.scrollHeight > container.clientHeight + 1;
	const isAtBottom = isScrollable && container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
	const isNearBottom = isScrollable && container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
	if (isAtBottom) return {
		activeIndex: steps.length - 1,
		commitFurthestStepIndex: false
	};
	let activeIndex = 0;
	let highestScore = Number.NEGATIVE_INFINITY;
	for (let i = 0; i < steps.length; i++) {
		const sectionMetrics = getSectionMetrics(container, steps[i].id, sectionId, containerRect);
		if (!sectionMetrics) continue;
		const score = scoreScrollableStep({
			stepIndex: i,
			stepCount: steps.length,
			containerRect,
			anchorY,
			isNearBottom,
			...sectionMetrics
		});
		if (score >= highestScore) {
			highestScore = score;
			activeIndex = i;
		}
	}
	return {
		activeIndex,
		commitFurthestStepIndex: true
	};
}
const SCROLL_PADDING_PX = 32;
function getSectionElement(container, stepId, sectionId) {
	return container.querySelector(`#${CSS.escape(sectionId(stepId))}`);
}
function scrollSectionIntoView(container, sectionElement, padding) {
	const elementTop = sectionElement.getBoundingClientRect().top;
	const containerTop = container.getBoundingClientRect().top;
	const targetScrollTop = container.scrollTop + (elementTop - containerTop) - padding;
	container.scrollTo({
		top: targetScrollTop,
		behavior: "smooth"
	});
}
function getSectionMetrics(container, stepId, sectionId, containerRect) {
	const sectionElement = getSectionElement(container, stepId, sectionId);
	if (!sectionElement) return null;
	const sectionRect = sectionElement.getBoundingClientRect();
	return {
		sectionRect,
		visibleHeight: getVisibleHeight(containerRect, sectionRect)
	};
}
function getVisibleHeight(containerRect, sectionRect) {
	return Math.max(0, Math.min(sectionRect.bottom, containerRect.bottom) - Math.max(sectionRect.top, containerRect.top));
}
function scoreScrollableStep({ stepIndex, stepCount, containerRect, sectionRect, visibleHeight, anchorY, isNearBottom }) {
	const isVisible = visibleHeight > 0;
	const focusBandTop = containerRect.top + Math.min(containerRect.height * .2, 140);
	const focusBandBottom = containerRect.top + Math.min(containerRect.height * .55, 360);
	const focusBandOverlap = getBandOverlapHeight(sectionRect, focusBandTop, focusBandBottom);
	const distanceToFocusBand = focusBandOverlap > 0 ? 0 : Math.min(Math.abs(sectionRect.top - focusBandBottom), Math.abs(sectionRect.bottom - focusBandTop));
	const lastStepProminent = stepIndex === stepCount - 1 && visibleHeight >= Math.min(sectionRect.height, containerRect.height) * .25 && sectionRect.top <= containerRect.top + containerRect.height * .65;
	let score = isVisible ? visibleHeight : Number.NEGATIVE_INFINITY;
	if (focusBandOverlap > 0) score += 12e3 + focusBandOverlap * 25;
	if (sectionRect.top <= anchorY) score += 250;
	score += Math.max(0, 1e3 - distanceToFocusBand);
	if (isNearBottom && lastStepProminent && isVisible) score += 15e3;
	return score;
}
function getBandOverlapHeight(sectionRect, bandTop, bandBottom) {
	return Math.max(0, Math.min(sectionRect.bottom, bandBottom) - Math.max(sectionRect.top, bandTop));
}
function isScrollableNavigationKey(event) {
	if (event.metaKey || event.ctrlKey || event.altKey) return false;
	return [
		"ArrowDown",
		"ArrowUp",
		"PageDown",
		"PageUp",
		"Home",
		"End",
		" "
	].includes(event.key);
}

//#endregion
//#region src/components/ui/wizard/WizardLayout.tsx
function PagedLayout({ steps, currentStepIndex, furthestStepIndex: furthestStepIndexProp, onStepChange, onComplete, onLastStepPrimary, onCancel, nextLabel, lastStepLabel, onLastStepSecondary, lastStepSecondaryLabel, lastStepSecondaryDisabled, hideLastStepPrimary, navActions, header, variant, className }) {
	const safeIndex = getSafeStepIndex(steps.length, currentStepIndex);
	const resolvedFurthestStepIndex = useFurthestStepIndex(safeIndex, furthestStepIndexProp);
	const stepBodyScrollRef = useRef(null);
	useLayoutEffect(() => {
		const el = stepBodyScrollRef.current;
		if (!el) return;
		el.scrollTop = 0;
	}, [safeIndex]);
	if (steps.length === 0) return null;
	const isFirstStep = safeIndex === 0;
	const isLastStep = safeIndex === steps.length - 1;
	const currentStep = steps[safeIndex];
	const canProceed = currentStep?.isValid !== false;
	const handleNext = () => {
		if (isLastStep) {
			if (onLastStepPrimary) onLastStepPrimary();
			else onComplete?.();
			return;
		}
		onStepChange(safeIndex + 1);
	};
	const handlePrevious = () => {
		if (!isFirstStep) onStepChange(safeIndex - 1);
	};
	const stepDefs = toStepDefs(steps, safeIndex);
	const footer = /* @__PURE__ */ jsx("div", {
		className: "shrink-0 border-t border-border bg-background px-8 py-4",
		children: /* @__PURE__ */ jsx("div", {
			className: "mx-auto max-w-5xl",
			children: /* @__PURE__ */ jsx(WizardNavigation, {
				isFirstStep,
				isLastStep,
				canProceed,
				onPrevious: handlePrevious,
				onNext: handleNext,
				onCancel,
				extraActions: navActions,
				nextLabel,
				lastStepLabel,
				onLastStepSecondary,
				lastStepSecondaryLabel,
				lastStepSecondaryDisabled,
				showLastStepPrimary: !hideLastStepPrimary
			})
		})
	});
	if (variant === "vertical") return /* @__PURE__ */ jsxs("div", {
		className: cn("flex h-full gap-6", className),
		children: [/* @__PURE__ */ jsx("div", {
			className: "w-[220px] shrink-0 py-6 pl-6",
			children: /* @__PURE__ */ jsx(WizardStepper, {
				variant: "vertical",
				steps: stepDefs,
				currentStepIndex: safeIndex,
				furthestStepIndex: resolvedFurthestStepIndex,
				onStepClick: onStepChange,
				className: "h-full"
			})
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex min-w-0 flex-1 flex-col overflow-hidden",
			children: [/* @__PURE__ */ jsx("div", {
				ref: stepBodyScrollRef,
				className: "flex-1 overflow-y-auto p-8",
				children: /* @__PURE__ */ jsxs("div", {
					className: "mx-auto max-w-5xl",
					children: [header, currentStep?.component]
				})
			}), footer]
		})]
	});
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex h-full flex-col", className),
		children: [/* @__PURE__ */ jsx("div", {
			className: "shrink-0 p-6 pb-0",
			children: /* @__PURE__ */ jsx(WizardStepper, {
				variant: "horizontal",
				steps: stepDefs,
				currentStepIndex: safeIndex,
				furthestStepIndex: resolvedFurthestStepIndex,
				onStepClick: onStepChange
			})
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex min-w-0 flex-1 flex-col overflow-hidden",
			children: [/* @__PURE__ */ jsx("div", {
				ref: stepBodyScrollRef,
				className: "flex-1 overflow-y-auto p-8",
				children: /* @__PURE__ */ jsxs("div", {
					className: "mx-auto max-w-5xl",
					children: [header, currentStep?.component]
				})
			}), footer]
		})]
	});
}
function ScrollableLayout({ steps, currentStepIndex, onStepChange, header, onComplete, hideScrollableCompleteButton, scrollableCompleteLabel, onScrollableSecondary, scrollableSecondaryLabel, scrollableSecondaryDisabled, scrollPadding, className }) {
	const instanceId = useId();
	const scrollRef = useRef(null);
	const sectionId = useCallback((stepId) => `wizard-section-${instanceId}-${stepId}`, [instanceId]);
	const { activeIndex, furthestStepIndex, scrollToSection } = useScrollableWizardStepTracking({
		steps,
		currentStepIndex,
		onStepChange,
		scrollRef,
		sectionId,
		scrollPadding
	});
	if (steps.length === 0) return null;
	const stepDefs = toStepDefs(steps, activeIndex);
	const showScrollableSecondary = onScrollableSecondary != null && scrollableSecondaryLabel != null && scrollableSecondaryLabel !== "";
	const showScrollablePrimary = onComplete != null && !hideScrollableCompleteButton;
	const showScrollableFooter = showScrollableSecondary || showScrollablePrimary;
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex h-full gap-6", className),
		children: [/* @__PURE__ */ jsx("div", {
			className: "w-[220px] shrink-0 py-6 pl-6",
			children: /* @__PURE__ */ jsx(WizardStepper, {
				variant: "vertical",
				steps: stepDefs,
				currentStepIndex: activeIndex,
				furthestStepIndex,
				onStepClick: scrollToSection,
				freeNavigation: true,
				className: "h-full"
			})
		}), /* @__PURE__ */ jsxs("div", {
			ref: scrollRef,
			className: "flex min-w-0 flex-1 flex-col overflow-y-auto p-8",
			children: [
				header,
				/* @__PURE__ */ jsx("div", {
					className: "space-y-12",
					children: steps.map((step) => /* @__PURE__ */ jsx("section", {
						id: sectionId(step.id),
						children: step.component
					}, step.id))
				}),
				showScrollableFooter && /* @__PURE__ */ jsxs("div", {
					className: "flex justify-end gap-2 pt-8",
					children: [showScrollableSecondary && /* @__PURE__ */ jsx(Button, {
						type: "button",
						variant: "outline",
						onClick: onScrollableSecondary,
						disabled: scrollableSecondaryDisabled ?? false,
						children: scrollableSecondaryLabel
					}), showScrollablePrimary && /* @__PURE__ */ jsx(Button, {
						type: "button",
						onClick: onComplete,
						children: scrollableCompleteLabel ?? "Finish"
					})]
				})
			]
		})]
	});
}
function toStepDefs(steps, currentStepIndex) {
	return steps.map((s, i) => {
		const isInvalid = s.isInvalid ?? s.isValid === false;
		const status = s.status ?? (i < currentStepIndex && !isInvalid ? "completed" : "pending");
		return {
			id: s.id,
			title: s.title,
			status,
			isInvalid
		};
	});
}
/**
* A layout component for the wizard.
*
* @param props - The props for the WizardLayout component.
* @returns A React node representing the layout component.
*/
function WizardLayout(props) {
	const { variant = "horizontal", ...rest } = props;
	if (variant === "scrollable") return /* @__PURE__ */ jsx(ScrollableLayout, {
		steps: rest.steps,
		currentStepIndex: rest.currentStepIndex,
		onStepChange: rest.onStepChange,
		header: rest.header,
		onComplete: rest.onComplete,
		hideScrollableCompleteButton: rest.hideScrollableCompleteButton,
		scrollableCompleteLabel: rest.scrollableCompleteLabel,
		onScrollableSecondary: rest.onScrollableSecondary,
		scrollableSecondaryLabel: rest.scrollableSecondaryLabel,
		scrollableSecondaryDisabled: rest.scrollableSecondaryDisabled,
		scrollPadding: rest.scrollPadding,
		className: rest.className
	});
	return /* @__PURE__ */ jsx(PagedLayout, {
		...rest,
		variant
	});
}

//#endregion
//#region src/components/fields/address-suggestion/AddressSuggestionList.tsx
/**
* Render the suggestion dropdown. Returns `null` when there are no suggestions
* (the consuming field also gates on `hasSuggestions`, so this is a safety net).
*
* @param props - {@link AddressSuggestionListProps}.
*/
function AddressSuggestionList({ id, suggestions, highlightedIndex, onSelect, onHighlight }) {
	if (suggestions.length === 0) return null;
	return /* @__PURE__ */ jsx("div", {
		id: `${id}-suggestions`,
		className: cn("absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md", "max-h-48 overflow-auto"),
		role: "listbox",
		children: suggestions.map((s, i) => /* @__PURE__ */ jsxs("button", {
			id: `${id}-suggestion-${i}`,
			type: "button",
			role: "option",
			"aria-selected": i === highlightedIndex,
			className: cn("flex w-full flex-col px-3 py-2 text-left text-sm", "hover:bg-selected/10", i === highlightedIndex && "bg-selected/10"),
			onMouseDown: (e) => {
				e.preventDefault();
				onSelect(s);
			},
			onMouseEnter: () => onHighlight(i),
			children: [/* @__PURE__ */ jsx("span", {
				className: "font-medium",
				children: s.label
			}), /* @__PURE__ */ jsx("span", {
				className: "truncate font-mono text-xs text-muted-foreground",
				children: s.value
			})]
		}, `${s.value}-${s.description ?? i}`))
	});
}
AddressSuggestionList.displayName = "AddressSuggestionList";

//#endregion
//#region src/components/fields/address-suggestion/context.ts
/**
* @internal Shared context instance consumed by both AddressField and
* AddressSuggestionProvider. Kept in its own file so component files export
* only components (required by React Fast Refresh).
*/
const AddressSuggestionContext = createContext(null);

//#endregion
//#region src/components/fields/address-suggestion/useAddressSuggestionField.ts
/**
* Headless suggestion-dropdown behavior for address fields.
*
* Extracted from `AddressField`'s inline dropdown logic (SF-3, D9) into one
* reusable headless hook the base `AddressField` consumes — no divergence, no
* regression (INV-72). It owns:
*   - `AddressSuggestionContext` resolution (explicit `suggestions` prop overrides),
*   - the `MAX_SUGGESTIONS` (5) cap on context-resolved lists,
*   - the 200 ms suggestion debounce,
*   - keyboard highlight state with wrapping ArrowUp / ArrowDown navigation,
*   - click-outside-to-close.
*
* It is capability-free and chain-agnostic — nothing about ENS or any chain
* leaks into `@openzeppelin/ui-components`. The consuming field owns the RHF
* value write on selection and the Enter / Escape input handlers; this hook
* exposes the state and primitives they need.
*
* Distinct from the pre-existing convenience hook `useAddressSuggestions(query,
* networkId)`, which resolves a raw suggestion list from context with no
* debounce / cap / keyboard state — that hook is unchanged and still exported.
*
* @see INV-72 (suggestion-dropdown behavior preserved with zero regression; SF-5 depends)
* @see INV-94 (legacy a11y + suggestion keyboard contract preserved verbatim)
*/
/** Suggestion debounce window (ms). Matches the legacy `AddressField`. */
const DEBOUNCE_MS = 200;
/** Cap on context-resolved suggestions. Explicit arrays are rendered as-is. */
const MAX_SUGGESTIONS = 5;
/**
* Headless address-suggestion dropdown behavior. See the module docstring for
* the full behavior contract.
*
* @param args - {@link UseAddressSuggestionFieldArgs}.
* @returns {@link UseAddressSuggestionFieldResult}.
*/
function useAddressSuggestionField({ inputValue, suggestions: suggestionsProp, networkId }) {
	const contextResolver = useContext(AddressSuggestionContext);
	const containerRef = useRef(null);
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	useEffect(() => {
		if (!inputValue.trim()) {
			setDebouncedQuery("");
			return;
		}
		const timer = setTimeout(() => setDebouncedQuery(inputValue), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [inputValue]);
	const suggestionsDisabled = suggestionsProp === false;
	const resolvedSuggestions = useMemo(() => {
		if (suggestionsDisabled) return [];
		if (Array.isArray(suggestionsProp)) return suggestionsProp;
		if (!contextResolver || !debouncedQuery.trim()) return [];
		return contextResolver.resolveSuggestions(debouncedQuery, networkId).slice(0, MAX_SUGGESTIONS);
	}, [
		suggestionsDisabled,
		suggestionsProp,
		contextResolver,
		debouncedQuery,
		networkId
	]);
	const hasSuggestions = showSuggestions && resolvedSuggestions.length > 0;
	useEffect(() => {
		let active = true;
		const handleClickOutside = (e) => {
			if (active && containerRef.current && !containerRef.current.contains(e.target)) setShowSuggestions(false);
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			active = false;
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);
	const onContainerKeyDown = useCallback((e) => {
		if (!hasSuggestions) return;
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((prev) => prev < resolvedSuggestions.length - 1 ? prev + 1 : 0);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((prev) => prev > 0 ? prev - 1 : resolvedSuggestions.length - 1);
		}
	}, [hasSuggestions, resolvedSuggestions.length]);
	const onInputChange = useCallback((value) => {
		setShowSuggestions(value.length > 0);
		setHighlightedIndex(-1);
	}, []);
	return {
		containerRef,
		resolvedSuggestions,
		hasSuggestions,
		highlightedIndex,
		suggestionsDisabled,
		setHighlightedIndex,
		closeSuggestions: useCallback(() => {
			setShowSuggestions(false);
			setHighlightedIndex(-1);
		}, []),
		onInputChange,
		onContainerKeyDown
	};
}

//#endregion
//#region src/components/fields/name-resolution/useInjectedNameResolution.ts
/**
* Injected name-resolution machine (SF-3).
*
* A thin, capability-free async machine that `AddressField` consumes: given a
* typed input and the injected `resolveName`, it debounces, dispatches, tracks
* status, and applies the out-of-order name-drop guard (INV-117). It is the
* component-boundary analogue of SF-2's `useResolveName`, with all hard
* mechanics (cache, dedupe, retries, UNSUPPORTED synthesis) deliberately left
* behind the injected function — this hook does ONLY debounce + status
* derivation + the funds guards.
*
* The machine holds no resolved-hex cache of its own (INV-85): its single
* `settled` record is valid only while it matches the current debounced input,
* the current resolver function identity, and the current retry attempt — any
* mismatch derives `loading`, never a stale `resolved`.
*
* Resolver-identity-churn backstop (INV-123/124/125). The dispatch effect keys
* on the injected `resolveName` function identity, so an unstable (non-memoized)
* integrator resolver re-fires it on every render. To keep that from becoming an
* unbounded RPC loop (rate-ban + cost + funds-path DoS), the machine caps
* dispatches per resolution intent — the pair (normalized debounced input × retry
* attempt) — at {@link MAX_DISPATCHES_PER_INTENT}, regardless of render count and
* regardless of how many times `resolveName` identity changes under that intent.
* The budget resets only on intent change (a new typed name, or `retry()`) or when
* the field leaves the active name-resolution path (clear / disable). It does
* *not* reset on `settled.source` mismatch: that would let two settling sources
* flap and refill the counter (settling churn), silently defeating INV-123. A
* genuine network swap still re-dispatches within the shared intent budget
* (INV-119 / acceptance #5) because a single identity change costs one unit.
* Clear→retype of the same name gets a fresh budget via the leave-path reset, so
* it cannot accumulate toward the cap across cycles and then strand a later swap.
* A churning identity that exhausts the budget emits a one-shot, development-only
* warning (INV-124) and degrades to the safe gated state (bounded RPC, RHF value
* `''`, submit gated, never a wrong hex, never a throw — INV-125). This backstop
* is entirely internal: no signature, param, or return-type change; `AddressField`
* consumes the machine unchanged.
*/
/**
* Default debounce window (ms) for typed names — mirrors SF-2's
* `forwardDebounceMs` default so the field and the bare hook feel identical.
*/
const NAME_RESOLUTION_DEBOUNCE_MS = 300;
/**
* Cap on resolver dispatches per resolution intent — the pair (normalized
* debounced input × retry attempt) — regardless of how many times the field
* re-renders or how many times `resolveName` identity changes under that intent
* (INV-123). Chosen to sit comfortably above the dispatches a single displayed
* input legitimately incurs (1 initial call + any human-initiated genuine network
* swaps of that same name, each ≤1 unit — INV-119) and far below per-render
* identity churn (hundreds/sec): a user does not switch networks eight times
* while one unedited name sits in the field, whereas churn blows past eight
* within milliseconds. The budget resets when the intent changes or when the
* field leaves the name-resolution path (clear / disable) — never on
* `settled.source` mismatch, so settling-source oscillation cannot refill it.
*/
const MAX_DISPATCHES_PER_INTENT = 8;
/**
* Debounce + dispatch + status derivation over an injected `resolveName`.
*
* Guarantees:
* - INV-83: no call unless `enabled` (name-candidate + injected method), and
*   never for a debounced copy that lags the current normalized input.
* - INV-117: a settled result whose requested name ≠ the current debounced
*   input is discarded (last-write-wins at the component boundary).
* - INV-87: a rejecting resolver (contract violation) is mapped to a typed
*   `ADAPTER_ERROR` — nothing throws into the render tree.
* - INV-123: resolver dispatches are bounded per resolution intent (normalized
*   debounced input × retry attempt) at {@link MAX_DISPATCHES_PER_INTENT},
*   regardless of render count or resolver-identity oscillation; the budget
*   resets on intent change or when leaving the name-resolution path — never on
*   `settled.source` mismatch; `retry()` never consumes an existing budget.
* - INV-124: an identity-churning resolver that exhausts the budget triggers a
*   one-shot, development-only `logger.warn` (silent in production).
* - INV-125: under sustained churn the field stays in the safe gated state — the
*   perpetually-changing identity keeps failing the `settled.source` check, so
*   the machine derives `loading`, never `resolved`; combined with INV-87 it
*   never throws and never writes a hex.
* - No `setState` after unmount: every dispatch is cancelled on cleanup.
*
* @param params - {@link UseInjectedNameResolutionParams}
* @returns The current {@link InjectedNameResolutionResult}.
*/
function useInjectedNameResolution({ input, enabled, resolveName, debounceMs = NAME_RESOLUTION_DEBOUNCE_MS }) {
	const normalized = input.trim().toLowerCase();
	const [debounced, setDebounced] = useState(normalized);
	const seededRef = useRef(false);
	useEffect(() => {
		if (!seededRef.current) {
			seededRef.current = true;
			return;
		}
		if (debounceMs <= 0) {
			setDebounced(normalized);
			return;
		}
		const timer = setTimeout(() => setDebounced(normalized), debounceMs);
		return () => clearTimeout(timer);
	}, [normalized, debounceMs]);
	const [attempt, setAttempt] = useState(0);
	const [settled, setSettled] = useState(null);
	const currentTargetRef = useRef("");
	currentTargetRef.current = enabled && resolveName ? debounced : "";
	const intentKeyRef = useRef("");
	const dispatchCountRef = useRef(0);
	const churnWarnedRef = useRef(false);
	const warnOnceOnChurn = useCallback(() => {
		if (process.env.NODE_ENV === "production" || churnWarnedRef.current) return;
		churnWarnedRef.current = true;
		logger.warn("AddressField", "The injected name resolver changes identity on every render, so inline name resolution was bounded to prevent an unbounded RPC loop. Memoize the resolver (e.g. wrap it in useMemo, or use useRuntimeNameResolver from @openzeppelin/ui-react) so its function identity is stable across renders. See the ens-address-input integration guide.");
	}, []);
	useEffect(() => {
		if (!enabled || !resolveName || debounced === "") {
			intentKeyRef.current = "";
			dispatchCountRef.current = 0;
			return;
		}
		if (debounced !== normalized) return;
		const intentKey = `${debounced}\u0000${attempt}`;
		if (intentKeyRef.current !== intentKey) {
			intentKeyRef.current = intentKey;
			dispatchCountRef.current = 0;
		}
		if (dispatchCountRef.current >= MAX_DISPATCHES_PER_INTENT) {
			warnOnceOnChurn();
			return;
		}
		dispatchCountRef.current += 1;
		let cancelled = false;
		const requestedName = debounced;
		const requestedAttempt = attempt;
		const source = resolveName;
		const settle = (result) => {
			if (cancelled) return;
			if (requestedName !== currentTargetRef.current) return;
			setSettled({
				name: requestedName,
				result,
				source,
				attempt: requestedAttempt
			});
		};
		source(requestedName).then(settle, (cause) => {
			settle({
				ok: false,
				error: {
					code: "ADAPTER_ERROR",
					message: cause instanceof Error ? cause.message : String(cause),
					cause
				}
			});
		});
		return () => {
			cancelled = true;
		};
	}, [
		enabled,
		resolveName,
		debounced,
		normalized,
		attempt,
		warnOnceOnChurn
	]);
	const retry = useCallback(() => {
		setAttempt((current) => current + 1);
	}, []);
	if (!enabled || !resolveName || normalized === "") return { status: "idle" };
	if (debounced !== normalized) return {
		status: "debouncing",
		name: normalized
	};
	if (settled !== null && settled.name === debounced && settled.source === resolveName && settled.attempt === attempt) {
		if (settled.result.ok) return {
			status: "resolved",
			name: settled.name,
			data: settled.result.value
		};
		return {
			status: "error",
			name: settled.name,
			error: settled.result.error,
			retry
		};
	}
	return {
		status: "loading",
		name: normalized
	};
}

//#endregion
//#region src/components/fields/name-resolution/context.ts
/**
* Process-global key for the context object. Uses `Symbol.for` so all duplicated
* module instances share ONE React context — the same cross-bundle-singleton
* pattern as `@openzeppelin/ui-react`'s `NameResolutionContext` /
* `WalletStateContext` (bundler pre-bundling / npm-installed consumers).
*/
const CONTEXT_KEY = Symbol.for("@openzeppelin/ui-components/NameResolverContext");
function getOrCreateSharedContext() {
	const globalScope = globalThis;
	if (!globalScope[CONTEXT_KEY]) globalScope[CONTEXT_KEY] = createContext(null);
	return globalScope[CONTEXT_KEY];
}
/**
* @internal Shared context instance consumed by both AddressField and
* NameResolverProvider (INV-118). Kept in its own file so component files
* export only components (required by React Fast Refresh).
*
* `null` means no provider is mounted — every ENS branch in `AddressField` is
* then dead code and the field is byte-identical to its pre-ENS behavior
* (INV-82).
*/
const NameResolverContext = getOrCreateSharedContext();

//#endregion
//#region src/components/fields/name-resolution/useNameResolver.ts
/**
* Read the injected resolver context. Returns `null` when no
* `NameResolverProvider` is mounted — the consuming field must then treat
* every ENS branch as dead code (INV-82).
*
* @returns The extended context value, or `null` when no provider is mounted.
*/
function useNameResolver() {
	return useContext(NameResolverContext);
}

//#endregion
//#region src/components/fields/name-resolution/useResolvingAnnouncerCopy.ts
/** Default escalation threshold for long-latency resolves (INV-129/130). */
const RESOLVING_ANNOUNCER_ESCALATE_AFTER_MS = 3e3;
const PHASE_1_COPY = "Resolving…";
const PHASE_2_COPY = "Still resolving…";
/**
* Returns the announcer string for debouncing/loading arms.
* Phase 1: "Resolving…" (SF-3 default, unchanged for first N ms).
* Phase 2: "Still resolving…" after threshold — reduces frozen perception
* during CCIP-scale latency without naming gateway/CCIP/v2.
*/
function useResolvingAnnouncerCopy({ isPending, escalateAfterMs = RESOLVING_ANNOUNCER_ESCALATE_AFTER_MS }) {
	const [escalated, setEscalated] = useState(false);
	useEffect(() => {
		if (!isPending) {
			setEscalated(false);
			return;
		}
		setEscalated(false);
		const timeoutId = window.setTimeout(() => {
			setEscalated(true);
		}, escalateAfterMs);
		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [isPending, escalateAfterMs]);
	if (!isPending) return PHASE_1_COPY;
	return escalated ? PHASE_2_COPY : PHASE_1_COPY;
}

//#endregion
//#region src/components/fields/utils/accessibility.ts
/**
* Accessibility utilities for form field components
*/
/**
* Default aria-describedby ID generator based on field ID
*/
function getDescribedById(id, type = "error") {
	return `${id}-${type}`;
}
/**
* Generates accessibility attributes for form fields
*/
function getAccessibilityProps({ id, hasError = false, isRequired = false, isDisabled = false, isReadOnly = false, hasHelperText = false, hasCharacterCounter = false }) {
	const describedByIds = [];
	if (hasError) describedByIds.push(getDescribedById(id, "error"));
	if (hasHelperText) describedByIds.push(getDescribedById(id, "description"));
	if (hasCharacterCounter) describedByIds.push(getDescribedById(id, "counter"));
	return {
		"aria-invalid": hasError,
		"aria-required": isRequired,
		"aria-describedby": describedByIds.length > 0 ? describedByIds.join(" ") : void 0,
		"aria-errormessage": hasError ? getDescribedById(id, "error") : void 0,
		required: isRequired,
		disabled: isDisabled,
		readOnly: isReadOnly,
		tabIndex: isDisabled ? -1 : 0
	};
}
/**
* Utility function to handle keyboard events for interactive elements
* Helps ensure keyboard users can interact with form controls
*/
function handleKeyboardEvent(event, handlers) {
	switch (event.key) {
		case "Enter":
			if (handlers.onEnter) {
				event.preventDefault();
				handlers.onEnter();
			}
			break;
		case " ":
			if (handlers.onSpace) {
				event.preventDefault();
				handlers.onSpace();
			}
			break;
		case "Escape":
			if (handlers.onEscape) {
				event.preventDefault();
				handlers.onEscape();
			}
			break;
		case "ArrowUp":
			if (handlers.onArrowUp) {
				event.preventDefault();
				handlers.onArrowUp();
			}
			break;
		case "ArrowDown":
			if (handlers.onArrowDown) {
				event.preventDefault();
				handlers.onArrowDown();
			}
			break;
		case "ArrowLeft":
			if (handlers.onArrowLeft) {
				event.preventDefault();
				handlers.onArrowLeft();
			}
			break;
		case "ArrowRight":
			if (handlers.onArrowRight) {
				event.preventDefault();
				handlers.onArrowRight();
			}
			break;
		case "Tab":
			if (handlers.onTab) {
				event.preventDefault();
				handlers.onTab();
			}
			break;
		default: break;
	}
}
/**
* Field focus management utility
* For managing focus within a field group or complex form component
*/
function createFocusManager() {
	return {
		focusFirstElement(container) {
			if (!container) return;
			const focusable = container.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
			if (focusable.length > 0) focusable[0].focus();
		},
		focusElementById(id) {
			const element = document.getElementById(id);
			if (element) element.focus();
		},
		trapFocus(event, container) {
			if (!container || event.key !== "Tab") return;
			const focusable = Array.from(container.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"));
			if (focusable.length === 0) return;
			const firstElement = focusable[0];
			const lastElement = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === firstElement) {
				event.preventDefault();
				lastElement.focus();
			} else if (!event.shiftKey && document.activeElement === lastElement) {
				event.preventDefault();
				firstElement.focus();
			}
		}
	};
}
/**
* Provides a handler for Escape key to clear input fields
*
* @param onChange - Function to call when value changes
* @param value - Current value of the input
* @returns A function to handle the Escape key press
*/
function handleEscapeKey(onChange, value) {
	return (e) => {
		if (e.key === "Escape") {
			e.preventDefault();
			if (value) onChange("");
			else e.target.blur();
		}
	};
}
/**
* Provides a handler for Space/Enter keys for toggle components (checkboxes, switches)
*
* @param onChange - Function to call when value changes
* @param value - Current value of the input
* @returns A function to handle the Space/Enter key press
*/
function handleToggleKeys(onChange, value) {
	return (e) => {
		if (e.key === " " || e.key === "Enter") {
			e.preventDefault();
			onChange(!value);
		}
	};
}
/**
* Provides a handler for arrow keys for numeric inputs
*
* @param onChange - Function to call when value changes
* @param value - Current numeric value
* @param step - Step amount for increments/decrements
* @param min - Minimum allowed value (optional)
* @param max - Maximum allowed value (optional)
* @returns A function to handle arrow key presses
*/
function handleNumericKeys(onChange, value, step = 1, min, max) {
	return (e) => {
		if (e.key === "ArrowUp" || e.key === "ArrowDown") {
			e.preventDefault();
			const direction = e.key === "ArrowUp" ? 1 : -1;
			let newValue = typeof value === "number" ? value + step * direction : 0;
			if (typeof min === "number") newValue = Math.max(min, newValue);
			if (typeof max === "number") newValue = Math.min(max, newValue);
			onChange(newValue);
		}
	};
}

//#endregion
//#region src/components/fields/utils/layout.ts
/**
* Layout utility functions for form components
*/
/**
* Helper function to get width classes based on the field width
*/
function getWidthClasses(width) {
	switch (width) {
		case "half": return "form-field-half";
		case "third": return "form-field-third";
		case "full":
		default: return "form-field-full";
	}
}

//#endregion
//#region src/components/fields/AddressField.tsx
/**
* Non-user-facing gate string returned by the sync validator while a typed name
* is resolving, so `formState.isValid` is `false` even for an optional field
* (INV-84). The visible message comes from the aria-live region, never this string.
*/
const NAME_RESOLUTION_PENDING_GATE = "__ens_name_resolution_pending__";
/** Codes for which a `retry()` affordance is offered — transient only (INV-90). */
const TRANSIENT_ERROR_CODES = new Set([
	"RESOLUTION_TIMEOUT",
	"EXTERNAL_GATEWAY_ERROR",
	"ADAPTER_ERROR"
]);
/**
* Normalize a typed name for the resolved-write match guard: trim then lowercase,
* mirroring the SF-2 engine (whose echoed `result.name` is already normalized).
* Comparing the raw typed value would spuriously fail on case/whitespace (INV-79).
*/
function normalizeResolvedName(value) {
	return value.trim().toLowerCase();
}
/**
* Address input field component specifically designed for blockchain addresses via React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like AddressField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles blockchain address-specific rendering and validation using the passed addressing capability
*
* The component includes:
* - Integration with React Hook Form
* - Blockchain address validation through the provided addressing capability
* - Automatic error handling and reporting
* - Chain-agnostic design (validation handled by capabilities)
* - Full accessibility support with ARIA attributes
* - Keyboard navigation
*
* Autocomplete suggestions can be provided in two ways:
*
* 1. **Context-based (zero-config)**: Mount an `AddressSuggestionProvider` in the
*    component tree. Every `AddressField` below it automatically resolves suggestions.
*
* 2. **Prop-based (explicit)**: Pass `suggestions` directly. This overrides context.
*    Pass `suggestions={false}` to opt out when a provider is mounted.
*
* The suggestion dropdown includes built-in debouncing, keyboard navigation (Arrow keys,
* Enter, Escape), click-outside dismissal, and ARIA listbox semantics — all provided by
* the shared headless `useAddressSuggestionField` hook + `AddressSuggestionList`.
*
* **Inline name resolution (SF-3, opt-in via context).** When a
* `NameResolverProvider` is mounted, the field also accepts a name (e.g.
* `alice.eth`): the name is resolved inline through the injected `resolveName`
* and the RHF form value becomes the resolved hex — never the name, never a
* silently-coerced value. The correctness spine is a shadow-state model:
* the `<input>` always shows the typed string (`inputValue`, INV-69); the RHF
* value is `''` for every unresolved name state so submit stays gated with no
* async validator (INV-75); the single name→hex write fires only on
* `resolved` + normalized name-match (INV-79/80). With **no** provider mounted
* every resolution branch below is dead code and the field is byte-identical
* to its pre-ENS behavior (INV-82 — the LOCKED backward-compat guarantee).
* The component stays capability-free: it never reads a runtime or wallet
* state; everything arrives through the injected context (INV-118).
*/
function AddressField({ id, label, placeholder, helperText, control, name, width = "full", validation, addressing, readOnly, suggestions: suggestionsProp, onSuggestionSelect, onResolvedNameChange, showCrossNetworkFallbackDisclaimer = true, showForwardResolutionSuccessAnnouncer = true, announcerEndSlot }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const resolutionRegionId = `${id}-resolution`;
	const resolver = useNameResolver();
	const resolveName = resolver?.resolveName;
	const isValidName = resolver?.isValidName;
	const activeNetworkId = resolver?.activeNetworkId;
	const activeNetworkName = resolver?.activeNetworkName;
	const resolveNetworkLabel = resolver?.resolveNetworkLabel;
	const lastSetValueRef = useRef("");
	const [inputValue, setInputValue] = useState("");
	const watchedFieldValue = useWatch({
		control,
		name
	});
	useEffect(() => {
		const currentFieldValue = watchedFieldValue ?? "";
		if (currentFieldValue !== lastSetValueRef.current) {
			lastSetValueRef.current = currentFieldValue;
			setInputValue(currentFieldValue);
		}
	}, [watchedFieldValue]);
	const isValidAddress = useMemo(() => addressing ? (v) => addressing.isValidAddress(v) : void 0, [addressing]);
	const classification = useMemo(() => classifyAddressInput(inputValue, {
		isValidAddress,
		isValidName
	}), [
		inputValue,
		isValidAddress,
		isValidName
	]);
	const resolverActive = resolver !== null && classification === "name-candidate";
	const nameEnabled = resolverActive && resolveName != null;
	const forwardUnsupported = resolverActive && resolveName == null;
	const resolution = useInjectedNameResolution({
		input: inputValue,
		enabled: nameEnabled,
		resolveName
	});
	const chainScopeBlocked = resolution.status === "resolved" && isChainScopeMismatch(resolution.data.provenance, activeNetworkId);
	const resolvingAnnouncerCopy = useResolvingAnnouncerCopy({ isPending: !forwardUnsupported && (resolution.status === "debouncing" || resolution.status === "loading") });
	const resolverPresentRef = useRef(false);
	const classificationRef = useRef("empty");
	const statusRef = useRef("idle");
	const resolvedNameRef = useRef(void 0);
	const chainScopeBlockedRef = useRef(false);
	const validationRef = useRef(validation);
	const addressingRef = useRef(addressing);
	resolverPresentRef.current = resolver !== null;
	classificationRef.current = classification;
	statusRef.current = resolution.status;
	resolvedNameRef.current = resolution.status === "resolved" ? resolution.name : void 0;
	chainScopeBlockedRef.current = chainScopeBlocked;
	validationRef.current = validation;
	addressingRef.current = addressing;
	const { field, fieldState } = useController({
		control,
		name,
		rules: { validate: useCallback((value) => {
			if (resolverPresentRef.current && classificationRef.current === "name-candidate" && (statusRef.current !== "resolved" || chainScopeBlockedRef.current)) return NAME_RESOLUTION_PENDING_GATE;
			if (value === void 0 || value === null || value === "") return validationRef.current?.required ? "This field is required" : true;
			const standardValidationResult = validateField(value, validationRef.current);
			if (standardValidationResult !== true) return standardValidationResult;
			if (addressingRef.current && typeof value === "string") {
				if (!addressingRef.current.isValidAddress(value)) return "Invalid address format for the selected chain";
			}
			return true;
		}, []) },
		disabled: readOnly
	});
	const fieldOnChange = field.onChange;
	const gateFormState = useFormState({
		control,
		disabled: resolver === null
	});
	const observedIsValid = resolver !== null && gateFormState.isValid;
	const pendingNameGate = resolver !== null && classification === "name-candidate" && (resolution.status !== "resolved" || chainScopeBlocked);
	useEffect(() => {
		if (pendingNameGate && observedIsValid) control.setError(name, {
			type: "validate",
			message: NAME_RESOLUTION_PENDING_GATE
		});
	}, [
		pendingNameGate,
		observedIsValid,
		control,
		name
	]);
	const resolvedAddress = resolution.status === "resolved" ? resolution.data.address : void 0;
	const resolvedName = resolution.status === "resolved" ? resolution.name : void 0;
	const isResolvedNameMatch = resolvedName !== void 0 && normalizeResolvedName(inputValue) === resolvedName;
	useEffect(() => {
		if (isResolvedNameMatch && resolvedAddress !== void 0 && !chainScopeBlocked) {
			lastSetValueRef.current = resolvedAddress;
			fieldOnChange(resolvedAddress);
		}
	}, [
		isResolvedNameMatch,
		resolvedAddress,
		chainScopeBlocked,
		fieldOnChange
	]);
	const matchedName = isResolvedNameMatch ? resolvedName : void 0;
	useEffect(() => {
		onResolvedNameChange?.(matchedName);
	}, [matchedName, onResolvedNameChange]);
	const { containerRef, resolvedSuggestions, hasSuggestions, highlightedIndex, suggestionsDisabled, setHighlightedIndex, closeSuggestions, onInputChange, onContainerKeyDown } = useAddressSuggestionField({
		inputValue,
		suggestions: suggestionsProp
	});
	/**
	* The single site for the typed/raw write (the name→hex write lives in the
	* effect above). With no resolver this is the legacy write, verbatim (INV-82);
	* with a resolver, only the `name-candidate` classification diverges:
	*  - hex / malformed / empty → the raw value (legacy sync validate applies)
	*  - name-candidate → `''` — gates submit and synchronously invalidates any
	*    prior resolved hex before re-resolution (INV-75 / INV-81)
	*/
	const commitTypedValue = useCallback((value) => {
		setInputValue(value);
		if (!resolverPresentRef.current) {
			lastSetValueRef.current = value;
			fieldOnChange(value);
			return;
		}
		const c = classifyAddressInput(value, {
			isValidAddress,
			isValidName
		});
		classificationRef.current = c;
		if (c === "name-candidate") {
			const normalized = normalizeResolvedName(value);
			if (statusRef.current === "resolved" && resolvedNameRef.current === normalized && !chainScopeBlockedRef.current) return;
			statusRef.current = "debouncing";
			lastSetValueRef.current = "";
			fieldOnChange("");
		} else {
			lastSetValueRef.current = value;
			fieldOnChange(value);
		}
	}, [
		fieldOnChange,
		isValidAddress,
		isValidName
	]);
	const handleInputChange = (e) => {
		commitTypedValue(e.target.value);
		onInputChange(e.target.value);
	};
	const applySuggestion = (suggestion) => {
		commitTypedValue(suggestion.value);
		onSuggestionSelect?.(suggestion);
		closeSuggestions();
	};
	const handleKeyDown = (e) => {
		if (hasSuggestions && e.key === "Enter" && highlightedIndex >= 0) {
			e.preventDefault();
			applySuggestion(resolvedSuggestions[highlightedIndex]);
			return;
		}
		if (e.key === "Escape") {
			if (hasSuggestions) {
				closeSuggestions();
				return;
			}
			handleEscapeKey(commitTypedValue, inputValue)(e);
		}
	};
	const hasError = !!fieldState.error;
	const isPendingGate = fieldState.error?.message === NAME_RESOLUTION_PENDING_GATE;
	const hasRealError = hasError && !isPendingGate;
	const shouldShowError = hasRealError && fieldState.isTouched;
	const validationClasses = getValidationStateClasses(hasRealError ? fieldState.error : void 0, isPendingGate ? false : fieldState.isTouched);
	const patternErrorMessage = validation?.pattern && typeof validation.pattern === "object" && "message" in validation.pattern ? validation.pattern.message : void 0;
	const accessibilityProps = getAccessibilityProps({
		id,
		hasError: hasRealError,
		isRequired,
		hasHelperText: !!helperText
	});
	const renderOutcome = () => {
		if (forwardUnsupported) return /* @__PURE__ */ jsx("span", {
			role: "alert",
			className: "text-destructive text-sm",
			children: nameResolutionMessageForCode("UNSUPPORTED_NETWORK")
		});
		switch (resolution.status) {
			case "idle": return null;
			case "debouncing":
			case "loading": return /* @__PURE__ */ jsx("span", {
				className: "text-muted-foreground text-sm",
				children: resolvingAnnouncerCopy
			});
			case "resolved":
				if (chainScopeBlocked) return /* @__PURE__ */ jsx("span", {
					role: "alert",
					className: "text-destructive text-sm",
					children: nameResolutionChainScopeMismatchMessage({ activeNetworkName })
				});
				const fallbackNetworks = showCrossNetworkFallbackDisclaimer && isCrossNetworkFallback(resolution.data.provenance) ? getFallbackNetworks(resolution.data.provenance) : void 0;
				const fallbackMessage = fallbackNetworks && nameResolutionCrossNetworkFallbackMessage(fallbackNetworks, crossNetworkFallbackMessageNames(fallbackNetworks, resolveNetworkLabel));
				if (!showForwardResolutionSuccessAnnouncer) return null;
				return /* @__PURE__ */ jsxs("span", {
					className: "text-sm",
					children: [/* @__PURE__ */ jsxs("span", { children: ["Resolved to ", /* @__PURE__ */ jsx("code", {
						className: "font-mono",
						children: resolution.data.address
					})] }), fallbackMessage ? /* @__PURE__ */ jsx("span", {
						className: "mt-1 block text-muted-foreground text-xs",
						role: "note",
						children: fallbackMessage
					}) : null]
				});
			case "error": {
				const networkName = resolution.error.code === "UNSUPPORTED_NETWORK" ? resolution.error.networkId || void 0 : void 0;
				const message = nameResolutionMessageForCode(resolution.error.code, { networkName });
				const isTransient = TRANSIENT_ERROR_CODES.has(resolution.error.code);
				const retry = resolution.retry;
				return /* @__PURE__ */ jsxs("span", {
					role: "alert",
					className: "text-destructive text-sm",
					children: [message, isTransient ? /* @__PURE__ */ jsx("button", {
						type: "button",
						className: "ml-2 underline",
						onClick: retry,
						children: "Retry"
					}) : null]
				});
			}
		}
	};
	const hasAnnouncerEndSlot = announcerEndSlot != null;
	const showResolutionAnnouncer = resolver !== null || hasAnnouncerEndSlot;
	const includeResolutionInDescribedBy = resolver !== null || hasAnnouncerEndSlot;
	const resolutionAnnouncerRow = showResolutionAnnouncer ? hasAnnouncerEndSlot ? /* @__PURE__ */ jsxs("div", {
		className: "-mt-2 flex items-start justify-between gap-2 pr-2.5",
		children: [/* @__PURE__ */ jsx("div", {
			id: resolutionRegionId,
			"aria-live": "polite",
			className: "mt-4 min-w-0 flex-1 text-sm",
			children: resolver !== null ? renderOutcome() : null
		}), /* @__PURE__ */ jsx("div", {
			className: "-mt-px shrink-0",
			children: announcerEndSlot
		})]
	}) : /* @__PURE__ */ jsx("div", {
		id: resolutionRegionId,
		"aria-live": "polite",
		className: "min-h-5",
		children: renderOutcome()
	}) : null;
	const inputBlock = /* @__PURE__ */ jsxs("div", {
		ref: containerRef,
		className: hasAnnouncerEndSlot ? "relative z-10" : "relative",
		onKeyDown: onContainerKeyDown,
		children: [/* @__PURE__ */ jsx(Input, {
			...field,
			id,
			placeholder: placeholder || (resolver !== null ? "0x... or name" : "0x..."),
			className: validationClasses,
			onChange: handleInputChange,
			onKeyDown: handleKeyDown,
			"data-slot": "input",
			value: inputValue,
			...accessibilityProps,
			"aria-describedby": !includeResolutionInDescribedBy ? `${helperText ? descriptionId : ""} ${hasRealError ? errorId : ""}` : [
				helperText ? descriptionId : "",
				hasRealError ? errorId : "",
				resolutionRegionId
			].filter(Boolean).join(" ") || void 0,
			"aria-expanded": hasSuggestions,
			"aria-autocomplete": suggestionsDisabled ? void 0 : "list",
			"aria-controls": hasSuggestions ? `${id}-suggestions` : void 0,
			"aria-activedescendant": hasSuggestions && highlightedIndex >= 0 ? `${id}-suggestion-${highlightedIndex}` : void 0,
			disabled: readOnly
		}), hasSuggestions && /* @__PURE__ */ jsx(AddressSuggestionList, {
			id,
			suggestions: resolvedSuggestions,
			highlightedIndex,
			onSelect: applySuggestion,
			onHighlight: setHighlightedIndex
		})]
	});
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [
			label && /* @__PURE__ */ jsxs(Label, {
				htmlFor: id,
				children: [
					label,
					" ",
					isRequired && /* @__PURE__ */ jsx("span", {
						className: "text-destructive",
						children: "*"
					})
				]
			}),
			hasAnnouncerEndSlot ? /* @__PURE__ */ jsxs(Fragment, { children: [inputBlock, resolutionAnnouncerRow] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
				inputBlock,
				helperText && /* @__PURE__ */ jsx("div", {
					id: descriptionId,
					className: "text-muted-foreground text-sm",
					children: helperText
				}),
				/* @__PURE__ */ jsx(ErrorMessage, {
					error: hasRealError ? fieldState.error : void 0,
					id: errorId,
					message: shouldShowError ? fieldState.error?.message || patternErrorMessage : void 0
				}),
				resolutionAnnouncerRow
			] }),
			hasAnnouncerEndSlot ? /* @__PURE__ */ jsxs(Fragment, { children: [helperText && /* @__PURE__ */ jsx("div", {
				id: descriptionId,
				className: "text-muted-foreground text-sm",
				children: helperText
			}), /* @__PURE__ */ jsx(ErrorMessage, {
				error: hasRealError ? fieldState.error : void 0,
				id: errorId,
				message: shouldShowError ? fieldState.error?.message || patternErrorMessage : void 0
			})] }) : null
		]
	});
}
AddressField.displayName = "AddressField";

//#endregion
//#region src/components/fields/ResolvedAddressFieldPreview.tsx
/**
* Rich ENS preview shown below an `AddressField` once the value is a valid address.
* Complements the field's mechanism-neutral "Resolved to 0x…" announcer with
* reverse-resolved name + avatar via `AddressDisplay`.
*
* For async reverse resolution, wrap in `AddressNameProvider` (or
* `AddressNameResolutionProvider` from `@openzeppelin/ui-renderer`) and omit
* `resolvedName`. For sync / app-owned resolution, pass `resolvedName` directly.
*/
function ResolvedAddressFieldPreview({ address, networkId, addressing, resolvedName, label = "Resolved account", displayProps }) {
	const trimmed = address?.trim() ?? "";
	if (!(trimmed !== "" && addressing?.isValidAddress(trimmed) === true)) return null;
	return /* @__PURE__ */ jsxs("div", {
		className: "rounded-md border border-border/60 bg-muted/30 px-3 py-2",
		"aria-label": "Resolved address preview",
		children: [/* @__PURE__ */ jsx("p", {
			className: "mb-1 text-xs text-muted-foreground",
			children: label
		}), /* @__PURE__ */ jsx(AddressDisplay, {
			address: trimmed,
			networkId,
			variant: "chip",
			showCopyButton: true,
			resolvedName,
			...displayProps
		})]
	});
}

//#endregion
//#region src/components/fields/AddressFieldWithResolvedPreview.tsx
/**
* `AddressField` plus a rich ENS preview card. Suppresses the redundant forward
* "Resolved to 0x…" announcer — the preview card below replaces it.
*
* Parent must `watch` the field and pass the value as `previewAddress`.
*/
function AddressFieldWithResolvedPreview({ previewAddress, previewNetworkId, addressing, className, preview, showCrossNetworkFallbackDisclaimer, showForwardResolutionSuccessAnnouncer, ...addressFieldProps }) {
	return /* @__PURE__ */ jsxs("div", {
		className: cn("space-y-1", "[&_[id$=\"-resolution\"]:empty]:min-h-0", className),
		children: [/* @__PURE__ */ jsx(AddressField, {
			...addressFieldProps,
			addressing,
			showCrossNetworkFallbackDisclaimer: showCrossNetworkFallbackDisclaimer ?? false,
			showForwardResolutionSuccessAnnouncer: showForwardResolutionSuccessAnnouncer ?? false
		}), preview ?? /* @__PURE__ */ jsx(ResolvedAddressFieldPreview, {
			address: previewAddress,
			networkId: previewNetworkId,
			addressing
		})]
	});
}

//#endregion
//#region src/components/fields/TextAreaField.tsx
/**
* Multi-line text input field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles textarea-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Resizable multi-line text input
* - Character limit support
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation with Escape to clear
*/
function TextAreaField({ id, label, placeholder, helperText, control, name, width = "full", validation, rows = 4, maxLength, validateTextArea, readOnly, helperEndSlot }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (validateTextArea && value) {
					const validation$1 = validateTextArea(value);
					if (validation$1 !== true && typeof validation$1 === "string") return validation$1;
				}
				if (maxLength && typeof value === "string" && value.length > maxLength) return `Maximum length is ${maxLength} characters`;
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				const hasHelperEndSlot = helperEndSlot != null;
				const textareaElement = /* @__PURE__ */ jsx(Textarea, {
					...field,
					id,
					placeholder,
					rows,
					maxLength,
					className: hasHelperEndSlot ? `relative z-10 ${validationClasses}` : validationClasses,
					value: field.value ?? "",
					disabled: readOnly,
					...accessibilityProps,
					"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
					onKeyDown: handleEscapeKey((value) => {
						if (typeof field.onChange === "function") field.onChange(value);
					}, field.value)
				});
				const helperTextContent = helperText != null && helperText !== "" ? helperText : null;
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					textareaElement,
					maxLength && typeof field.value === "string" && /* @__PURE__ */ jsxs("div", {
						className: "text-muted-foreground text-right text-xs",
						children: [
							field.value.length,
							"/",
							maxLength
						]
					}),
					hasHelperEndSlot ? /* @__PURE__ */ jsxs("div", {
						className: "-mt-2 flex items-start justify-between gap-2 pr-2.5",
						children: [/* @__PURE__ */ jsx("div", {
							id: descriptionId,
							className: "mt-4 min-w-0 flex-1 text-sm text-muted-foreground",
							children: helperTextContent
						}), /* @__PURE__ */ jsx("div", {
							className: "-mt-px shrink-0",
							children: helperEndSlot
						})]
					}) : helperTextContent && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperTextContent
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
TextAreaField.displayName = "TextAreaField";

//#endregion
//#region src/components/fields/AddressListField/labels.ts
/** Default English labels for {@link AddressListField}. */
const DEFAULT_ADDRESS_LIST_FIELD_LABELS = {
	addSingleAddress: "Add",
	invalidSingleAddress: "Enter a valid address",
	enableBulkEntry: "Bulk paste",
	enableSingleEntry: "Single entry",
	addAddresses: "Add addresses",
	addAddressCount: (count) => `Add ${count} address${count === 1 ? "" : "es"}`,
	invalidPrefix: "Invalid:",
	invalidMore: (extraCount) => `(+${extraCount} more)`,
	maxItemsReached: (maxItems) => `Maximum of ${maxItems} addresses reached.`,
	addressesAdded: (count) => `${count} address${count === 1 ? "" : "es"} added`,
	removeAddress: (index) => `Remove address ${index + 1}`,
	previewReadyToAdd: (count) => `${count} ready to add`,
	previewInvalid: (count) => `${count} invalid`,
	previewAlreadyListed: (count) => `${count} already listed`,
	previewDuplicateInPaste: (count) => `${count} duplicate in paste`,
	bulkNothingAdded: "No addresses were added. Check formatting and try again.",
	bulkAdded: (count) => `Added ${count} address${count === 1 ? "" : "es"}`,
	bulkInvalidFormats: (count) => `${count} invalid format${count === 1 ? "" : "s"}`,
	bulkAlreadyListed: (count) => `${count} already listed`,
	bulkDuplicatesInPaste: (count) => `${count} duplicate${count === 1 ? "" : "s"} in paste`
};
/** Merges {@link DEFAULT_ADDRESS_LIST_FIELD_LABELS} with optional overrides. */
function resolveAddressListFieldLabels(overrides) {
	return {
		...DEFAULT_ADDRESS_LIST_FIELD_LABELS,
		...overrides
	};
}
/** Builds the inline helper preview shown while the user types in the draft textarea. */
function buildAddressListPreviewSummary(classification, labels) {
	if (!classification) return null;
	const skipped = classification.invalid.length + classification.alreadyListed.length + classification.duplicatesInInput.length;
	if (classification.accepted.length === 0 && skipped === 0) return null;
	const parts = [];
	if (classification.accepted.length > 0) parts.push(labels.previewReadyToAdd(classification.accepted.length));
	if (classification.invalid.length > 0) parts.push(labels.previewInvalid(classification.invalid.length));
	if (classification.alreadyListed.length > 0) parts.push(labels.previewAlreadyListed(classification.alreadyListed.length));
	if (classification.duplicatesInInput.length > 0) parts.push(labels.previewDuplicateInPaste(classification.duplicatesInInput.length));
	return parts.join(" · ");
}
/** Formats post-add feedback after the user commits parsed addresses to the list. */
function formatAddressBulkSummary(classification, addedCount, labels) {
	if (addedCount === 0 && classification.accepted.length === 0) {
		if (classification.invalid.length + classification.alreadyListed.length + classification.duplicatesInInput.length === 0) return null;
		return labels.bulkNothingAdded;
	}
	const parts = [];
	if (addedCount > 0) parts.push(labels.bulkAdded(addedCount));
	if (classification.invalid.length > 0) parts.push(labels.bulkInvalidFormats(classification.invalid.length));
	if (classification.alreadyListed.length > 0) parts.push(labels.bulkAlreadyListed(classification.alreadyListed.length));
	if (classification.duplicatesInInput.length > 0) parts.push(labels.bulkDuplicatesInPaste(classification.duplicatesInInput.length));
	if (parts.length === 0) return null;
	return parts.join(". ") + ".";
}

//#endregion
//#region src/components/fields/AddressListField/AddressListBulkEntry.tsx
/** Bulk paste entry — info callout, textarea, and add action in a compact stack. */
function AddressListBulkEntry({ fieldId, value, onAdd, placeholder, formatHint, addressing, maxItems, disabled = false, maxInvalidPreview = 3, labels, modeToggle }) {
	const atLimit = maxItems != null && value.length >= maxItems;
	const inputDisabled = disabled || atLimit;
	const [feedback, setFeedback] = useState(null);
	const { control, reset, watch } = useForm({
		defaultValues: { input: "" },
		mode: "onChange"
	});
	const rawInput = watch("input");
	const classification = useMemo(() => {
		if (!rawInput.trim()) return null;
		return classifyAddressCandidates(parseDelimitedAddressInput(rawInput), value, addressing, maxItems);
	}, [
		rawInput,
		value,
		addressing,
		maxItems
	]);
	const previewSummary = useMemo(() => buildAddressListPreviewSummary(classification, labels), [classification, labels]);
	const statusMessage = rawInput.trim() ? previewSummary ?? void 0 : feedback ?? void 0;
	const handleAdd = useCallback(() => {
		if (!classification || classification.accepted.length === 0 || atLimit) return;
		onAdd(classification.accepted);
		reset({ input: "" });
		setFeedback(formatAddressBulkSummary(classification, classification.accepted.length, labels));
	}, [
		classification,
		atLimit,
		onAdd,
		reset,
		labels
	]);
	const acceptedCount = classification?.accepted.length ?? 0;
	const addButtonLabel = acceptedCount > 0 ? labels.addAddressCount(acceptedCount) : labels.addAddresses;
	return /* @__PURE__ */ jsxs("div", {
		className: "space-y-3",
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "flex items-start gap-2 rounded-lg border border-border/70 bg-muted/30 px-3 py-2.5",
				children: [/* @__PURE__ */ jsx(Info, { className: "mt-0.5 size-4 shrink-0 text-muted-foreground" }), /* @__PURE__ */ jsx("p", {
					className: "text-xs leading-relaxed text-muted-foreground",
					children: formatHint
				})]
			}),
			/* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(TextAreaField, {
				id: `address-list-bulk-${fieldId}`,
				name: "input",
				label: "",
				placeholder,
				helperText: statusMessage,
				control,
				rows: 4,
				validation: { required: false },
				readOnly: inputDisabled,
				helperEndSlot: modeToggle
			}) }),
			/* @__PURE__ */ jsxs("div", {
				className: "flex flex-wrap items-center justify-between gap-2",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "min-w-0 flex-1",
					children: [classification && classification.invalid.length > 0 ? /* @__PURE__ */ jsxs("p", {
						className: "text-xs text-destructive",
						children: [
							labels.invalidPrefix,
							" ",
							classification.invalid.slice(0, maxInvalidPreview).join(", "),
							classification.invalid.length > maxInvalidPreview ? ` ${labels.invalidMore(classification.invalid.length - maxInvalidPreview)}` : ""
						]
					}) : null, atLimit && maxItems != null ? /* @__PURE__ */ jsx("p", {
						className: "text-xs text-muted-foreground",
						children: labels.maxItemsReached(maxItems)
					}) : null]
				}), /* @__PURE__ */ jsxs(Button, {
					type: "button",
					size: "sm",
					className: "shrink-0",
					disabled: inputDisabled || acceptedCount === 0,
					onClick: () => handleAdd(),
					children: [/* @__PURE__ */ jsx(Plus, { className: "mr-1 size-4" }), addButtonLabel]
				})]
			})
		]
	});
}

//#endregion
//#region src/components/fields/AddressListField/AddressListEntries.tsx
/** Renders committed addresses with remove actions. Shared by single and bulk entry modes. */
function AddressListEntries({ value, getExplorerUrl, disabled = false, labels, onRemove }) {
	if (value.length === 0) return null;
	return /* @__PURE__ */ jsxs("div", {
		className: "space-y-1",
		children: [/* @__PURE__ */ jsx("p", {
			className: "text-xs text-muted-foreground",
			children: labels.addressesAdded(value.length)
		}), /* @__PURE__ */ jsx("div", {
			className: "max-h-44 space-y-1 overflow-y-auto rounded-md border border-border/70 p-1",
			children: value.map((address, index) => /* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between rounded bg-muted p-2",
				children: [/* @__PURE__ */ jsx(AddressDisplay, {
					address,
					variant: "inline",
					truncateWhenLabeled: true,
					showCopyButton: true,
					explorerUrl: getExplorerUrl?.(address) ?? void 0
				}), /* @__PURE__ */ jsx(Button, {
					type: "button",
					variant: "ghost",
					size: "sm",
					className: "size-7 shrink-0 p-0 text-muted-foreground hover:text-destructive",
					onClick: () => onRemove(index),
					disabled,
					"aria-label": labels.removeAddress(index),
					children: /* @__PURE__ */ jsx(X, { className: "size-3.5" })
				})]
			}, `${address}-${index}`))
		})]
	});
}

//#endregion
//#region src/components/fields/AddressListField/AddressListSingleEntry.tsx
/** Single-address entry — inline {@link AddressField} with an Add button (address-book aware). */
function AddressListSingleEntry({ fieldId, value, onAdd, placeholder, addressing, maxItems, disabled = false, labels, modeToggle }) {
	const atLimit = maxItems != null && value.length >= maxItems;
	const inputDisabled = disabled || atLimit;
	const { control, reset, getValues, handleSubmit } = useForm({
		defaultValues: { draft: "" },
		mode: "onChange"
	});
	const trimmedDraft = (useWatch({
		control,
		name: "draft"
	}) ?? "").trim();
	const validationState = useMemo(() => {
		if (!trimmedDraft) return {
			canAdd: false,
			showInvalid: false
		};
		if (value.includes(trimmedDraft)) return {
			canAdd: false,
			showInvalid: false
		};
		if (atLimit) return {
			canAdd: false,
			showInvalid: false
		};
		if (addressing && !addressing.isValidAddress(trimmedDraft)) return {
			canAdd: false,
			showInvalid: true
		};
		return {
			canAdd: true,
			showInvalid: false
		};
	}, [
		trimmedDraft,
		value,
		atLimit,
		addressing
	]);
	const submitAdd = useCallback(() => {
		if (!validationState.canAdd) return;
		onAdd(getValues("draft").trim());
		reset({ draft: "" });
	}, [
		validationState.canAdd,
		onAdd,
		getValues,
		reset
	]);
	return /* @__PURE__ */ jsxs("div", {
		className: "space-y-1",
		children: [/* @__PURE__ */ jsxs("div", {
			className: "flex items-start gap-2",
			children: [/* @__PURE__ */ jsx("div", {
				className: "min-w-0 flex-1",
				children: /* @__PURE__ */ jsx(AddressField, {
					id: `address-list-single-${fieldId}`,
					name: "draft",
					label: "",
					placeholder,
					helperText: validationState.showInvalid ? labels.invalidSingleAddress : void 0,
					control,
					addressing,
					validation: { required: false },
					readOnly: inputDisabled,
					announcerEndSlot: modeToggle
				})
			}), /* @__PURE__ */ jsxs(Button, {
				type: "button",
				className: "h-10 shrink-0",
				disabled: inputDisabled || !validationState.canAdd,
				onClick: handleSubmit(submitAdd),
				children: [/* @__PURE__ */ jsx(Plus, { className: "mr-1 size-4" }), labels.addSingleAddress]
			})]
		}), atLimit && maxItems != null && /* @__PURE__ */ jsx("p", {
			className: "text-xs text-muted-foreground",
			children: labels.maxItemsReached(maxItems)
		})]
	});
}

//#endregion
//#region src/components/fields/AddressListField/AddressListField.tsx
/**
* Multi-address list field with single-entry default and optional bulk paste.
*/
function AddressListField({ value, onChange, placeholder, bulkPlaceholder, formatHint, addressing, getExplorerUrl, label, helperText, maxItems, disabled = false, maxInvalidPreview = 3, className, defaultEntryMode = "single", allowModeToggle = true, labels: labelOverrides }) {
	const fieldId = useId();
	const labels = useMemo(() => resolveAddressListFieldLabels(labelOverrides), [labelOverrides]);
	const [entryMode, setEntryMode] = useState(defaultEntryMode);
	const resolvedBulkPlaceholder = bulkPlaceholder ?? placeholder;
	const handleRemove = useCallback((index) => {
		onChange(value.filter((_, currentIndex) => currentIndex !== index));
	}, [onChange, value]);
	const handleAddSingle = useCallback((address) => {
		onChange([...value, address]);
	}, [onChange, value]);
	const handleAddBulk = useCallback((addresses) => {
		onChange([...value, ...addresses]);
	}, [onChange, value]);
	const switchToBulk = useCallback(() => setEntryMode("bulk"), []);
	const switchToSingle = useCallback(() => setEntryMode("single"), []);
	const toggleClassName = "inline-flex items-center gap-1 rounded-b-md rounded-t-none border border-t-0 border-input bg-muted/40 px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";
	const modeToggle = !allowModeToggle ? void 0 : entryMode === "single" ? /* @__PURE__ */ jsxs("button", {
		type: "button",
		onClick: switchToBulk,
		className: toggleClassName,
		children: [/* @__PURE__ */ jsx(ClipboardPaste, { className: "size-3" }), labels.enableBulkEntry]
	}) : /* @__PURE__ */ jsxs("button", {
		type: "button",
		onClick: switchToSingle,
		className: toggleClassName,
		children: [/* @__PURE__ */ jsx(PencilLine, { className: "size-3" }), labels.enableSingleEntry]
	});
	return /* @__PURE__ */ jsxs("div", {
		className: cn("space-y-3", className),
		children: [
			label ? /* @__PURE__ */ jsx(Label, {
				className: "text-sm font-medium leading-none",
				children: label
			}) : null,
			helperText && entryMode === "single" ? /* @__PURE__ */ jsx("p", {
				className: "text-sm leading-relaxed text-muted-foreground",
				children: helperText
			}) : null,
			entryMode === "single" ? /* @__PURE__ */ jsx(AddressListSingleEntry, {
				fieldId,
				value,
				onAdd: handleAddSingle,
				placeholder,
				addressing,
				maxItems,
				disabled,
				labels,
				modeToggle
			}) : /* @__PURE__ */ jsx(AddressListBulkEntry, {
				fieldId,
				value,
				onAdd: handleAddBulk,
				placeholder: resolvedBulkPlaceholder,
				formatHint,
				addressing,
				maxItems,
				disabled,
				maxInvalidPreview,
				labels,
				modeToggle
			}),
			/* @__PURE__ */ jsx(AddressListEntries, {
				value,
				getExplorerUrl,
				disabled,
				labels,
				onRemove: handleRemove
			})
		]
	});
}

//#endregion
//#region src/components/fields/address-suggestion/address-suggestion-context.tsx
/**
* Address Suggestion Context
*
* Provides a React context for resolving address autocomplete suggestions.
* When an `AddressSuggestionProvider` is mounted, every `AddressField`
* in the subtree automatically renders a suggestion dropdown as the user types.
*
* @example
* ```tsx
* import { AddressSuggestionProvider } from '@openzeppelin/ui-components';
* import { useAliasSuggestionResolver } from '@openzeppelin/ui-storage';
*
* function App() {
*   const resolver = useAliasSuggestionResolver(db);
*   return (
*     <AddressSuggestionProvider {...resolver}>
*       <MyApp />
*     </AddressSuggestionProvider>
*   );
* }
* ```
*/
/**
* Provides address suggestion resolution to all `AddressField` instances in the
* subtree. Wrap your application (or a subsection) with this provider and
* supply a `resolveSuggestions` function.
*
* @param props - Resolver function and children
*
* @example
* ```tsx
* <AddressSuggestionProvider
*   resolveSuggestions={(query, networkId) => filterAliases(query, networkId)}
* >
*   <App />
* </AddressSuggestionProvider>
* ```
*/
function AddressSuggestionProvider({ children, resolveSuggestions }) {
	const value = React$1.useMemo(() => ({ resolveSuggestions }), [resolveSuggestions]);
	return /* @__PURE__ */ jsx(AddressSuggestionContext.Provider, {
		value,
		children
	});
}

//#endregion
//#region src/components/fields/address-suggestion/useAddressSuggestions.ts
/**
* Convenience hook that resolves suggestions for a query string using the
* nearest `AddressSuggestionProvider`. Returns an empty array when no provider
* is mounted or when the query is empty.
*
* @param query - Current input value to match against
* @param networkId - Optional network identifier for scoping results
* @returns Object containing the resolved suggestions array
*
* @example
* ```tsx
* function MyField({ query }: { query: string }) {
*   const { suggestions } = useAddressSuggestions(query, 'ethereum-mainnet');
*   return (
*     <ul>
*       {suggestions.map(s => <li key={s.value}>{s.label}</li>)}
*     </ul>
*   );
* }
* ```
*/
/** Resolves address suggestions from the nearest `AddressSuggestionProvider`. */
function useAddressSuggestions(query, networkId) {
	const resolver = React$1.useContext(AddressSuggestionContext);
	return { suggestions: React$1.useMemo(() => {
		if (!resolver || !query.trim()) return [];
		return resolver.resolveSuggestions(query, networkId);
	}, [
		resolver,
		query,
		networkId
	]) };
}

//#endregion
//#region src/components/fields/name-resolution/name-resolver-context.tsx
/**
* Name Resolver Context (SF-3)
*
* Provides a React context for forward name resolution (name → address).
* When a `NameResolverProvider` is mounted, every `AddressField` in the
* subtree resolves typed names inline through the injected `resolveName`
* and submits the resolved hex — never the name (SC-004).
*
* The provider is deliberately **dumb** (INV-118): it memoizes the injected
* functions into the context value and nothing else — no hook state, no
* capability, no chain dependency. The smart runtime wiring lives upstream in
* `@openzeppelin/ui-react` (`useRuntimeNameResolver`), which an app or the
* renderer mounts ambiently:
*
* @example
* ```tsx
* import { NameResolverProvider } from '@openzeppelin/ui-components';
* import { useRuntimeNameResolver } from '@openzeppelin/ui-react';
*
* function FormRoot() {
*   const resolver = useRuntimeNameResolver();
*   return (
*     <NameResolverProvider {...resolver}>
*       <MyForm />
*     </NameResolverProvider>
*   );
* }
* ```
*/
/**
* Provides forward name resolution to all `AddressField` instances in the
* subtree (zero call-site wiring, SC-001). Both functions are optional: an
* absent `resolveName` means forward resolution is unsupported and a typed
* name surfaces `UNSUPPORTED_NETWORK` (INV-119 / SC-006).
*
* ## Stable resolver identity (integrator contract)
*
* The injected resolver **must be referentially stable across renders** —
* memoize it (e.g. `useMemo`) or use `useRuntimeNameResolver` from
* `@openzeppelin/ui-react`, which is stable by construction. A resolver whose
* function identity changes on every render (e.g. a fresh inline function) is a
* misconfiguration: the field keys inline resolution on that identity, so a
* churning identity would otherwise re-dispatch on every render — an unbounded
* RPC loop on a funds path.
*
* The base component **detects and withstands** this (it is not merely a caveat):
* dispatch is bounded per resolution intent so a churning resolver can never drive
* an unbounded loop (INV-123), a one-shot development-only warning names the
* misconfiguration (INV-124), and the field degrades to a **safe gated state**
* (empty value, submit blocked — never a wrong address, never a throw; INV-125)
* until the resolver is memoized. A **genuine** resolver/network swap — a single
* identity change for a given input — is still honored and re-resolves within
* budget (INV-119). The bound is a backstop; the contract is a stable identity.
*
* @param props - Injected resolver functions and children
*/
function NameResolverProvider({ children, isValidName, resolveName, activeNetworkId, activeNetworkName, resolveNetworkLabel }) {
	const value = React$1.useMemo(() => ({
		isValidName,
		resolveName,
		activeNetworkId,
		activeNetworkName,
		resolveNetworkLabel
	}), [
		isValidName,
		resolveName,
		activeNetworkId,
		activeNetworkName,
		resolveNetworkLabel
	]);
	return /* @__PURE__ */ jsx(NameResolverContext.Provider, {
		value,
		children
	});
}

//#endregion
//#region src/components/fields/AmountField.tsx
/**
* Amount input field component specifically designed for currency/token amounts with React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles amount-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Support for currency/token symbols
* - Numeric-specific validations (min, max, decimals)
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation with arrow keys for numeric increment/decrement
*/
function AmountField({ id, label, placeholder, helperText, control, name, width = "full", validation, min, max, step = .01, decimals = 2, symbol = "", symbolPosition = "suffix", validateAmount }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const formatCurrency = (value) => {
		if (value === "" || value === void 0 || value === null) return "";
		const numValue = typeof value === "string" ? parseFloat(value) : value;
		if (isNaN(numValue)) return "";
		return numValue.toFixed(decimals);
	};
	const parseAmount = (value) => {
		const parsed = parseFloat(value);
		return isNaN(parsed) ? 0 : parsed;
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				const numValue = typeof value === "string" ? parseFloat(value) : value;
				if (value === void 0 || value === null || value === "" || isNaN(numValue)) return validation?.required ? "This field is required" : true;
				if (min !== void 0 && numValue < min) return `Value must be at least ${min}`;
				if (max !== void 0 && numValue > max) return `Value must be at most ${max}`;
				if (validateAmount && !isNaN(numValue)) {
					const validation$1 = validateAmount(numValue);
					if (validation$1 !== true && typeof validation$1 === "string") return validation$1;
				}
				return true;
			} },
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error?.message;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				const displayValue = field.value !== void 0 && field.value !== null ? field.value : "";
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs("div", {
						className: "relative",
						children: [
							symbol && symbolPosition === "prefix" && /* @__PURE__ */ jsx("span", {
								className: "absolute top-1/2 left-3 -translate-y-1/2 text-gray-500",
								children: symbol
							}),
							/* @__PURE__ */ jsx(Input, {
								...field,
								id,
								type: "text",
								inputMode: "decimal",
								placeholder,
								value: displayValue,
								className: `${validationClasses} ${symbol && symbolPosition === "prefix" ? "pl-8" : ""} ${symbol && symbolPosition === "suffix" ? "pr-8" : ""}`,
								"data-slot": "input",
								...accessibilityProps,
								"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
								onChange: (e) => {
									const value = e.target.value;
									if (value === "" || /^-?\d*\.?\d*$/.test(value)) field.onChange(value);
								},
								onBlur: (e) => {
									if (e.target.value) {
										const parsed = parseFloat(e.target.value);
										if (!isNaN(parsed)) field.onChange(formatCurrency(parsed));
									}
									if (field.onBlur) field.onBlur();
								},
								onKeyDown: (e) => {
									if (e.key === "Escape") {
										handleEscapeKey((value) => {
											field.onChange(value === "" ? null : value);
										}, field.value)(e);
										return;
									}
									if (e.key === "ArrowUp" || e.key === "ArrowDown") handleNumericKeys((newValue) => {
										field.onChange(formatCurrency(newValue));
									}, parseAmount(field.value || ""), step, min, max)(e);
								}
							}),
							symbol && symbolPosition === "suffix" && /* @__PURE__ */ jsx("span", {
								className: "absolute top-1/2 right-3 -translate-y-1/2 text-gray-500",
								children: symbol
							})
						]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
AmountField.displayName = "AmountField";

//#endregion
//#region src/components/fields/ArrayField.tsx
/**
* Array input field component specifically designed for React Hook Form integration.
*
* This component provides a dynamic interface for managing array inputs with:
* - Add/remove functionality for array items
* - Validation for array length constraints
* - Integration with existing field components for array elements
* - Full accessibility support
*
* The component reuses existing field components (TextField, NumberField, etc.)
* for individual array elements, maintaining consistency across the form system.
*
* Why the extra guards and synchronization exist (post-mortem/rationale):
* - In certain runtime states (StrictMode double-invocation, preview remounts, state rehydration
*   from storage), React Hook Form's useFieldArray may not synchronously reflect an `append()`
*   or `remove()` call in the immediately subsequent read (e.g., reading `fields.length` or
*   `getValues(name)` right after the call). This resulted in newly added items (especially
*   falsy defaults like 0/false) appearing briefly and then disappearing, or remove operations
*   not updating the UI.
* - To make array operations deterministic and resilient across flows (preview form with
*   FormProvider and the wizard's "Use Hardcoded Value" without a provider), we:
*   1) Read a stable snapshot of the array value BEFORE `append()` and, if the immediate
*      post-append read doesn't reflect the addition, we coerce the state to
*      "previousSnapshot + newValue" via `setValue` (when context exists) or `replace`.
*   2) Avoid `remove()` race conditions by always computing the new array via filter and
*      applying it with `replace`, which proved to be the most consistent across contexts.
*   3) Keep `fields` in sync with the watched array value using a guarded `replace` in an effect.
*      A ref flag (`isReplacingRef`) prevents infinite loops when we ourselves are driving
*      the `replace`.
*   4) Use optional chaining for form context (`formContext?.control`, `formContext?.getValues`,
*      `formContext?.setValue`) so the component works with either an inherited FormProvider or
*      an explicit `control` prop.
*   5) Use `queueMicrotask` to reset the guard flag after our `replace` has been scheduled,
*      avoiding an extra timer tick while ensuring the effect observes the final state.
*
* Guarantees:
* - Operations are idempotent: the fallback only runs when the immediate read does not reflect
*   the intended state, so we don't double-append/remove.
* - Works both with and without FormProvider (preview vs wizard) due to context-agnostic state
*   reads and `replace` fallback.
*/
function ArrayField({ id, label, helperText, control, name, width = "full", validation, elementType = "text", minItems = 0, maxItems, elementFieldConfig, renderElement, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const formContext = useFormContext();
	const { fields, append, replace } = useFieldArray({
		control: control || formContext?.control,
		name
	});
	const rawWatchedArray = useWatch({
		control: control || formContext?.control,
		name
	});
	const watchedArrayValue = React.useMemo(() => {
		return Array.isArray(rawWatchedArray) ? rawWatchedArray : [];
	}, [rawWatchedArray]);
	const isReplacingRef = React.useRef(false);
	React.useEffect(() => {
		if (isReplacingRef.current) return;
		if (Array.isArray(watchedArrayValue) && fields.length !== watchedArrayValue.length) {
			isReplacingRef.current = true;
			replace(watchedArrayValue);
			queueMicrotask(() => {
				isReplacingRef.current = false;
			});
		}
	}, [
		fields.length,
		watchedArrayValue,
		replace
	]);
	const handleRemoveItem = (index) => {
		const updatedArray = (Array.isArray(watchedArrayValue) ? watchedArrayValue : []).filter((_, i) => i !== index);
		isReplacingRef.current = true;
		replace(updatedArray);
		queueMicrotask(() => {
			isReplacingRef.current = false;
		});
	};
	const handleAddItem = () => {
		const defaultValue = elementFieldConfig?.defaultValue !== void 0 ? elementFieldConfig.defaultValue : elementType === "number" || elementType === "amount" ? 0 : elementType === "checkbox" ? false : "";
		isReplacingRef.current = true;
		const fieldsBeforeAppend = fields.length;
		const valuesBeforeAppend = Array.isArray(watchedArrayValue) ? watchedArrayValue : [];
		append(defaultValue);
		if (((formContext?.getValues?.(name))?.length ?? fields.length) <= fieldsBeforeAppend) {
			const coercedArray = [...Array.isArray(valuesBeforeAppend) ? valuesBeforeAppend : [], defaultValue];
			if (formContext?.setValue) formContext.setValue(name, coercedArray, {
				shouldDirty: true,
				shouldTouch: true
			});
			else replace(coercedArray);
		}
		queueMicrotask(() => {
			isReplacingRef.current = false;
		});
	};
	const canAddMore = !maxItems || fields.length < maxItems;
	const canRemove = fields.length > minItems;
	return /* @__PURE__ */ jsx(Controller, {
		control: control || formContext?.control,
		name,
		rules: { validate: (value) => {
			if (validation?.required && (!Array.isArray(value) || value.length === 0)) return "At least one item is required";
			if (minItems && Array.isArray(value) && value.length < minItems) return `Minimum ${minItems} item${minItems > 1 ? "s" : ""} required`;
			if (maxItems && Array.isArray(value) && value.length > maxItems) return `Maximum ${maxItems} item${maxItems > 1 ? "s" : ""} allowed`;
			return true;
		} },
		render: ({ fieldState: { error } }) => {
			const hasError = !!error;
			const accessibilityProps = getAccessibilityProps({
				id,
				hasError,
				isRequired,
				hasHelperText: !!helperText
			});
			return /* @__PURE__ */ jsxs("div", {
				className: `flex flex-col gap-4 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
				...accessibilityProps,
				"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
				children: [
					label && /* @__PURE__ */ jsx("div", {
						className: "flex items-center justify-between",
						children: /* @__PURE__ */ jsxs(Label, {
							htmlFor: id,
							children: [
								label,
								" ",
								isRequired && /* @__PURE__ */ jsx("span", {
									className: "text-destructive",
									children: "*"
								})
							]
						})
					}),
					/* @__PURE__ */ jsx("div", {
						className: "space-y-3",
						children: fields.length === 0 ? /* @__PURE__ */ jsx("div", {
							className: "text-muted-foreground text-sm py-4 text-center border border-dashed rounded-md",
							children: "No items added yet. Click “Add Item” to begin."
						}) : fields.map((field, index) => {
							const elementField = {
								...elementFieldConfig,
								id: field.id,
								name: `${name}.${index}`,
								label: elementFieldConfig?.label || "",
								type: elementFieldConfig?.type || elementType,
								validation: elementFieldConfig?.validation || {},
								placeholder: elementFieldConfig?.placeholder,
								helperText: elementFieldConfig?.helperText,
								width: "full",
								readOnly: elementFieldConfig?.readOnly ?? readOnly
							};
							return /* @__PURE__ */ jsxs("div", {
								className: "flex gap-2 items-center",
								children: [
									/* @__PURE__ */ jsx("div", {
										className: "flex items-center",
										children: /* @__PURE__ */ jsx(GripVertical, { className: "size-4 text-muted-foreground" })
									}),
									/* @__PURE__ */ jsx("div", {
										className: "flex-1",
										children: renderElement ? renderElement(elementField, index) : /* @__PURE__ */ jsxs("div", {
											className: "text-sm text-muted-foreground",
											children: [
												"Field type “",
												elementType,
												"” requires a render function"
											]
										})
									}),
									!readOnly && canRemove && /* @__PURE__ */ jsx(Button, {
										type: "button",
										variant: "ghost",
										size: "sm",
										onClick: () => handleRemoveItem(index),
										className: "size-8 p-0",
										"aria-label": `Remove item ${index + 1}`,
										children: /* @__PURE__ */ jsx(X, { className: "size-4" })
									})
								]
							}, field.id);
						})
					}),
					!readOnly && canAddMore && /* @__PURE__ */ jsxs(Button, {
						type: "button",
						variant: "outline",
						size: "sm",
						onClick: handleAddItem,
						className: "gap-1 w-full sm:w-auto",
						children: [/* @__PURE__ */ jsx(Plus, { className: "size-3" }), "Add Item"]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				]
			});
		}
	});
}
ArrayField.displayName = "ArrayField";

//#endregion
//#region src/components/fields/ArrayObjectField.tsx
/**
* Array of objects input field component specifically designed for React Hook Form integration.
*
* This component provides a dynamic interface for managing arrays of composite objects with:
* - Add/remove functionality for array items
* - Structured rendering of object properties within each array item
* - Collapsible items for better UX with complex objects
* - Validation for array constraints and object properties (though individual property validation is largely via rendered component)
* - Full accessibility support
*
* The component combines the functionality of ArrayField and ObjectField to handle
* complex nested data structures commonly found in blockchain contracts.
*/
function ArrayObjectField({ id, label, helperText, control, name, width = "full", validation, components = [], minItems = 0, maxItems, renderProperty, collapsible = true, defaultCollapsed = false, readOnly, typeMapping, contractSchema }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const formContext = useFormContext();
	const effectiveControl = control || formContext.control;
	const { fields, append, remove } = useFieldArray({
		control: effectiveControl,
		name
	});
	const [collapsedItems, setCollapsedItems] = React.useState(() => {
		const initial = {};
		fields.forEach((field) => {
			initial[field.id] = defaultCollapsed;
		});
		return initial;
	});
	const handleAddItem = () => {
		const defaultObject = {};
		components.forEach((component) => {
			if (component.type.includes("bool")) defaultObject[component.name] = false;
			else if (component.type.includes("int") || component.type.includes("uint")) defaultObject[component.name] = 0;
			else if (component.type.includes("[]")) defaultObject[component.name] = [];
			else if (component.type.startsWith("tuple")) defaultObject[component.name] = {};
			else defaultObject[component.name] = "";
		});
		append(defaultObject);
	};
	const toggleCollapsed = (fieldId) => {
		setCollapsedItems((prev) => ({
			...prev,
			[fieldId]: !prev[fieldId]
		}));
	};
	const canAddMore = !maxItems || fields.length < maxItems;
	const canRemove = fields.length > minItems;
	return /* @__PURE__ */ jsx(Controller, {
		control: effectiveControl,
		name,
		rules: { validate: (value) => {
			if (validation?.required && (!value || !Array.isArray(value) || value.length === 0)) return "At least one item is required";
			if (minItems && Array.isArray(value) && value.length < minItems) return `Minimum ${minItems} item${minItems > 1 ? "s" : ""} required`;
			if (maxItems && Array.isArray(value) && value.length > maxItems) return `Maximum ${maxItems} item${maxItems > 1 ? "s" : ""} allowed`;
			return true;
		} },
		render: ({ fieldState: { error } }) => {
			const hasError = !!error;
			const accessibilityProps = getAccessibilityProps({
				id,
				hasError,
				isRequired,
				hasHelperText: !!helperText
			});
			return /* @__PURE__ */ jsxs("div", {
				className: `flex flex-col gap-4 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
				...accessibilityProps,
				"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
				children: [
					label && /* @__PURE__ */ jsx("div", {
						className: "flex items-center justify-between",
						children: /* @__PURE__ */ jsxs(Label, {
							htmlFor: id,
							children: [
								label,
								" ",
								isRequired && /* @__PURE__ */ jsx("span", {
									className: "text-destructive",
									children: "*"
								})
							]
						})
					}),
					/* @__PURE__ */ jsx("div", {
						className: "space-y-3",
						children: fields.length === 0 ? /* @__PURE__ */ jsx("div", {
							className: "text-muted-foreground text-sm py-4 text-center border border-dashed rounded-md",
							children: "No items added yet. Click “Add Item” to begin."
						}) : fields.map((field, index) => {
							const isCollapsed = collapsedItems[field.id] ?? defaultCollapsed;
							return /* @__PURE__ */ jsxs(Card, {
								className: "p-4",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "flex items-start justify-between",
									children: [/* @__PURE__ */ jsxs("div", {
										className: "flex items-center gap-2 flex-1",
										children: [
											/* @__PURE__ */ jsx(GripVertical, { className: "size-4 text-muted-foreground" }),
											collapsible && /* @__PURE__ */ jsx(Button, {
												type: "button",
												variant: "ghost",
												size: "sm",
												onClick: () => toggleCollapsed(field.id),
												className: "p-0 h-auto",
												"aria-label": isCollapsed ? "Expand item" : "Collapse item",
												children: isCollapsed ? /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4" })
											}),
											isCollapsed && /* @__PURE__ */ jsx("div", {
												className: "flex items-center gap-4 text-sm text-muted-foreground ml-2",
												children: /* @__PURE__ */ jsx(CollapsedSummary, {
													name,
													index,
													components,
													control: effectiveControl
												})
											})
										]
									}), !readOnly && canRemove && /* @__PURE__ */ jsx(Button, {
										type: "button",
										variant: "ghost",
										size: "sm",
										onClick: () => remove(index),
										"aria-label": `Remove item ${index + 1}`,
										children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
									})]
								}), !isCollapsed && /* @__PURE__ */ jsx("div", {
									className: "space-y-4 mt-4",
									children: components.map((component) => {
										if (!typeMapping) throw new Error(`ArrayObjectField: No typeMapping capability provided for field generation. Cannot generate field for "${component.name}"`);
										const generatedField = typeMapping.generateDefaultField(component, contractSchema);
										const propertyField = {
											...generatedField,
											id: `${id}-${index}-${component.name}`,
											name: `${name}.${index}.${component.name}`,
											label: component.displayName || component.name,
											validation: {
												...generatedField.validation,
												required: validation?.required
											},
											placeholder: `Enter ${component.displayName || component.name}`,
											helperText: component.description || (generatedField.type === "number" ? "Numbers only" : void 0),
											width: "full",
											readOnly,
											...component.components && { components: component.components }
										};
										return /* @__PURE__ */ jsx("div", { children: renderProperty ? renderProperty(propertyField, index, component.name) : /* @__PURE__ */ jsxs("div", {
											className: "text-sm text-muted-foreground",
											children: [
												"Property “",
												component.name,
												"” requires a render function"
											]
										}) }, component.name);
									})
								})]
							}, field.id);
						})
					}),
					!readOnly && canAddMore && /* @__PURE__ */ jsxs(Button, {
						type: "button",
						variant: "outline",
						size: "sm",
						onClick: handleAddItem,
						className: "gap-1 w-full sm:w-auto",
						children: [/* @__PURE__ */ jsx(Plus, { className: "h-3 w-3" }), "Add Item"]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				]
			});
		}
	});
}
/**
* Component to display collapsed item summary
* TODO: This component is quite specific to ArrayObjectField.
* If similar summaries are needed elsewhere, consider making it more generic
* or moving it to a shared utils/components location.
*/
function CollapsedSummary({ name, index, components, control }) {
	const itemValues = useWatch({
		control,
		name: `${name}.${index}`
	});
	const summaryItems = components.slice(0, 3).map((component) => {
		const fieldValue = itemValues?.[component.name];
		const displayValue = fieldValue !== void 0 && fieldValue !== "" && fieldValue !== 0 && fieldValue !== false ? String(fieldValue).length > 20 ? `${String(fieldValue).substring(0, 20)}...` : String(fieldValue) : null;
		return displayValue ? /* @__PURE__ */ jsxs("span", {
			className: "flex items-center gap-1",
			children: [/* @__PURE__ */ jsxs("span", {
				className: "font-medium",
				children: [component.displayName || component.name, ":"]
			}), /* @__PURE__ */ jsx("span", {
				className: "text-foreground",
				children: displayValue
			})]
		}, component.name) : null;
	}).filter(Boolean);
	if ((!itemValues || Object.values(itemValues).every((value) => value === void 0 || value === "" || value === 0 || value === false)) && summaryItems.length === 0) return /* @__PURE__ */ jsx("span", {
		className: "italic",
		children: "Empty item"
	});
	return /* @__PURE__ */ jsxs(Fragment, { children: [summaryItems.length > 0 ? summaryItems : /* @__PURE__ */ jsx("span", {
		className: "italic",
		children: "Item has values (not shown in summary)"
	}), components.length > 3 && summaryItems.length < components.length && /* @__PURE__ */ jsxs("span", {
		className: "text-xs",
		children: [
			"+",
			components.length - summaryItems.length,
			" more"
		]
	})] });
}
ArrayObjectField.displayName = "ArrayObjectField";

//#endregion
//#region src/components/fields/BaseField.tsx
/**
* BaseField component to provide a consistent layout for form fields
*
* @important This component is the foundation of the app rendering architecture and should
* ONLY be used by field-specific components within the system, not as a standalone component.
*
* Architecture role:
* 1. Provides a consistent layout structure for all field types
* 2. Handles integration with React Hook Form through the FormField component
* 3. Manages common rendering aspects like labels, error messages, and help text
* 4. Delegates actual input rendering to field-specific components via renderInput
*
* Field component hierarchy:
* - TransactionForm (top-level renderer)
*   - DynamicFormField (field type selector)
*     - Specific field components (TextField, NumberField, etc.)
*       - BaseField (common field structure - this component)
*         - Actual input element rendered by renderInput
*
* This component should only be extended by field-specific components like
* TextField, NumberField, and AddressField - it is not meant for direct use in forms.
*/
function BaseField({ id, label, helperText, control, name, width = "full", renderInput }) {
	return /* @__PURE__ */ jsx(FormField, {
		control,
		name,
		render: ({ field }) => /* @__PURE__ */ jsxs(FormItem, {
			className: getWidthClasses(width),
			children: [
				/* @__PURE__ */ jsx(FormLabel, {
					htmlFor: id,
					children: label
				}),
				/* @__PURE__ */ jsx(FormControl, { children: renderInput(field, { id }) }),
				helperText && /* @__PURE__ */ jsx(FormDescription, {
					id: `${id}-description`,
					children: helperText
				}),
				/* @__PURE__ */ jsx(FormMessage, { id: `${id}-error` })
			]
		})
	});
}
BaseField.displayName = "BaseField";

//#endregion
//#region src/components/fields/BigIntField.tsx
/**
* Big integer input field component for large numbers beyond JavaScript's Number precision.
*
* This component is designed for blockchain integer types like uint128, uint256, int128, int256
* that can hold values larger than JavaScript's Number.MAX_SAFE_INTEGER (2^53 - 1).
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects BigIntField for large integer types
* 4. This component handles string-based integer input with validation
* 5. Adapters convert the string to BigInt when submitting transactions
*
* The component includes:
* - Integer-only validation (no decimals allowed)
* - String-based storage to avoid JavaScript Number precision issues
* - Integration with React Hook Form
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation
*/
function BigIntField({ id, label, placeholder, helperText, control, name, width = "full", validation, validateBigInt, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const enhancedValidation = {
		...validation,
		pattern: validation?.pattern || INTEGER_PATTERN
	};
	const validateBigIntValue = (value) => {
		const standardValidation = validateField(value, enhancedValidation);
		if (standardValidation !== true) return standardValidation;
		if (validateBigInt) {
			const customValidation = validateBigInt(value);
			if (customValidation !== true && typeof customValidation === "string") return customValidation;
		}
		return true;
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (value === "-") return "Please enter a complete number";
				if (typeof value === "string") return validateBigIntValue(value);
				if (typeof value === "number") return validateBigIntValue(value.toString());
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleInputChange = (e) => {
					const rawValue = e.target.value;
					if (rawValue === "") {
						field.onChange("");
						return;
					}
					if (rawValue === "-") {
						field.onChange(rawValue);
						return;
					}
					if (INTEGER_INPUT_PATTERN.test(rawValue)) field.onChange(rawValue);
				};
				const handleKeyDown = (e) => {
					const allowedKeys = [
						"0",
						"1",
						"2",
						"3",
						"4",
						"5",
						"6",
						"7",
						"8",
						"9",
						"Backspace",
						"Delete",
						"Tab",
						"Escape",
						"Enter",
						"ArrowLeft",
						"ArrowRight",
						"ArrowUp",
						"ArrowDown",
						"-"
					];
					if ((e.ctrlKey || e.metaKey) && [
						"a",
						"c",
						"v",
						"x",
						"z"
					].includes(e.key.toLowerCase())) return;
					if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault();
					if (e.key === "Escape") {
						handleEscapeKey(field.onChange, field.value)(e);
						return;
					}
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Input, {
						...field,
						value: field.value ?? "",
						id,
						placeholder: placeholder || "Enter integer value",
						type: "text",
						className: validationClasses,
						onKeyDown: handleKeyDown,
						"data-slot": "input",
						onChange: handleInputChange,
						inputMode: "numeric",
						pattern: INTEGER_HTML_PATTERN,
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
						disabled: readOnly
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
BigIntField.displayName = "BigIntField";

//#endregion
//#region src/components/fields/BooleanField.tsx
/**
* Boolean input field component (checkbox) specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like BooleanField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles boolean-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Checkbox-specific behavior
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation with Space/Enter for toggling
*/
function BooleanField({ id, label, helperText, control, name, width = "full", validation, validateBoolean, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const validateBooleanValue = (value) => {
		const standardValidation = validateField(value, validation);
		if (standardValidation !== true) return standardValidation;
		if (validateBoolean) {
			const customValidation = validateBoolean(value);
			if (customValidation !== true && typeof customValidation === "string") return customValidation;
		}
		return true;
	};
	return /* @__PURE__ */ jsx("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			defaultValue: false,
			rules: { validate: (value) => {
				if (value === void 0 || value === null) return validation?.required ? "This field is required" : true;
				if (typeof value === "boolean") return validateBooleanValue(value);
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleCheckedChange = (checked) => {
					const value = checked === true;
					field.onChange(value);
					if (isRequired && !value) setTimeout(() => field.onBlur(), 0);
				};
				const handleKeyDown = (e) => {
					handleToggleKeys(field.onChange, !!field.value)(e);
					if (e.key === " " || e.key === "Enter") {
						if (isRequired) setTimeout(() => field.onBlur(), 0);
					}
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					isDisabled: readOnly,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs("div", {
						className: "flex items-center space-x-2",
						children: [/* @__PURE__ */ jsx(Checkbox, {
							...field,
							id,
							checked: !!field.value,
							onCheckedChange: handleCheckedChange,
							className: validationClasses,
							onKeyDown: handleKeyDown,
							disabled: readOnly,
							...accessibilityProps,
							"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`
						}), /* @__PURE__ */ jsxs(Label, {
							htmlFor: id,
							children: [
								label,
								" ",
								isRequired && /* @__PURE__ */ jsx("span", {
									className: "text-destructive",
									children: "*"
								})
							]
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground pl-6 text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						className: "pl-6"
					})
				] });
			}
		})
	});
}
BooleanField.displayName = "BooleanField";

//#endregion
//#region src/components/fields/BytesField.tsx
/**
* Specialized input field for bytes data with built-in hex/base64 validation.
*
* This component provides proper validation for blockchain bytes data including:
* - Hex encoding validation (with optional 0x prefix support)
* - Base64 encoding validation
* - Byte length validation
* - Format detection and conversion
*
* Key props for EVM compatibility:
* - `allowHexPrefix`: Whether to accept 0x prefixed input (defaults to true)
* - `autoPrefix`: Whether to automatically add 0x prefixes (defaults to false)
*
* These are separate concerns - you can accept 0x input without auto-adding prefixes.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects BytesField for 'bytes' field types
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles bytes-specific validation and formatting
*/
function BytesField({ id, label, helperText, control, name, width = "full", validation, placeholder = "Enter hex or base64 encoded bytes", rows = 3, maxBytes, exactBytes, acceptedFormats = "both", autoPrefix = false, allowHexPrefix = true, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	/**
	* Validates bytes input format and encoding using validator.js
	*/
	const validateBytesField = (value) => {
		return validateBytesSimple(value, {
			acceptedFormats,
			maxBytes,
			exactBytes,
			allowHexPrefix
		});
	};
	/**
	* Formats the input value (adds 0x prefix if needed)
	*/
	const formatValue = (value) => {
		if (!value || !autoPrefix) return value;
		const cleanValue = value.trim().replace(/\s+/g, "");
		const withoutPrefix = cleanValue.startsWith("0x") ? cleanValue.slice(2) : cleanValue;
		if (withoutPrefix && /^[0-9a-fA-F]*$/.test(withoutPrefix) && withoutPrefix.length % 2 === 0) return cleanValue.startsWith("0x") ? cleanValue : `0x${cleanValue}`;
		return cleanValue;
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			disabled: readOnly,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				return validateBytesField(value);
			} },
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && (isTouched || validation?.required);
				const validationClasses = getValidationStateClasses(error, isTouched);
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					isDisabled: readOnly,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Textarea, {
						id,
						name: field.name,
						ref: field.ref,
						placeholder,
						rows,
						className: validationClasses,
						disabled: readOnly,
						value: field.value ?? "",
						onChange: (e) => {
							field.onChange(e.target.value);
						},
						onBlur: (e) => {
							const formatted = formatValue(e.target.value);
							field.onChange(formatted);
							field.onBlur();
						},
						onKeyDown: handleEscapeKey(field.onChange, field.value),
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`.trim()
					}),
					helperText && /* @__PURE__ */ jsxs("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: [helperText, /* @__PURE__ */ jsxs("div", {
							className: "text-xs text-muted-foreground mt-1",
							children: [
								acceptedFormats === "hex" && "Hex format (e.g., 48656c6c6f or 0x48656c6c6f)",
								acceptedFormats === "base64" && "Base64 format (e.g., SGVsbG8=)",
								acceptedFormats === "both" && "Hex (e.g., 48656c6c6f) or Base64 (e.g., SGVsbG8=) format",
								exactBytes && ` • Exactly ${exactBytes} bytes (${exactBytes * 2} hex chars)`,
								!exactBytes && maxBytes && ` • Max ${maxBytes} bytes`
							]
						})]
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
BytesField.displayName = "BytesField";

//#endregion
//#region src/components/fields/DateTimeField.tsx
/**
* Datetime-local input field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like DateTimeField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles date-time specific rendering, conversion, and validation
*
* The component includes:
* - Integration with React Hook Form
* - Conversion between input value (YYYY-MM-DDTHH:mm) and ISO 8601 strings (toISOString)
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation with Escape-to-clear behavior
*/
function DateTimeField({ id, label, placeholder, helperText, control, name, width = "full", validation, readOnly, clearOnEscape = true }) {
	/**
	* TODO: Replace native datetime-local input with a styled popover-based
	* calendar/time picker (Radix Popover + Select) to match the design system.
	* Maintain ISO 8601 value semantics and provide a mobile fallback to native input.
	*/
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const validateDateValue = (value) => {
		const validationResult = validateField(value, validation);
		return validationResult === true ? true : validationResult;
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (typeof value === "string") return validateDateValue(value);
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleChange = (e) => {
					const raw = e.target.value;
					if (!raw) {
						field.onChange("");
						return;
					}
					const [datePart, timePart] = raw.split("T");
					const [year, month, day] = datePart.split("-").map((s) => Number(s));
					const [hours, minutes] = timePart.split(":").map((s) => Number(s));
					const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
					const pad = (n) => String(n).padStart(2, "0");
					const serialized = `${localDate.getFullYear()}-${pad(localDate.getMonth() + 1)}-${pad(localDate.getDate())}T${pad(localDate.getHours())}:${pad(localDate.getMinutes())}`;
					field.onChange(serialized);
				};
				const handleKeyDown = (e) => {
					if (e.key === "Escape") if (clearOnEscape) handleEscapeKey(field.onChange, field.value)(e);
					else e.target.blur();
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText,
					isDisabled: !!readOnly
				});
				const formatDateTimeLocal = (val) => {
					if (!val) return "";
					try {
						const d = new Date(val);
						if (isNaN(d.getTime())) return "";
						const pad = (n) => String(n).padStart(2, "0");
						return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
					} catch {
						return "";
					}
				};
				const inputValue = formatDateTimeLocal(field.value);
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Input, {
						...field,
						id,
						placeholder,
						className: validationClasses,
						onChange: handleChange,
						onKeyDown: handleKeyDown,
						"data-slot": "input",
						value: inputValue,
						type: "datetime-local",
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
						disabled: readOnly
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
DateTimeField.displayName = "DateTimeField";

//#endregion
//#region src/components/fields/EnumField.tsx
/**
* Composite enum field component for blockchain enum types.
*
* This component handles both unit enums (simple variants) and tagged enums (variants with payloads).
* For unit enums, it renders as a simple select dropdown.
* For tagged enums, it renders a variant picker plus conditional input fields for payload data.
*
* The value format is designed to be chain-agnostic:
* - Unit variants: { tag: "VariantName" }
* - Tuple variants: { tag: "VariantName", values: [...] }
*/
function EnumField({ id, label, placeholder = "Select variant", helperText, control, name, width = "full", validation, enumMetadata, validateEnum, renderPayloadField, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const widthClass = width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3";
	if (!enumMetadata || enumMetadata.variants.length === 0) return /* @__PURE__ */ jsxs("div", {
		className: cn("flex flex-col gap-2", widthClass),
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			disabled: readOnly,
			rules: { validate: (value) => {
				const standardValidation = validateField(value, validation);
				if (standardValidation !== true) return standardValidation;
				if (typeof value === "string") try {
					if (!JSON.parse(value).tag) return "JSON must have a \"tag\" property specifying the enum variant";
				} catch {
					return "Invalid JSON format. Please enter valid JSON like {\"tag\":\"VariantName\"}";
				}
				if (typeof value === "object" && value !== null) {
					if (!("tag" in value)) return "Enum value must have a \"tag\" property specifying the variant";
				}
				return true;
			} },
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "p-3 border border-border rounded-md bg-muted",
							children: [/* @__PURE__ */ jsx("p", {
								className: "text-sm text-muted-foreground",
								children: "Enum metadata not available. Please enter the value in JSON format."
							}), /* @__PURE__ */ jsxs("p", {
								className: "text-xs text-muted-foreground mt-1",
								children: [
									"Example: ",
									`{"tag":"One"}`,
									" or ",
									`{"tag":"Two","values":[42]}`
								]
							})]
						}), /* @__PURE__ */ jsx(Textarea, {
							id,
							...accessibilityProps,
							className: cn("min-h-[80px]", validationClasses),
							placeholder: `{"tag":"One"} or {"tag":"Two","values":[42]}`,
							value: typeof field.value === "string" ? field.value : JSON.stringify(field.value || {}),
							onChange: (e) => {
								try {
									const parsed = JSON.parse(e.target.value);
									field.onChange(parsed);
								} catch {
									field.onChange(e.target.value);
								}
							},
							onBlur: field.onBlur,
							name: field.name,
							disabled: readOnly
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				] });
			}
		})]
	});
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex flex-col gap-2", widthClass),
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			disabled: readOnly,
			rules: { validate: (value) => {
				const standardValidation = validateField(value, validation);
				if (standardValidation !== true) return standardValidation;
				if (!value || !value.tag) return validation?.required ? "This field is required" : true;
				if (validateEnum && value) {
					const customValidation = validateEnum(value);
					if (customValidation !== true && typeof customValidation === "string") return customValidation;
				}
				return true;
			} },
			render: ({ field, fieldState: { error } }) => {
				const hasError = !!error;
				const currentValue = field.value || { tag: "" };
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				const handleVariantChange = (selectedTag) => {
					const selectedVariant$1 = enumMetadata.variants.find((v) => v.name === selectedTag);
					if (!selectedVariant$1) return;
					if (selectedVariant$1.type === "void" || selectedVariant$1.type === "integer") field.onChange({ tag: selectedTag });
					else {
						const initialValues = new Array(selectedVariant$1.payloadTypes?.length || 0).fill("");
						field.onChange({
							tag: selectedTag,
							values: initialValues
						});
					}
				};
				const handlePayloadChange = (index, value) => {
					const newValues = [...currentValue.values || []];
					newValues[index] = value;
					const newEnumValue = {
						...currentValue,
						values: newValues
					};
					field.onChange(newEnumValue);
				};
				const selectedVariant = enumMetadata.variants.find((v) => v.name === currentValue.tag);
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs(Select, {
						value: currentValue.tag,
						onValueChange: handleVariantChange,
						disabled: readOnly,
						children: [/* @__PURE__ */ jsx(SelectTrigger, {
							id,
							...accessibilityProps,
							children: /* @__PURE__ */ jsx(SelectValue, { placeholder })
						}), /* @__PURE__ */ jsx(SelectContent, { children: enumMetadata.variants.map((variant) => /* @__PURE__ */ jsxs(SelectItem, {
							value: variant.name,
							children: [
								variant.name,
								variant.type === "integer" && variant.value !== void 0 && /* @__PURE__ */ jsxs("span", {
									className: "text-muted-foreground ml-2",
									children: [
										"(",
										variant.value,
										")"
									]
								}),
								variant.type === "tuple" && variant.payloadTypes && /* @__PURE__ */ jsxs("span", {
									className: "text-muted-foreground ml-2",
									children: [
										"(",
										variant.payloadTypes.join(", "),
										")"
									]
								})
							]
						}, variant.name)) })]
					}),
					selectedVariant && selectedVariant.type === "tuple" && selectedVariant.payloadTypes && /* @__PURE__ */ jsxs("div", {
						className: "ml-4 space-y-2 border-l-2 border-border pl-4",
						children: [/* @__PURE__ */ jsxs("p", {
							className: "text-sm text-muted-foreground",
							children: [
								"Enter values for ",
								selectedVariant.name,
								":"
							]
						}), selectedVariant.payloadTypes.map((payloadType, index) => {
							const payloadField = {
								id: `${id}-payload-${index}`,
								name: `${name}.values.${index}`,
								label: payloadType,
								type: "text",
								validation: {},
								placeholder: `Enter ${payloadType} value`,
								helperText: void 0,
								width: "full",
								originalParameterType: payloadType,
								readOnly,
								...selectedVariant.payloadComponents?.[index] && { components: selectedVariant.payloadComponents[index] }
							};
							return /* @__PURE__ */ jsx("div", {
								className: "flex flex-col gap-1",
								children: renderPayloadField ? renderPayloadField(payloadField, index) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col gap-2",
									children: [/* @__PURE__ */ jsx(Label, {
										htmlFor: `${id}-payload-${index}`,
										className: "text-sm",
										children: payloadType
									}), /* @__PURE__ */ jsx(Input, {
										id: `${id}-payload-${index}`,
										type: "text",
										placeholder: `Enter ${payloadType} value`,
										value: currentValue.values?.[index] || "",
										onChange: (e) => handlePayloadChange(index, e.target.value),
										disabled: readOnly
									})]
								})
							}, index);
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				] });
			}
		})]
	});
}
EnumField.displayName = "EnumField";

//#endregion
//#region src/components/fields/FileUploadField.tsx
/**
* File upload field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like FileUploadField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles file upload-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - File type and size validation
* - Optional conversion to base64 for storage
* - Visual feedback for selected files
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation
*/
/**
* Format bytes to human-readable size
*/
function formatFileSize(bytes) {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = [
		"Bytes",
		"KB",
		"MB",
		"GB"
	];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
/** File upload field component with drag-and-drop support. */
function FileUploadField({ id, label, placeholder = "Drop your file here or click to browse", helperText, control, name, width = "full", validation, readOnly, accept, maxSize, convertToBase64 = false }) {
	const fileInputRef = useRef(null);
	const [fileName, setFileName] = useState("");
	const [fileSize, setFileSize] = useState(0);
	const [isProcessing, setIsProcessing] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);
	const [uploadStatus, setUploadStatus] = useState("idle");
	const handleFileToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				if (typeof reader.result === "string") {
					const parts = reader.result.split(",");
					if (parts.length !== 2) {
						reject(/* @__PURE__ */ new Error("Invalid data URL format"));
						return;
					}
					const base64 = parts[1];
					resolve(base64);
				} else reject(/* @__PURE__ */ new Error("Failed to convert file to base64"));
			};
			reader.onerror = () => {
				reject(reader.error);
			};
			reader.readAsDataURL(file);
		});
	};
	const handleFileChange = async (file, onChange) => {
		if (!file) {
			setFileName("");
			setFileSize(0);
			setUploadStatus("idle");
			onChange("");
			return;
		}
		if (maxSize && file.size > maxSize) {
			const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
			onChange("");
			setFileName("");
			setFileSize(0);
			setUploadStatus("error");
			throw new Error(`File size exceeds ${maxSizeMB} MB limit`);
		}
		setFileName(file.name);
		setFileSize(file.size);
		setUploadStatus("idle");
		onChange(file);
		if (!convertToBase64) {
			setUploadStatus("success");
			return;
		}
		setIsProcessing(true);
		try {
			onChange(await handleFileToBase64(file));
			setUploadStatus("success");
		} catch {
			onChange("");
			setFileName("");
			setFileSize(0);
			setUploadStatus("error");
		} finally {
			setIsProcessing(false);
		}
	};
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!readOnly && !isProcessing) setIsDragOver(true);
	};
	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	};
	const handleDrop = (e, onChange) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
		if (readOnly || isProcessing) return;
		const file = e.dataTransfer.files?.[0] || null;
		if (file) handleFileChange(file, onChange);
	};
	const clearFile = (onChange) => {
		if (fileInputRef.current) fileInputRef.current.value = "";
		setFileName("");
		setFileSize(0);
		setUploadStatus("idle");
		onChange("");
	};
	return /* @__PURE__ */ jsx(Controller, {
		control,
		name,
		rules: { validate: (value) => {
			if (!value) return validateField(value, validation);
			if (maxSize && value && typeof value === "object" && "size" in value && value.size > maxSize) return `File size must be less than ${(maxSize / (1024 * 1024)).toFixed(2)} MB`;
			return validateField(value, validation);
		} },
		disabled: readOnly,
		render: ({ field: { onChange, onBlur }, fieldState: { error } }) => {
			const fieldId = `${id}-field`;
			const errorId = `${fieldId}-error`;
			const hasError = !!error;
			const widthClass = width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3";
			const requirementsText = [];
			if (accept) {
				const types = accept.split(",").map((t) => t.trim()).join(", ");
				requirementsText.push(`Accepted types: ${types}`);
			}
			if (maxSize) requirementsText.push(`Max size: ${formatFileSize(maxSize)}`);
			const fullHelperText = helperText || requirementsText.join(" • ");
			return /* @__PURE__ */ jsxs("div", {
				className: `flex flex-col gap-2 ${widthClass}`,
				children: [
					/* @__PURE__ */ jsxs(Label, {
						htmlFor: fieldId,
						children: [label, validation?.required && /* @__PURE__ */ jsx("span", {
							className: "text-red-500 ml-1",
							children: "*"
						})]
					}),
					/* @__PURE__ */ jsx("input", {
						ref: fileInputRef,
						id: fieldId,
						type: "file",
						accept,
						disabled: readOnly || isProcessing,
						onBlur,
						onChange: (e) => {
							handleFileChange(e.target.files?.[0] || null, onChange);
						},
						className: "hidden",
						...getAccessibilityProps({
							id: fieldId,
							hasError,
							isRequired: validation?.required,
							isDisabled: readOnly || isProcessing
						})
					}),
					/* @__PURE__ */ jsx("div", {
						onDragOver: handleDragOver,
						onDragLeave: handleDragLeave,
						onDrop: (e) => handleDrop(e, onChange),
						onClick: () => {
							if (!readOnly && !isProcessing && !fileName) fileInputRef.current?.click();
						},
						className: `
                relative rounded-lg border-2 border-dashed transition-all duration-200
                ${isDragOver ? "border-selected bg-selected/5" : hasError ? "border-destructive bg-destructive/5" : fileName ? "border-border bg-muted/30" : "border-border bg-background hover:border-selected/50 hover:bg-muted/50"}
                ${readOnly || isProcessing ? "cursor-not-allowed opacity-60" : fileName ? "cursor-default" : "cursor-pointer"}
                p-6
              `,
						children: !fileName ? /* @__PURE__ */ jsxs("div", {
							className: "flex flex-col items-center justify-center gap-3 text-center",
							children: [/* @__PURE__ */ jsx("div", {
								className: `rounded-full p-3 transition-colors ${isDragOver ? "bg-selected/10" : "bg-muted"}`,
								children: /* @__PURE__ */ jsx(Upload, { className: `h-8 w-8 transition-colors ${isDragOver ? "text-selected" : "text-muted-foreground"}` })
							}), /* @__PURE__ */ jsxs("div", {
								className: "space-y-1",
								children: [/* @__PURE__ */ jsx("p", {
									className: "text-sm font-medium",
									children: isDragOver ? "Drop file here" : placeholder
								}), fullHelperText && !isDragOver && /* @__PURE__ */ jsx("p", {
									className: "text-xs text-muted-foreground",
									children: fullHelperText
								})]
							})]
						}) : /* @__PURE__ */ jsxs("div", {
							className: "flex items-start gap-4",
							children: [/* @__PURE__ */ jsx("div", {
								className: `rounded-lg p-2 ${uploadStatus === "success" ? "bg-success/10" : uploadStatus === "error" ? "bg-destructive/10" : "bg-muted"}`,
								children: /* @__PURE__ */ jsx(File, { className: `h-8 w-8 ${uploadStatus === "success" ? "text-success" : uploadStatus === "error" ? "text-destructive" : "text-muted-foreground"}` })
							}), /* @__PURE__ */ jsxs("div", {
								className: "flex-1 min-w-0 space-y-1",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "flex items-start justify-between gap-2",
									children: [/* @__PURE__ */ jsxs("div", {
										className: "min-w-0 flex-1",
										children: [/* @__PURE__ */ jsx("p", {
											className: "text-sm font-medium truncate",
											children: fileName
										}), /* @__PURE__ */ jsxs("p", {
											className: "text-xs text-muted-foreground",
											children: [formatFileSize(fileSize), isProcessing && " • Processing..."]
										})]
									}), /* @__PURE__ */ jsxs("div", {
										className: "flex items-center gap-2 shrink-0",
										children: [
											uploadStatus === "success" && !isProcessing && /* @__PURE__ */ jsx(CheckCircle2, { className: "h-5 w-5 text-success" }),
											uploadStatus === "error" && /* @__PURE__ */ jsx(AlertCircle, { className: "h-5 w-5 text-destructive" }),
											!readOnly && !isProcessing && /* @__PURE__ */ jsx(Button, {
												type: "button",
												variant: "ghost",
												size: "sm",
												onClick: (e) => {
													e.stopPropagation();
													clearFile(onChange);
												},
												className: "h-8 w-8 p-0",
												children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
											})
										]
									})]
								}), !isProcessing && /* @__PURE__ */ jsx(Button, {
									type: "button",
									variant: "outline",
									size: "sm",
									disabled: readOnly,
									onClick: (e) => {
										e.stopPropagation();
										fileInputRef.current?.click();
									},
									className: "mt-2",
									children: "Change file"
								})]
							})]
						})
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				]
			});
		}
	});
}

//#endregion
//#region src/components/fields/MapField/MapField.tsx
/**
* Helper function to format a label with an optional type hint
*/
function formatLabeledType(baseLabel, typeHint) {
	if (!typeHint) return baseLabel;
	return `${baseLabel} (${typeHint})`;
}
/**
* Helper function to create a default map entry with appropriate default values for key and value types
*/
function createDefaultEntry(mapMetadata) {
	return {
		key: mapMetadata?.keyFieldConfig?.type ? getDefaultValueForType(mapMetadata.keyFieldConfig.type) : "",
		value: mapMetadata?.valueFieldConfig?.type ? getDefaultValueForType(mapMetadata.valueFieldConfig.type) : ""
	};
}
/**
* Map (key-value dictionary) input field component specifically designed for React Hook Form integration.
*
* This component provides a dynamic interface for managing map/dictionary inputs with:
* - Add/remove functionality for key-value pairs
* - Validation for map size constraints
* - Integration with existing field components for keys and values
* - Chain-agnostic data format (array of {key, value} objects)
* - Full accessibility support
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like MapField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles map-specific rendering and validation
*
* The component stores data in a chain-agnostic format as an array of {key, value} objects.
* The adapter is responsible for converting this to the chain-specific format (e.g., SorobanMapEntry[])
* when submitting to the blockchain.
*/
function MapField({ id, label, helperText, control, name, width = "full", validation, mapMetadata, minItems = 0, renderKeyField, renderValueField, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const formContext = useFormContext();
	const effectiveControl = control || formContext.control;
	const { fields, append, remove, replace } = useFieldArray({
		control: effectiveControl,
		name
	});
	const watchedValue = useWatch({
		control: effectiveControl,
		name
	});
	const watchedValueKey = React.useMemo(() => JSON.stringify(watchedValue), [watchedValue]);
	const isReplacingRef = useMapFieldSync(watchedValue, fields.length, replace);
	React.useEffect(() => {
		if (!formContext) return;
		if (isReplacingRef.current) return;
		const mapArray = Array.isArray(watchedValue) ? watchedValue : [];
		let childHasError = false;
		for (let i = 0; i < mapArray.length; i++) {
			const keyPath = `${name}.${i}.key`;
			const valuePath = `${name}.${i}.value`;
			const keyError = formContext.formState.errors[keyPath]?.message;
			const valueError = formContext.formState.errors[valuePath]?.message;
			if (keyError || valueError) {
				childHasError = true;
				break;
			}
		}
		const structural = validateMapStructure({
			value: watchedValue,
			required: !!validation?.required,
			minItems
		});
		let nextMessage;
		if (childHasError && validation?.required) nextMessage = "This field is required";
		else if (structural !== true) nextMessage = structural;
		const currentMessage = formContext.formState.errors[name]?.message || void 0;
		if (nextMessage && nextMessage !== currentMessage) formContext.setError(name, {
			type: "manual",
			message: nextMessage
		});
		else if (!nextMessage && currentMessage) formContext.clearErrors(name);
	}, [
		formContext,
		name,
		watchedValue,
		watchedValueKey,
		validation?.required,
		minItems,
		isReplacingRef
	]);
	const duplicateKeyIndexes = useDuplicateKeyIndexes(watchedValue);
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control: effectiveControl,
			name,
			disabled: readOnly,
			rules: { validate: (value) => validateMapStructure({
				value,
				required: !!validation?.required,
				minItems
			}) },
			render: ({ fieldState: { error, isTouched } }) => {
				const anyChildTouched = computeChildTouched(formContext, name, watchedValue);
				const effectivelyTouched = isTouched || anyChildTouched;
				const hasError = !!error?.message;
				const shouldShowError = hasError && (effectivelyTouched || isRequired);
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				const handleAddEntry = () => {
					isReplacingRef.current = true;
					append(createDefaultEntry(mapMetadata));
					queueMicrotask(() => {
						isReplacingRef.current = false;
					});
				};
				const handleRemoveEntry = (index) => {
					if (fields.length > minItems) {
						isReplacingRef.current = true;
						remove(index);
						queueMicrotask(() => {
							isReplacingRef.current = false;
						});
					}
				};
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs("div", {
						className: "space-y-3",
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`.trim(),
						children: [fields.length === 0 ? /* @__PURE__ */ jsx("div", {
							className: "text-muted-foreground text-sm p-4 border border-dashed border-border rounded-md text-center",
							children: "No items added yet. Click “Add Item” to begin."
						}) : fields.map((field, index) => {
							const isDuplicateKey = duplicateKeyIndexes.has(index);
							return /* @__PURE__ */ jsxs("div", {
								className: "flex items-start gap-2 p-3 border border-border rounded-md bg-background",
								children: [/* @__PURE__ */ jsx(MapEntryRow, {
									keyField: {
										...mapMetadata?.keyFieldConfig,
										id: `${id}-key-${index}`,
										name: `${name}.${index}.key`,
										label: formatLabeledType("Key", mapMetadata?.keyFieldConfig?.originalParameterType || mapMetadata?.keyType),
										type: mapMetadata?.keyFieldConfig?.type || "text",
										validation: {
											required: true,
											...mapMetadata?.keyFieldConfig?.validation
										},
										placeholder: mapMetadata?.keyFieldConfig?.placeholder || "Enter key",
										width: "full",
										readOnly,
										originalParameterType: mapMetadata?.keyFieldConfig?.originalParameterType
									},
									valueField: {
										...mapMetadata?.valueFieldConfig,
										id: `${id}-value-${index}`,
										name: `${name}.${index}.value`,
										label: formatLabeledType("Value", mapMetadata?.valueFieldConfig?.originalParameterType || mapMetadata?.valueType),
										type: mapMetadata?.valueFieldConfig?.type || "text",
										validation: {
											required: true,
											...mapMetadata?.valueFieldConfig?.validation
										},
										placeholder: mapMetadata?.valueFieldConfig?.placeholder || "Enter value",
										width: "full",
										readOnly,
										originalParameterType: mapMetadata?.valueFieldConfig?.originalParameterType
									},
									isDuplicateKey,
									renderKeyField,
									renderValueField,
									index
								}), !readOnly && fields.length > minItems && /* @__PURE__ */ jsx(Button, {
									type: "button",
									variant: "ghost",
									size: "sm",
									onClick: () => handleRemoveEntry(index),
									className: "size-8 p-0",
									"aria-label": `Remove item ${index + 1}`,
									children: /* @__PURE__ */ jsx(X, { className: "size-4" })
								})]
							}, field.id);
						}), !readOnly && /* @__PURE__ */ jsxs(Button, {
							type: "button",
							variant: "outline",
							size: "sm",
							className: "w-full",
							onClick: handleAddEntry,
							children: [/* @__PURE__ */ jsx(Plus, { className: "h-4 w-4 mr-2" }), "Add Item"]
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						message: shouldShowError ? error?.message : void 0,
						error,
						id: errorId
					})
				] });
			}
		})]
	});
}
MapField.displayName = "MapField";

//#endregion
//#region src/components/fields/MapField/MapEntryRow.tsx
/** Renders a single key-value entry row in a map field. */
function MapEntryRow({ keyField, valueField, isDuplicateKey, renderKeyField, renderValueField, index }) {
	return /* @__PURE__ */ jsxs("div", {
		className: "flex-1 grid grid-cols-1 gap-2 md:grid-cols-2",
		children: [/* @__PURE__ */ jsx("div", {
			className: "flex flex-col gap-1",
			children: renderKeyField ? /* @__PURE__ */ jsxs(Fragment, { children: [renderKeyField(keyField, index), isDuplicateKey && /* @__PURE__ */ jsx("div", {
				className: "text-destructive mt-1 text-sm",
				role: "alert",
				children: "Duplicate keys are not allowed"
			})] }) : /* @__PURE__ */ jsxs("div", {
				className: "text-sm text-muted-foreground p-2 border border-dashed border-border rounded",
				children: [
					"Key field type “",
					keyField.type,
					"” requires a render function"
				]
			})
		}), /* @__PURE__ */ jsx("div", {
			className: "flex flex-col gap-1 md:items-center md:justify-center",
			children: renderValueField ? renderValueField(valueField, index) : /* @__PURE__ */ jsxs("div", {
				className: "text-sm text-muted-foreground p-2 border border-dashed border-border rounded",
				children: [
					"Value field type “",
					valueField.type,
					"” requires a render function"
				]
			})
		})]
	});
}

//#endregion
//#region src/components/fields/MapField/hooks/useChildTouched.ts
/** Computes whether any child field in a map has been touched. */
function computeChildTouched(formContext, name, watchedValue) {
	const mapArray = Array.isArray(watchedValue) ? watchedValue : [];
	if (!formContext || mapArray.length === 0) return false;
	for (let i = 0; i < mapArray.length; i++) {
		const keyPath = `${name}.${i}.key`;
		const valuePath = `${name}.${i}.value`;
		const keyState = formContext.getFieldState(keyPath, formContext.formState);
		const valueState = formContext.getFieldState(valuePath, formContext.formState);
		if (keyState.isTouched || valueState.isTouched) return true;
	}
	return false;
}

//#endregion
//#region src/components/fields/MapField/hooks/useDuplicateKeyIndexes.ts
/** Hook that returns indexes of map entries with duplicate keys. */
function useDuplicateKeyIndexes(watchedValue) {
	return React.useMemo(() => {
		const duplicates = /* @__PURE__ */ new Set();
		if (!Array.isArray(watchedValue)) return duplicates;
		const keyCounts = /* @__PURE__ */ new Map();
		watchedValue.forEach((item, index) => {
			const key = item?.key;
			if (key !== void 0 && key !== null && key !== "") {
				const keyStr = String(key);
				if (!keyCounts.has(keyStr)) keyCounts.set(keyStr, []);
				keyCounts.get(keyStr).push(index);
			}
		});
		for (const [, indexes] of keyCounts) if (indexes.length > 1) indexes.forEach((i) => duplicates.add(i));
		return duplicates;
	}, [watchedValue]);
}

//#endregion
//#region src/components/fields/MapField/hooks/useMapFieldSync.ts
/**
* Keeps a field array synchronized with a watched value's length using replace,
* while preventing infinite loops via a guard ref.
*/
function useMapFieldSync(watchedValue, fieldsLength, replace) {
	const isReplacingRef = React.useRef(false);
	React.useEffect(() => {
		if (isReplacingRef.current) return;
		if (Array.isArray(watchedValue) && fieldsLength !== watchedValue.length) {
			isReplacingRef.current = true;
			replace(watchedValue);
			queueMicrotask(() => {
				isReplacingRef.current = false;
			});
		}
	}, [
		fieldsLength,
		watchedValue,
		replace
	]);
	return isReplacingRef;
}

//#endregion
//#region src/components/fields/MapField/validation/validateMapStructure.ts
/** Validates a map field's structure including duplicates and required entries. */
function validateMapStructure({ value, required, minItems }) {
	if (!isMapEntryArray(value)) {
		if (required) return "This field is required";
		return true;
	}
	const mapArray = value;
	const duplicateError = validateMapEntries(mapArray);
	if (duplicateError) return duplicateError;
	if (mapArray.some((entry) => {
		const key = entry.key;
		const val = entry.value;
		const keyEmpty = key === "" || key == null;
		const valEmpty = val === "" || val == null;
		return keyEmpty && !valEmpty || !keyEmpty && valEmpty;
	})) return "All key-value pairs must be completed";
	const validEntries = mapArray.filter((entry) => {
		const key = entry.key;
		const val = entry.value;
		return key !== "" && key != null && val !== "" && val != null;
	});
	if (required && validEntries.length === 0) return "This field is required";
	if (minItems && validEntries.length < minItems) return `At least ${minItems} item${minItems > 1 ? "s" : ""} required`;
	return true;
}

//#endregion
//#region src/components/fields/NumberField.tsx
/**
* Number input field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like NumberField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles number-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Numeric-specific validations (min, max, step)
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation with arrow keys for numeric increment/decrement
*/
function NumberField({ id, label, placeholder, helperText, control, name, width = "full", validation, min, max, step = 1, validateNumber, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const mergedValidation = {
		...validation,
		min: validation?.min !== void 0 ? validation.min : min,
		max: validation?.max !== void 0 ? validation.max : max
	};
	const NUMBER_REGEX = {
		VALID_FORMAT: /^-?\d*\.?\d*$/,
		COMPLETE_NUMBER: /^-?\d+\.?\d*$/
	};
	const validateNumericValue = (value) => {
		const standardValidation = validateField(value, mergedValidation);
		if (standardValidation !== true) return standardValidation;
		if (validateNumber) {
			const customValidation = validateNumber(value);
			if (customValidation !== true && typeof customValidation === "string") return customValidation;
		}
		return true;
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return mergedValidation?.required ? "This field is required" : true;
				if (value === "-") return "Please enter a complete number";
				if (typeof value === "string" && value.endsWith(".")) return "Please enter a complete decimal number";
				if (typeof value === "string") {
					const parsedNum = parseFloat(value);
					if (isNaN(parsedNum)) return "Invalid number";
					return validateNumericValue(parsedNum);
				}
				if (typeof value === "number") return validateNumericValue(value);
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleInputChange = (e) => {
					const rawValue = e.target.value;
					if (!NUMBER_REGEX.VALID_FORMAT.test(rawValue) && rawValue !== "") return;
					if (rawValue === "") field.onChange("");
					else if (rawValue === "-") field.onChange(rawValue);
					else if (rawValue.endsWith(".")) field.onChange(rawValue);
					else {
						const numValue = parseFloat(rawValue);
						field.onChange(isNaN(numValue) ? rawValue : numValue);
					}
				};
				const handleKeyDown = (e) => {
					const allowedKeys = [
						"0",
						"1",
						"2",
						"3",
						"4",
						"5",
						"6",
						"7",
						"8",
						"9",
						"Backspace",
						"Delete",
						"Tab",
						"Escape",
						"Enter",
						"ArrowLeft",
						"ArrowRight",
						"ArrowUp",
						"ArrowDown",
						".",
						"-"
					];
					if ((e.ctrlKey || e.metaKey) && [
						"a",
						"c",
						"v",
						"x",
						"z"
					].includes(e.key.toLowerCase())) return;
					if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault();
					if (e.key === "Escape") {
						handleEscapeKey(field.onChange, field.value)(e);
						return;
					}
					if (e.key === "ArrowUp" || e.key === "ArrowDown") {
						e.preventDefault();
						let currentValue = 0;
						if (typeof field.value === "number") currentValue = field.value;
						else if (typeof field.value === "string" && NUMBER_REGEX.COMPLETE_NUMBER.test(field.value)) currentValue = parseFloat(field.value);
						const increment = e.key === "ArrowUp" ? step : -step;
						const newValue = Math.min(max !== void 0 ? max : Infinity, Math.max(min !== void 0 ? min : -Infinity, currentValue + increment));
						field.onChange(newValue);
					}
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Input, {
						...field,
						value: field.value ?? "",
						id,
						placeholder,
						type: "text",
						min,
						max,
						step,
						className: validationClasses,
						onKeyDown: handleKeyDown,
						"data-slot": "input",
						onChange: handleInputChange,
						inputMode: "decimal",
						pattern: "-?[0-9]*\\.?[0-9]*",
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
						disabled: readOnly
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
NumberField.displayName = "NumberField";

//#endregion
//#region src/components/fields/ObjectField.tsx
/**
* Object (composite/nested) input field component specifically designed for React Hook Form integration.
*
* This component provides a structured interface for managing object inputs with:
* - Nested field rendering based on object components
* - Validation for required properties (currently, the object itself can be required)
* - Integration with existing field components for object properties
* - Full accessibility support
*
* The component reuses existing field components for individual properties,
* maintaining consistency across the form system while supporting complex nested structures.
*/
function ObjectField({ id, label, helperText, control, name, width = "full", validation, components = [], renderProperty, showCard = true, readOnly, typeMapping, contractSchema }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const formContext = useFormContext();
	return /* @__PURE__ */ jsx(Controller, {
		control: control || formContext.control,
		name,
		rules: { validate: (value) => {
			if (validation?.required && (!value || typeof value !== "object" || Array.isArray(value))) return "This field is required";
			return true;
		} },
		render: ({ fieldState: { error } }) => {
			const hasError = !!error;
			const accessibilityProps = getAccessibilityProps({
				id,
				hasError,
				isRequired,
				hasHelperText: !!helperText
			});
			const content = /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsx("div", {
					className: "space-y-4",
					children: components.length === 0 ? /* @__PURE__ */ jsx("div", {
						className: "text-muted-foreground text-sm",
						children: "No properties defined for this object"
					}) : components.map((component) => {
						if (!typeMapping) throw new Error(`ObjectField: No typeMapping capability provided for field generation. Cannot generate field for "${component.name}"`);
						const generatedField = typeMapping.generateDefaultField(component, contractSchema);
						const propertyField = {
							...generatedField,
							id: `${id}-${component.name}`,
							name: `${name}.${component.name}`,
							label: component.displayName || component.name,
							validation: {
								...generatedField.validation,
								required: validation?.required
							},
							placeholder: `Enter ${component.displayName || component.name}`,
							helperText: component.description || (generatedField.type === "number" ? "Numbers only" : void 0),
							width: "full",
							readOnly,
							...component.components && { components: component.components }
						};
						return /* @__PURE__ */ jsx("div", { children: renderProperty ? renderProperty(propertyField, component.name) : /* @__PURE__ */ jsxs("div", {
							className: "text-sm text-muted-foreground",
							children: [
								"Property “",
								component.name,
								"” requires a render function"
							]
						}) }, component.name);
					})
				}),
				helperText && /* @__PURE__ */ jsx("div", {
					id: descriptionId,
					className: "text-muted-foreground text-sm mt-2",
					children: helperText
				}),
				/* @__PURE__ */ jsx(ErrorMessage, {
					error,
					id: errorId
				})
			] });
			return /* @__PURE__ */ jsxs("div", {
				className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
				...accessibilityProps,
				"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
				children: [label && /* @__PURE__ */ jsxs(Label, {
					htmlFor: id,
					children: [
						label,
						" ",
						isRequired && /* @__PURE__ */ jsx("span", {
							className: "text-destructive",
							children: "*"
						})
					]
				}), showCard ? /* @__PURE__ */ jsx(Card, {
					className: "p-4",
					children: content
				}) : content]
			});
		}
	});
}
ObjectField.displayName = "ObjectField";

//#endregion
//#region src/components/fields/PasswordField.tsx
/**
* Password input field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like PasswordField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles password-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Password visibility toggle functionality
* - Password-specific validations (minLength, maxLength, pattern)
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation
*/
function PasswordField({ id, label, placeholder, helperText, control, name, width = "full", validation, readOnly, showToggle = true }) {
	const [showPassword, setShowPassword] = useState(false);
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const validatePasswordValue = (value) => {
		const validationResult = validateField(value, validation);
		return validationResult === true ? true : validationResult;
	};
	const togglePasswordVisibility = () => {
		setShowPassword((prev) => !prev);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (typeof value === "string") return validatePasswordValue(value);
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleInputChange = (e) => {
					const value = e.target.value;
					field.onChange(value);
					if (isRequired && value === "") setTimeout(() => field.onBlur(), 0);
				};
				const handleKeyDown = (e) => {
					if (e.key === "Escape") {
						handleEscapeKey(field.onChange, field.value)(e);
						if (isRequired) setTimeout(() => field.onBlur(), 0);
					}
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs("div", {
						className: "relative",
						children: [/* @__PURE__ */ jsx(Input, {
							...field,
							id,
							type: showPassword ? "text" : "password",
							placeholder,
							className: `${validationClasses} ${showToggle ? "pr-10" : ""}`,
							onChange: handleInputChange,
							onKeyDown: handleKeyDown,
							"data-slot": "input",
							value: field.value ?? "",
							...accessibilityProps,
							"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
							disabled: readOnly
						}), showToggle && !readOnly && /* @__PURE__ */ jsx(Button, {
							type: "button",
							variant: "ghost",
							size: "sm",
							className: "absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent",
							onClick: togglePasswordVisibility,
							"aria-label": showPassword ? "Hide password" : "Show password",
							children: showPassword ? /* @__PURE__ */ jsx(EyeOff, { className: "h-4 w-4 text-muted-foreground" }) : /* @__PURE__ */ jsx(Eye, { className: "h-4 w-4 text-muted-foreground" })
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
PasswordField.displayName = "PasswordField";

//#endregion
//#region src/components/fields/RadioField.tsx
/**
* Radio button field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles radio-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Customizable options list
* - Horizontal or vertical layout options
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
*/
function RadioField({ id, label, helperText, control, name, width = "full", validation, options = [], layout = "vertical", validateRadio }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const radioGroupId = `${id}-group`;
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: radioGroupId,
			className: "text-sm font-medium",
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (validateRadio && value) {
					const validation$1 = validateRadio(value);
					if (validation$1 !== true && typeof validation$1 === "string") return validation$1;
				}
				return true;
			} },
			render: ({ field, fieldState: { error } }) => {
				const hasError = !!error;
				const groupAccessibilityProps = getAccessibilityProps({
					id: radioGroupId,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx("div", {
						id: radioGroupId,
						role: "radiogroup",
						className: `flex ${layout === "vertical" ? "flex-col gap-3" : "flex-row flex-wrap gap-4"}`,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
						...groupAccessibilityProps,
						children: options.map((option, index) => {
							const optionId = `${id}-option-${index}`;
							const isSelected = field.value === option.value;
							const isDisabled = option.disabled;
							return /* @__PURE__ */ jsxs("label", {
								htmlFor: optionId,
								className: `group relative flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 transition-all duration-150 ${isDisabled ? "cursor-not-allowed opacity-50" : "hover:bg-muted/50"} ${isSelected && !hasError ? "bg-selected/5" : ""}`,
								children: [
									/* @__PURE__ */ jsx("input", {
										type: "radio",
										id: optionId,
										value: option.value,
										disabled: isDisabled,
										checked: isSelected,
										onChange: () => field.onChange(option.value),
										className: "sr-only",
										"aria-describedby": hasError ? errorId : void 0
									}),
									/* @__PURE__ */ jsxs("div", {
										className: "relative flex size-4 items-center justify-center",
										children: [/* @__PURE__ */ jsx("div", { className: `absolute size-4 rounded-full border-2 transition-all duration-150 ${isSelected ? hasError ? "border-destructive bg-destructive/10" : "border-selected bg-selected/10" : hasError ? "border-destructive/50" : "border-muted-foreground/40 group-hover:border-muted-foreground/60"} ${!isDisabled && "group-hover:scale-105"}` }), /* @__PURE__ */ jsx("div", { className: `absolute size-1.5 rounded-full transition-all duration-150 ${isSelected ? hasError ? "bg-destructive scale-100 opacity-100" : "bg-selected scale-100 opacity-100" : "scale-0 opacity-0"}` })]
									}),
									/* @__PURE__ */ jsx("span", {
										className: `text-sm transition-colors duration-150 ${isDisabled ? "text-muted-foreground" : isSelected ? hasError ? "text-destructive font-medium" : "text-foreground font-medium" : "text-foreground"}`,
										children: option.label
									}),
									/* @__PURE__ */ jsx("div", { className: `absolute -inset-1 rounded-lg ring-2 ring-selected ring-offset-2 ring-offset-background transition-opacity duration-150 ${isSelected && !isDisabled ? "opacity-0 focus-within:opacity-100" : "opacity-0"}` })
								]
							}, option.value);
						})
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-sm text-muted-foreground",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				] });
			}
		})]
	});
}
RadioField.displayName = "RadioField";

//#endregion
//#region src/components/fields/SelectField.tsx
/**
* Select dropdown field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles radio-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Customizable options list
* - Horizontal or vertical layout options
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
*/
function SelectField({ id, label, placeholder = "Select an option", helperText, control, name, width = "full", validation, options = [], validateSelect, defaultValue }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const widthPercentage = width === "full" ? "100%" : width === "half" ? "50%" : "33.333%";
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex flex-col gap-2", width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"),
		style: { width: widthPercentage },
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			defaultValue,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (validateSelect && value) {
					const validation$1 = validateSelect(value);
					if (validation$1 !== true && typeof validation$1 === "string") return validation$1;
				}
				return true;
			} },
			render: ({ field, fieldState: { error } }) => {
				const hasError = !!error;
				const accessibilityAttrs = {
					"aria-invalid": hasError,
					"aria-required": isRequired,
					"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`.trim()
				};
				const handleValueChange = (newValue) => {
					field.onChange(newValue);
				};
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs(Select, {
						defaultValue: field.value,
						onValueChange: handleValueChange,
						value: field.value,
						children: [/* @__PURE__ */ jsx(SelectTrigger, {
							id,
							...accessibilityAttrs,
							children: /* @__PURE__ */ jsx(SelectValue, { placeholder })
						}), /* @__PURE__ */ jsx(SelectContent, {
							style: {
								width: "var(--radix-select-trigger-width)",
								maxWidth: "var(--radix-select-trigger-width)"
							},
							children: options.length === 0 ? /* @__PURE__ */ jsx("div", {
								className: "px-2 py-1.5 text-sm text-muted-foreground",
								children: "No options available"
							}) : options.map((option) => /* @__PURE__ */ jsx(SelectItem, {
								value: option.value,
								disabled: option.disabled,
								children: option.label
							}, option.value))
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				] });
			}
		})]
	});
}
SelectField.displayName = "SelectField";

//#endregion
//#region src/components/fields/SelectGroupedField.tsx
/**
* Select dropdown field component with grouped options specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles radio-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Customizable options list
* - Horizontal or vertical layout options
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
*/
function SelectGroupedField({ id, label, placeholder = "Select an option", helperText, control, name, width = "full", validation, groups = [], validateSelect }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const widthPercentage = width === "full" ? "100%" : width === "half" ? "50%" : "33.333%";
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex flex-col gap-2", width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"),
		style: { width: widthPercentage },
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (validateSelect && value) {
					const validation$1 = validateSelect(value);
					if (validation$1 !== true && typeof validation$1 === "string") return validation$1;
				}
				return true;
			} },
			render: ({ field, fieldState: { error } }) => {
				const hasError = !!error;
				const accessibilityAttrs = {
					"aria-invalid": hasError,
					"aria-required": isRequired,
					"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`.trim()
				};
				const handleValueChange = (newValue) => {
					field.onChange(newValue);
				};
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsxs(Select, {
						defaultValue: field.value,
						onValueChange: handleValueChange,
						value: field.value,
						children: [/* @__PURE__ */ jsx(SelectTrigger, {
							id,
							...accessibilityAttrs,
							children: /* @__PURE__ */ jsx(SelectValue, { placeholder })
						}), /* @__PURE__ */ jsx(SelectContent, {
							style: {
								width: "var(--radix-select-trigger-width)",
								maxWidth: "var(--radix-select-trigger-width)"
							},
							children: groups.map((group, groupIndex) => /* @__PURE__ */ jsxs(React.Fragment, { children: [groupIndex > 0 && /* @__PURE__ */ jsx(SelectSeparator, {}), /* @__PURE__ */ jsxs(SelectGroup, { children: [/* @__PURE__ */ jsx(SelectLabel, { children: group.label }), group.options.map((option) => /* @__PURE__ */ jsx(SelectItem, {
								value: option.value,
								disabled: option.disabled,
								className: cn(option.className, option.disabled && "opacity-50"),
								children: /* @__PURE__ */ jsx("span", { children: option.label })
							}, option.value))] })] }, `group-${groupIndex}`))
						})]
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId
					})
				] });
			}
		})]
	});
}
SelectGroupedField.displayName = "SelectGroupedField";

//#endregion
//#region src/components/fields/TextField.tsx
/**
* Text input field component specifically designed for React Hook Form integration.
*
* Architecture flow:
* 1. Form schemas are generated from contract functions using adapters
* 2. TransactionForm renders the overall form structure with React Hook Form
* 3. DynamicFormField selects the appropriate field component (like TextField) based on field type
* 4. BaseField provides consistent layout and hook form integration
* 5. This component handles text-specific rendering and validation
*
* The component includes:
* - Integration with React Hook Form
* - Text-specific validations (minLength, maxLength, pattern)
* - Customizable validation through adapter integration
* - Automatic error handling and reporting
* - Full accessibility support with ARIA attributes
* - Keyboard navigation
*/
function TextField({ id, label, placeholder, helperText, control, name, width = "full", validation, readOnly, onUserEdit }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const validateTextValue = (value) => {
		const validationResult = validateField(value, validation);
		return validationResult === true ? true : validationResult;
	};
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				if (typeof value === "string") return validateTextValue(value);
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleInputChange = (e) => {
					const value = e.target.value;
					field.onChange(value);
					onUserEdit?.(value);
				};
				const handleKeyDown = (e) => {
					if (e.key === "Escape") handleEscapeKey((value) => {
						field.onChange(value);
						onUserEdit?.(value);
					}, field.value)(e);
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Input, {
						...field,
						id,
						placeholder,
						className: validationClasses,
						onChange: handleInputChange,
						onKeyDown: handleKeyDown,
						"data-slot": "input",
						value: field.value ?? "",
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
						disabled: readOnly
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
TextField.displayName = "TextField";

//#endregion
//#region src/components/fields/UrlField.tsx
/**
* URL input field component specifically designed for React Hook Form integration.
*/
function UrlField({ id, label, placeholder, helperText, control, name, width = "full", validation, readOnly }) {
	const isRequired = !!validation?.required;
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	return /* @__PURE__ */ jsxs("div", {
		className: `flex flex-col gap-2 ${width === "full" ? "w-full" : width === "half" ? "w-1/2" : "w-1/3"}`,
		children: [label && /* @__PURE__ */ jsxs(Label, {
			htmlFor: id,
			children: [
				label,
				" ",
				isRequired && /* @__PURE__ */ jsx("span", {
					className: "text-destructive",
					children: "*"
				})
			]
		}), /* @__PURE__ */ jsx(Controller, {
			control,
			name,
			rules: { validate: (value) => {
				if (value === void 0 || value === null || value === "") return validation?.required ? "This field is required" : true;
				const standardValidationResult = validateField(value, validation);
				if (standardValidationResult !== true) return standardValidationResult;
				if (typeof value === "string" && value && !isValidUrl(value)) return getInvalidUrlMessage();
				return true;
			} },
			disabled: readOnly,
			render: ({ field, fieldState: { error, isTouched } }) => {
				const hasError = !!error;
				const shouldShowError = hasError && isTouched;
				const validationClasses = getValidationStateClasses(error, isTouched);
				const handleInputChange = (e) => {
					const value = e.target.value;
					field.onChange(value);
					if (isRequired && value === "") setTimeout(() => field.onBlur(), 0);
				};
				const handleKeyDown = (e) => {
					if (e.key === "Escape") {
						handleEscapeKey(field.onChange, field.value)(e);
						if (isRequired) setTimeout(() => field.onBlur(), 0);
					}
				};
				const accessibilityProps = getAccessibilityProps({
					id,
					hasError,
					isRequired,
					hasHelperText: !!helperText
				});
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Input, {
						...field,
						id,
						type: "url",
						placeholder: placeholder || "https://example.com",
						className: validationClasses,
						onChange: handleInputChange,
						onKeyDown: handleKeyDown,
						"data-slot": "input",
						value: field.value ?? "",
						...accessibilityProps,
						"aria-describedby": `${helperText ? descriptionId : ""} ${hasError ? errorId : ""}`,
						disabled: readOnly
					}),
					helperText && /* @__PURE__ */ jsx("div", {
						id: descriptionId,
						className: "text-muted-foreground text-sm",
						children: helperText
					}),
					/* @__PURE__ */ jsx(ErrorMessage, {
						error,
						id: errorId,
						message: shouldShowError ? error?.message : void 0
					})
				] });
			}
		})]
	});
}
UrlField.displayName = "UrlField";

//#endregion
//#region src/components/RelayerDetailsCard/RelayerDetailsCard.tsx
const RelayerDetailsCard = ({ details, enhancedDetails, loading = false, className, labels }) => {
	const displayDetails = enhancedDetails || details;
	const l = {
		detailsTitle: labels?.detailsTitle || "Relayer Details",
		active: labels?.active || "Active",
		paused: labels?.paused || "Paused",
		systemDisabled: labels?.systemDisabled || "System Disabled",
		network: labels?.network || "Network",
		relayerId: labels?.relayerId || "Relayer ID",
		balance: labels?.balance || "Balance",
		nonce: labels?.nonce || "Nonce",
		pending: labels?.pending || "Pending Transactions",
		lastTransaction: labels?.lastTransaction || "Last Transaction"
	};
	const formatTimestamp = (timestamp) => {
		try {
			return new Date(timestamp).toLocaleString();
		} catch {
			return timestamp;
		}
	};
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3 p-3 bg-slate-50 rounded-md", className),
		children: [/* @__PURE__ */ jsx(Info, { className: "size-5 text-primary mt-0.5 flex-shrink-0 hidden sm:block" }), /* @__PURE__ */ jsxs("div", {
			className: "space-y-3 flex-1 min-w-0",
			children: [/* @__PURE__ */ jsxs("div", { children: [
				/* @__PURE__ */ jsx("h4", {
					className: "text-sm font-medium mb-2",
					children: l.detailsTitle
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0",
					children: [/* @__PURE__ */ jsx("p", {
						className: "text-sm font-medium text-foreground truncate",
						children: displayDetails.name
					}), /* @__PURE__ */ jsx("span", {
						className: cn("inline-flex items-center px-2 py-1 rounded-full text-xs font-medium self-start", displayDetails.paused || enhancedDetails?.systemDisabled ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"),
						children: displayDetails.paused || enhancedDetails?.systemDisabled ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(AlertCircle, { className: "size-3 mr-1" }), enhancedDetails?.systemDisabled ? l.systemDisabled : l.paused] }) : /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(CheckCircle, { className: "size-3 mr-1" }), l.active] })
					})]
				}),
				/* @__PURE__ */ jsx("div", {
					className: "mb-3",
					children: /* @__PURE__ */ jsx(AddressDisplay, {
						className: "w-full sm:w-auto",
						address: displayDetails.address,
						truncate: true,
						startChars: 6,
						endChars: 4,
						showCopyButton: true
					})
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "space-y-1 pt-2 border-t border-slate-200",
					children: [/* @__PURE__ */ jsxs("div", {
						className: "flex items-center gap-2",
						children: [/* @__PURE__ */ jsx(Network, { className: "size-3.5 text-muted-foreground flex-shrink-0" }), /* @__PURE__ */ jsxs("p", {
							className: "text-xs text-muted-foreground truncate",
							children: [
								/* @__PURE__ */ jsxs("span", {
									className: "font-medium",
									children: [l.network, ":"]
								}),
								" ",
								displayDetails.network
							]
						})]
					}), /* @__PURE__ */ jsxs("div", {
						className: "flex items-start gap-2",
						children: [/* @__PURE__ */ jsx(Hash, { className: "size-3.5 text-muted-foreground flex-shrink-0 mt-0.5" }), /* @__PURE__ */ jsxs("div", {
							className: "text-xs text-muted-foreground min-w-0 flex-1",
							children: [
								/* @__PURE__ */ jsxs("span", {
									className: "font-medium",
									children: [l.relayerId, ":"]
								}),
								" ",
								/* @__PURE__ */ jsx("code", {
									className: "font-mono text-xs bg-slate-100 px-1 py-0.5 rounded break-all",
									children: displayDetails.relayerId
								})
							]
						})]
					})]
				}),
				loading && /* @__PURE__ */ jsxs("div", {
					className: "mt-3 flex items-center gap-2 text-xs text-muted-foreground",
					children: [/* @__PURE__ */ jsx(Loader2, { className: "size-3 animate-spin" }), "Loading additional details..."]
				}),
				!loading && enhancedDetails && /* @__PURE__ */ jsxs("div", {
					className: "mt-3 pt-3 border-t border-slate-200 space-y-2",
					children: [
						enhancedDetails.balance && /* @__PURE__ */ jsxs("div", {
							className: "flex items-center gap-2 text-xs",
							children: [/* @__PURE__ */ jsx(DollarSign, { className: "size-3.5 text-muted-foreground flex-shrink-0" }), /* @__PURE__ */ jsxs("div", {
								className: "text-muted-foreground min-w-0 flex-1",
								children: [
									/* @__PURE__ */ jsxs("span", {
										className: "font-medium",
										children: [l.balance, ":"]
									}),
									" ",
									/* @__PURE__ */ jsx("span", {
										className: "break-all",
										children: enhancedDetails.balance
									})
								]
							})]
						}),
						enhancedDetails.nonce && /* @__PURE__ */ jsxs("div", {
							className: "flex items-center gap-2 text-xs",
							children: [/* @__PURE__ */ jsx(Hash, { className: "size-3.5 text-muted-foreground flex-shrink-0" }), /* @__PURE__ */ jsxs("span", {
								className: "text-muted-foreground",
								children: [
									/* @__PURE__ */ jsxs("span", {
										className: "font-medium",
										children: [l.nonce, ":"]
									}),
									" ",
									enhancedDetails.nonce
								]
							})]
						}),
						enhancedDetails.pendingTransactionsCount !== void 0 && /* @__PURE__ */ jsxs("div", {
							className: "flex items-center gap-2 text-xs",
							children: [/* @__PURE__ */ jsx(Timer, { className: "size-3.5 text-muted-foreground flex-shrink-0" }), /* @__PURE__ */ jsxs("span", {
								className: "text-muted-foreground",
								children: [
									/* @__PURE__ */ jsxs("span", {
										className: "font-medium",
										children: [l.pending, ":"]
									}),
									" ",
									enhancedDetails.pendingTransactionsCount
								]
							})]
						}),
						enhancedDetails.lastConfirmedTransactionTimestamp && /* @__PURE__ */ jsxs("div", {
							className: "flex items-start gap-2 text-xs",
							children: [/* @__PURE__ */ jsx(CheckCircle, { className: "size-3.5 text-muted-foreground mt-0.5 flex-shrink-0" }), /* @__PURE__ */ jsxs("div", {
								className: "text-muted-foreground min-w-0 flex-1",
								children: [
									/* @__PURE__ */ jsxs("span", {
										className: "font-medium",
										children: [l.lastTransaction, ":"]
									}),
									/* @__PURE__ */ jsx("br", {}),
									/* @__PURE__ */ jsx("span", {
										className: "text-xs break-words",
										children: formatTimestamp(enhancedDetails.lastConfirmedTransactionTimestamp)
									})
								]
							})]
						})
					]
				})
			] }), (displayDetails.paused || enhancedDetails?.systemDisabled) && /* @__PURE__ */ jsx("div", {
				className: "bg-red-50 border border-red-200 rounded-md p-2",
				children: /* @__PURE__ */ jsxs("p", {
					className: "text-xs text-red-700 flex items-start gap-1",
					children: [/* @__PURE__ */ jsx(AlertCircle, { className: "size-3 mt-0.5 flex-shrink-0" }), /* @__PURE__ */ jsx("span", {
						className: "min-w-0 flex-1",
						children: enhancedDetails?.systemDisabled ? "This relayer has been disabled by the system and cannot process transactions." : "This relayer is currently paused and cannot process transactions."
					})]
				})
			})]
		})]
	});
};

//#endregion
//#region src/components/network-errors/NetworkErrorContext.ts
const NetworkErrorContext = createContext(void 0);

//#endregion
//#region src/components/network-errors/NetworkErrorNotificationProvider.tsx
/** Provider component for managing and displaying network error notifications. */
function NetworkErrorNotificationProvider({ children }) {
	const [errors, setErrors] = useState([]);
	const [openNetworkSettingsHandler, setOpenNetworkSettingsHandler] = useState();
	const errorDedupeRef = useRef(/* @__PURE__ */ new Map());
	const reportNetworkError = useCallback((type, networkId, networkName, message) => {
		const dedupeKey = `${type}-${networkId}-${message}`;
		const now = Date.now();
		const lastReported = errorDedupeRef.current.get(dedupeKey);
		if (lastReported && now - lastReported < 5e3) return;
		errorDedupeRef.current.set(dedupeKey, now);
		const error = {
			id: `${type}-${networkId}-${now}`,
			type,
			networkId,
			networkName,
			message,
			timestamp: now
		};
		setErrors((prev) => {
			return [...prev, error];
		});
		const typeLabel = type === "rpc" ? "RPC" : "Explorer";
		toast.error(`${typeLabel} Error - ${networkName}`, {
			description: message,
			duration: 1e4,
			action: openNetworkSettingsHandler ? {
				label: "Configure",
				onClick: () => {
					openNetworkSettingsHandler(networkId);
				}
			} : void 0
		});
		setTimeout(() => {
			setErrors((prev) => prev.filter((e) => e.id !== error.id));
			errorDedupeRef.current.delete(dedupeKey);
		}, 6e4);
	}, [openNetworkSettingsHandler]);
	const clearError = useCallback((id) => {
		setErrors((prev) => prev.filter((e) => e.id !== id));
	}, []);
	const clearAllErrors = useCallback(() => {
		setErrors([]);
		errorDedupeRef.current.clear();
	}, []);
	const stableSetHandler = useCallback((handler) => {
		setOpenNetworkSettingsHandler(() => handler);
	}, []);
	const value = useMemo(() => ({
		errors,
		reportNetworkError,
		clearError,
		clearAllErrors,
		onOpenNetworkSettings: openNetworkSettingsHandler,
		setOpenNetworkSettingsHandler: stableSetHandler
	}), [
		errors,
		reportNetworkError,
		clearError,
		clearAllErrors,
		openNetworkSettingsHandler,
		stableSetHandler
	]);
	return /* @__PURE__ */ jsx(NetworkErrorContext.Provider, {
		value,
		children
	});
}

//#endregion
//#region src/components/network-errors/useNetworkErrors.ts
/** Hook to access network error reporting and management functions. */
function useNetworkErrors() {
	const context = useContext(NetworkErrorContext);
	if (!context) throw new Error("useNetworkErrors must be used within NetworkErrorNotificationProvider");
	return context;
}
/**
* Hook for reporting network errors for a specific runtime-bound capability
*/
function useNetworkErrorReporter(capability) {
	const { reportNetworkError } = useNetworkErrors();
	return {
		reportRpcError: useCallback((message) => {
			if (!capability) return;
			reportNetworkError("rpc", capability.networkConfig.id, capability.networkConfig.name, message);
		}, [capability, reportNetworkError]),
		reportExplorerError: useCallback((message) => {
			if (!capability) return;
			reportNetworkError("explorer", capability.networkConfig.id, capability.networkConfig.name, message);
		}, [capability, reportNetworkError])
	};
}

//#endregion
//#region src/components/network-errors/NetworkErrorAwareAdapter.tsx
/**
* Creates an adapter proxy that intercepts and reports network errors
*/
function useNetworkErrorAwareAdapter(capability) {
	const { reportNetworkError } = useNetworkErrors();
	const wrappedCapabilityRef = useRef(null);
	useEffect(() => {
		if (!capability) {
			wrappedCapabilityRef.current = null;
			return;
		}
		wrappedCapabilityRef.current = new Proxy(capability, { get(target, prop, receiver) {
			const value = Reflect.get(target, prop, receiver);
			if (typeof value === "function" && (prop === "queryViewFunction" || prop === "loadContract")) return async (...args) => {
				try {
					return await value.apply(target, args);
				} catch (error) {
					const errorMessage = error instanceof Error ? error.message : "Unknown error";
					if (errorMessage.toLowerCase().includes("rpc") || errorMessage.toLowerCase().includes("network") || errorMessage.toLowerCase().includes("timeout") || errorMessage.toLowerCase().includes("fetch") || errorMessage.toLowerCase().includes("connection")) reportNetworkError("rpc", target.networkConfig.id, target.networkConfig.name, errorMessage);
					if (errorMessage.toLowerCase().includes("explorer") || errorMessage.toLowerCase().includes("etherscan") || errorMessage.toLowerCase().includes("api key") || errorMessage.toLowerCase().includes("abi") || errorMessage.toLowerCase().includes("verified")) reportNetworkError("explorer", target.networkConfig.id, target.networkConfig.name, errorMessage);
					throw error;
				}
			};
			return value;
		} });
	}, [capability, reportNetworkError]);
	return wrappedCapabilityRef.current;
}

//#endregion
//#region src/components/network-errors/NetworkServiceErrorBanner.tsx
/**
* User-friendly banner displayed when a network service connection fails.
* Works with any service type (RPC, Explorer, Indexer, etc.) and provides
* a clear explanation of the issue with a call-to-action to open the
* network settings dialog where users can configure an alternative endpoint.
*
* This component can be used with or without the NetworkErrorNotificationProvider:
* - With provider: The settings handler is obtained from context
* - Without provider: Pass onOpenNetworkSettings prop directly, or the button won't render
*/
function NetworkServiceErrorBanner({ networkConfig, serviceType, errorMessage, title, description, onOpenNetworkSettings: onOpenNetworkSettingsProp }) {
	const context = useContext(NetworkErrorContext);
	const onOpenNetworkSettings = onOpenNetworkSettingsProp ?? context?.onOpenNetworkSettings;
	const handleOpenSettings = () => {
		onOpenNetworkSettings?.(networkConfig.id);
	};
	const serviceName = getServiceDisplayName(serviceType);
	const defaultTitle = `Unable to Connect to ${networkConfig.name}`;
	const defaultDescription = onOpenNetworkSettings ? `This could be due to network congestion, rate limiting, or the service being temporarily down. You can configure a custom ${serviceName.toLowerCase()} endpoint in the network settings.` : `This could be due to network congestion, rate limiting, or the service being temporarily down.`;
	return /* @__PURE__ */ jsxs(Alert, {
		variant: "destructive",
		className: "border-red-500/50 bg-red-50 dark:bg-red-950/20",
		children: [
			/* @__PURE__ */ jsx(CloudOff, { className: "h-4 w-4 text-red-600 dark:text-red-400" }),
			/* @__PURE__ */ jsx(AlertTitle, {
				className: "text-red-900 dark:text-red-100",
				children: title || defaultTitle
			}),
			/* @__PURE__ */ jsxs(AlertDescription, {
				className: "text-red-800 dark:text-red-200",
				children: [
					/* @__PURE__ */ jsx("p", {
						className: "mb-2",
						children: errorMessage || `The ${serviceName.toLowerCase()} for ${networkConfig.name} is currently unavailable or not responding.`
					}),
					/* @__PURE__ */ jsx("p", {
						className: "text-sm mb-3",
						children: description || defaultDescription
					}),
					onOpenNetworkSettings && /* @__PURE__ */ jsxs(Button, {
						onClick: handleOpenSettings,
						variant: "default",
						size: "sm",
						className: "bg-red-600 hover:bg-red-700 text-white dark:bg-red-500 dark:hover:bg-red-600",
						children: [
							/* @__PURE__ */ jsx(Settings, { className: "mr-2 h-4 w-4" }),
							"Configure ",
							serviceName,
							" Settings"
						]
					})
				]
			})
		]
	});
}

//#endregion
//#region src/components/network-availability/NetworkAvailabilityNotice.tsx
/**
* Banner shown when the deployment disables mainnet or specific network IDs.
* Hidden when no network availability policy is active.
*/
function NetworkAvailabilityNotice({ appName, selfHostRepoUrl, className }) {
	if (!isNetworkAvailabilityPolicyActive()) return null;
	const { title, descriptionBeforeLink, descriptionLinkLabel } = getHostedNetworkAvailabilityNoticeCopy(appName);
	return /* @__PURE__ */ jsxs(Alert, {
		className,
		children: [
			/* @__PURE__ */ jsx(Info, { className: "h-4 w-4" }),
			/* @__PURE__ */ jsx(AlertTitle, { children: title }),
			/* @__PURE__ */ jsxs(AlertDescription, { children: [
				descriptionBeforeLink,
				/* @__PURE__ */ jsx("a", {
					href: selfHostRepoUrl,
					target: "_blank",
					rel: "noopener noreferrer",
					className: "font-medium underline underline-offset-4 hover:text-foreground",
					children: descriptionLinkLabel
				}),
				"."
			] })
		]
	});
}

//#endregion
//#region src/components/ui/sonner.tsx
const Toaster = ({ ...props }) => {
	const { theme = "system" } = useTheme();
	return /* @__PURE__ */ jsx(Toaster$1, {
		theme,
		className: "toaster group",
		toastOptions: { classNames: {
			toast: "group toast group-[.toaster]:bg-gray-900 group-[.toaster]:text-gray-50 group-[.toaster]:border-gray-800 group-[.toaster]:shadow-lg",
			description: "group-[.toast]:text-gray-400",
			actionButton: "group-[.toast]:bg-white group-[.toast]:text-gray-900 group-[.toast]:hover:bg-gray-100 group-[.toast]:font-medium group-[.toast]:border-0",
			cancelButton: "group-[.toast]:bg-gray-800 group-[.toast]:text-gray-300 group-[.toast]:hover:bg-gray-700",
			error: "group-[.toast]:!bg-red-950 group-[.toast]:!text-red-50 group-[.toast]:!border-red-900"
		} },
		...props
	});
};

//#endregion
export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, AddressDisplay, AddressField, AddressFieldWithResolvedPreview, AddressLabelProvider, AddressListField, AddressNameProvider, AddressSuggestionList, AddressSuggestionProvider, Alert, AlertDescription, AlertTitle, AmountField, ArrayField, ArrayObjectField, Banner, BaseField, BigIntField, BooleanField, Button, BytesField, Calendar, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, DEFAULT_ADDRESS_LIST_FIELD_LABELS, DateRangePicker, DateTimeField, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EcosystemDropdown, EcosystemIcon, EmptyState, EnumField, ErrorMessage, ExternalLink, FileUploadField, Footer, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, Header, INTEGER_HTML_PATTERN, INTEGER_INPUT_PATTERN, INTEGER_PATTERN, Input, Label, LoadingButton, MapEntryRow, MapField, MidnightIcon, NAME_RESOLUTION_DEBOUNCE_MS, NameResolverContext, NameResolverProvider, NetworkAvailabilityNotice, NetworkErrorNotificationProvider, NetworkIcon, NetworkSelector, NetworkServiceErrorBanner, NetworkStatusBadge, NumberField, ObjectField, OverflowMenu, PasswordField, Popover, PopoverAnchor, PopoverContent, PopoverTrigger, Progress, RadioField, RadioGroup, RadioGroupItem, RelayerDetailsCard, ResolvedAddressFieldPreview, Select, SelectContent, SelectField, SelectGroup, SelectGroupedField, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, SidebarButton, SidebarGroup, SidebarLayout, SidebarSection, Tabs, TabsContent, TabsList, TabsTrigger, TextAreaField, TextField, Textarea, Toaster, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, UrlField, VERSION, ViewContractStateButton, WizardLayout, WizardNavigation, WizardStepper, buildAddressListPreviewSummary, buttonVariants, computeChildTouched, createFocusManager, createValidationResult, formatAddressBulkSummary, formatValidationError, getAccessibilityProps, getDescribedById, getErrorMessage, getValidationStateClasses, getWidthClasses, handleEscapeKey, handleKeyboardEvent, handleNumericKeys, handleToggleKeys, handleValidationError, hasFieldError, isDuplicateMapKey, resolveAddressListFieldLabels, useAddressLabel, useAddressName, useAddressSuggestionField, useAddressSuggestions, useDuplicateKeyIndexes, useInjectedNameResolution, useMapFieldSync, useNameResolver, useNetworkErrorAwareAdapter, useNetworkErrorReporter, useNetworkErrors, validateField, validateMapEntries, validateMapStructure };
//# sourceMappingURL=index.mjs.map